/**
 * QA Panel — popup window HTML template.
 *
 * The popup is opened with `window.open('about:blank', ...)` and this entire
 * string is `document.write()`-ten into it. The popup runs in its own window
 * context (own document, own JS realm) and communicates with the lobby
 * exclusively via postMessage.
 *
 * Why vanilla HTML/CSS/JS instead of React:
 *   - The popup is a thin remote control — no need for VDOM
 *   - Loads instantly, no bundle, no dependency on the lobby's React tree
 *   - styled-components targeting another window is awkward; this is simpler
 *   - Easy to grep / edit when QA needs a tweak
 *
 * The popup state is held in a single `state` object. Whenever a message
 * arrives from the lobby, the relevant render*() function is called to
 * patch the DOM. No diffing — sections re-render in full because they're
 * small.
 */

import { MESSAGE_NAMESPACE } from '../constants';

/**
 * Build the full popup HTML. Initial state JSON is inlined so the popup
 * paints immediately, before the first message round-trip.
 */
export function buildPopupHtml(initialStateJson: string): string {
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Lobby QA Panel</title>
<script>
// Theme detection — runs before <body> paints to avoid a flash of the wrong
// theme. Reads explicit user preference from localStorage first; falls back
// to the OS-level prefers-color-scheme. Defaults to dark if neither hint
// is available.
(function () {
    try {
        var saved = localStorage.getItem('qa-panel-theme');
        var theme;
        if (saved === 'light' || saved === 'dark') {
            theme = saved;
        } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
            theme = 'light';
        } else {
            theme = 'dark';
        }
        document.documentElement.setAttribute('data-theme', theme);
    } catch (e) {
        document.documentElement.setAttribute('data-theme', 'dark');
    }
})();
</script>
<style>
/* ─── Tokens — "Vibrant Cards" (iPadOS / Stage Manager) ─── */
/* Tinted body backdrop, solid white cards that pop, indigo accent for
 * active controls only. Inactive controls inside cards are gray-fill
 * pills; top-level pills float on the body bg as clean white chips.
 * No backdrop-filter on cards — surfaces are solid. */
:root,
:root[data-theme="dark"] {
    /* react.dev wash-dark + card-dark; accent = link-dark (#58C4DC). */
    --bg-grad: linear-gradient(180deg, #23272F 0%, #23272F 100%);
    --bg: #23272F;                          /* react.dev wash-dark */
    --surface: #343A46;                     /* react.dev card-dark */
    --surface-floating: #3D434F;            /* half-step lighter than --surface for top-level pills */
    --surface-2: rgba(255, 255, 255, 0.08); /* gray-fill for nested controls inside cards */
    --surface-3: rgba(255, 255, 255, 0.14);
    /* Borders — single hairline, no "strong" stack anymore. Aliases kept
     * so per-tab CSS that references --border-strong / --border-bright
     * still resolves without per-rule edits. */
    --border: #343A46;                      /* react.dev border-dark (== card-dark, intentional) */
    --border-strong: rgba(255, 255, 255, 0.14);
    --border-bright: rgba(255, 255, 255, 0.22);
    /* Text — four-step ramp derived from react.dev primary-dark */
    --text: #F6F7F9;
    --text-dim: rgba(246, 247, 249, 0.6);
    --text-mute: rgba(246, 247, 249, 0.4);
    --text-faint: rgba(246, 247, 249, 0.25);
    /* Accent — react.dev link-dark family */
    --accent: #58C4DC;
    --accent-dim: #149ECA;
    --accent-glow: rgba(88, 196, 220, 0.32);
    --accent-bg: rgba(88, 196, 220, 0.10);
    /* Status — SF Symbols-aligned */
    --status-error: #ff453a;
    --status-warn: #ff9f0a;
    --status-suspect: #ffd60a;
    --status-ok: #30d158;
    /* Type — SF system stack */
    --font-sans: -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
    --font-mono: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    /* Easing */
    --ease: cubic-bezier(0.16, 1, 0.3, 1);
    /* Radii — soft, iPadOS-scale */
    --radius-sm: 8px;
    --radius: 14px;
    --radius-lg: 18px;
    /* Shadows — soft drop on solid surfaces. No inset highlight (no glass anymore). */
    --shadow-card: 0 1px 2px rgba(0, 0, 0, 0.25), 0 4px 16px rgba(0, 0, 0, 0.28);
    --shadow-pill: 0 1px 2px rgba(0, 0, 0, 0.3);
    /* Scrollbar thumb tint */
    --scroll-thumb: rgba(255, 255, 255, 0.15);
    --scroll-thumb-hover: rgba(255, 255, 255, 0.25);
}

:root[data-theme="light"] {
    /* react.dev wash + card; accent = link (#087EA4). */
    --bg-grad: linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%);
    --bg: #FFFFFF;                          /* react.dev wash */
    --surface: #F6F7F9;                     /* react.dev card — slightly darker than body */
    --surface-floating: #F6F7F9;            /* top-level pills sit on the white body — same tone as --surface so they read as raised chips */
    /* --surface-2 is a translucent gray fill (Apple's iOS tertiarySystemFill-ish).
     * Inactive buttons / inputs / nested list containers use it on top of the
     * --surface card and need to read as a discrete affordance. */
    --surface-2: rgba(120, 120, 128, 0.12);
    --surface-3: rgba(120, 120, 128, 0.2);
    --border: #EBECF0;                      /* react.dev border */
    --border-strong: rgba(0, 0, 0, 0.12);
    --border-bright: rgba(0, 0, 0, 0.2);
    --text: #23272F;                        /* react.dev primary */
    --text-dim: rgba(35, 39, 47, 0.6);
    --text-mute: rgba(35, 39, 47, 0.4);
    --text-faint: rgba(35, 39, 47, 0.25);
    /* Accent — react.dev link family */
    --accent: #087EA4;
    --accent-dim: #149ECA;
    --accent-glow: rgba(8, 126, 164, 0.28);
    --accent-bg: rgba(8, 126, 164, 0.10);
    --status-error: #ff3b30;
    --status-warn: #ff9500;
    --status-suspect: #ffcc00;
    --status-ok: #34c759;
    /* Very soft, short shadow — barely-there, matches the reference image */
    --shadow-card: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
    --shadow-pill: 0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.04);
    --scroll-thumb: rgba(0, 0, 0, 0.15);
    --scroll-thumb-hover: rgba(0, 0, 0, 0.25);
}

* { box-sizing: border-box; margin: 0; padding: 0; }
*::selection { background: var(--accent); color: #ffffff; }

html, body {
    color: var(--text);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    height: 100%;
}
html { background: var(--bg); }
body {
    background: var(--bg-grad);
    background-attachment: fixed;
    padding: 24px 20px 40px;
    overflow-y: auto;
}
body::-webkit-scrollbar { width: 6px; }
body::-webkit-scrollbar-track { background: transparent; }
body::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 3px;
}
body::-webkit-scrollbar-thumb:hover { background: var(--scroll-thumb-hover); }

/* ─── Header ──────────────────────────────────────────── */
h1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: var(--font-sans);
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.022em;
    color: var(--text);
    margin-bottom: 6px;
}
h1 #version {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 400;
    color: var(--text-faint);
    margin-left: 8px;
    letter-spacing: 0;
}
.subtle {
    font-size: 12px;
    color: var(--text-mute);
    font-family: var(--font-sans);
    margin-bottom: 6px;
    letter-spacing: 0;
}

/* Header controls — circular soft glass buttons, no border */
.header-controls {
    display: flex;
    align-items: center;
    gap: 10px;
}
#reload-btn,
#theme-btn {
    background: var(--surface-floating);
    box-shadow: var(--shadow-pill);
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    padding: 0;
    color: var(--text-dim);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 200ms var(--ease), color 200ms var(--ease);
}
#reload-btn:hover,
#theme-btn:hover {
    color: var(--text);
    background: var(--surface-2);
}
#reload-btn:active,
#theme-btn:active { opacity: 0.8; }
#reload-btn.spinning {
    transform: rotate(360deg);
    transition: transform 600ms var(--ease);
}
#theme-btn { font-size: 12px; }

/* Connection indicator — pulsing indigo dot when connected */
.conn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-faint);
}
.conn::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--text-faint);
    transition: all 300ms var(--ease);
}
.conn.ok { color: var(--accent); }
.conn.ok::before {
    background: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-glow);
    animation: pulse 2.4s var(--ease) infinite;
}
@keyframes pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 3px var(--accent-glow); }
    50%      { opacity: 0.55; box-shadow: 0 0 0 1px var(--accent-glow); }
}

/* ─── Tabs — floating pills directly on the body bg ──── */
/* No wrapper background. Each pill is its own white chip with a soft
 * shadow, popping against the tinted lavender bg. Active becomes solid
 * indigo. The wrapper is layout-only (flex, gap, horizontal scroll). */
.tab-nav {
    display: flex;
    gap: 6px;
    margin: 20px 0 24px;
    border-radius: 10px;
    /* The tab strip overflows horizontally at 460px with 7 items — let it
     * scroll silently. Quiet, stable seven-item list, scroll is rare. */
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;
}
.tab-nav::-webkit-scrollbar { display: none; }
.tab-nav button {
    flex: 0 0 auto;
    background: var(--surface-floating);
    border: none;
    border-radius: 10px;
    padding: 8px 14px;
    color: var(--text);
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: var(--shadow-pill);
    transition: background 200ms var(--ease), color 200ms var(--ease);
}
.tab-nav button:hover {
    color: var(--text);
    background: var(--surface-2);
}
.tab-nav button.active,
.tab-nav button.active:hover {
    color: #ffffff;
    background: var(--accent);
}
.tab-nav button:active { opacity: 0.85; }
/* Red-dot count badge appended inside a tab button (Console tab). Hidden
   when count is 0 via the [hidden] attribute. */
.tab-badge {
    display: inline-block;
    margin-left: 6px;
    min-width: 16px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--status-error);
    color: #ffffff;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    text-align: center;
    vertical-align: middle;
}
.tab-nav button.active .tab-badge {
    /* On active (accent-filled) buttons, switch to a translucent white so
       the red dot doesn't fight the accent fill. */
    background: rgba(255, 255, 255, 0.28);
}
.tab-panel { display: none; animation: fadeIn 240ms var(--ease); padding-bottom: 48px; }
.tab-panel.active { display: block; }
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(2px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* ─── Section headers — eyebrow above each card ──────── */
h2 {
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 600;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 24px 0 8px;
    padding: 0 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.tab-panel > h2:first-child { margin-top: 0; }
h2 .badge {
    font-family: var(--font-mono);
    background: var(--surface-2);
    color: var(--text-dim);
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
}

/* ─── Sections — solid cards on the tinted body bg ────── */
.section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    padding: 18px;
    margin-bottom: 16px;
}
.section:last-child { margin-bottom: 0; }
.row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

/* ─── Buttons — flat, no border, brighten on hover ─────── */
button {
    background: var(--surface-2);
    color: var(--text);
    border: none;
    border-radius: var(--radius-sm);
    padding: 8px 14px;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    letter-spacing: 0;
    transition: background 180ms var(--ease), color 180ms var(--ease), opacity 120ms var(--ease);
}
button:hover {
    background: var(--surface-3);
}
button:active { opacity: 0.8; }
button.active {
    color: #ffffff;
    background: var(--accent);
}
button.primary {
    color: #ffffff;
    background: var(--accent);
}
button.primary:hover {
    background: var(--accent-dim);
    color: #ffffff;
}
button.danger {
    color: #ffffff;
    background: var(--status-error);
}
button.danger:hover {
    background: var(--status-error);
    opacity: 0.88;
    color: #ffffff;
}

/* Language switcher grid */
.lang-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
    gap: 6px;
}
.lang-grid button {
    padding: 12px 4px;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.05em;
}

/* ─── Coverage table ──────────────────────────────────── */
.coverage-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-mono);
    font-size: 12px;
}
.coverage-table th {
    text-align: left;
    color: var(--text-mute);
    padding: 10px 8px;
    font-weight: 500;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid var(--border);
    font-family: var(--font-sans);
}
.coverage-table td {
    padding: 10px 8px;
    border-bottom: 1px solid var(--border);
    color: var(--text-dim);
}
.coverage-table tbody tr:last-child td { border-bottom: none; }
.coverage-table tbody tr {
    cursor: pointer;
    transition: background 180ms var(--ease);
}
.coverage-table tbody tr:hover td { background: var(--surface-2); }
.coverage-table tbody tr.selected td {
    background: var(--accent-bg);
    color: var(--text);
}
.coverage-table .lang {
    color: var(--text);
    font-weight: 500;
    letter-spacing: 0.05em;
}
.coverage-table .pct { font-weight: 500; }
.coverage-table .pct.full { color: var(--status-ok); }
.coverage-table .pct.partial { color: var(--status-suspect); }
.coverage-table .pct.bad { color: var(--status-error); }

/* ─── Inspector — card matching .section ──────────────── */
.inspector {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    padding: 18px;
}
.inspector .toolbar {
    display: flex;
    gap: 4px;
    margin-bottom: 12px;
    background: var(--surface-2);
    border-radius: 10px;
    padding: 3px;
}
.inspector .toolbar button {
    flex: 1;
    padding: 6px 8px;
    font-size: 11px;
    background: transparent;
    border-radius: 7px;
}
.inspector .toolbar button:hover { background: var(--surface-3); }
.inspector .toolbar button.active { background: var(--accent); color: #ffffff; }
.inspector input[type="text"] {
    width: 100%;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-family: var(--font-sans);
    font-size: 13px;
    margin-bottom: 12px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
}
.inspector input[type="text"]::placeholder { color: var(--text-faint); }
.inspector input[type="text"]:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.inspector .stats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 10px;
    margin-bottom: 12px;
    font-family: var(--font-mono);
}
.inspector .stats span {
    padding: 3px 8px;
    border-radius: 999px;
    background: var(--surface-2);
    color: var(--text-mute);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 500;
}
.inspector .stats span.missing { color: var(--status-error); }
.inspector .stats span.empty { color: var(--status-warn); }
.inspector .stats span.keyAsValue { color: var(--status-warn); }
.inspector .stats span.sameAsRef { color: var(--status-suspect); }
.inspector .stats span.translated { color: var(--status-ok); }
.inspector .list {
    max-height: 400px;
    overflow-y: auto;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
}
.inspector .list::-webkit-scrollbar { width: 6px; }
.inspector .list::-webkit-scrollbar-track { background: transparent; }
.inspector .list::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 3px;
}
.inspector .item {
    padding: 12px 14px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
    transition: background 150ms var(--ease);
}
.inspector .item:hover { background: var(--surface-3); }
.inspector .item:last-child { border-bottom: none; }
.inspector .item .head {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 6px;
}
.inspector .item .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}
.inspector .item.missing .dot { background: var(--status-error); }
.inspector .item.empty .dot,
.inspector .item.keyAsValue .dot { background: var(--status-warn); }
.inspector .item.sameAsRef .dot { background: var(--status-suspect); }
.inspector .item.translated .dot { background: var(--status-ok); }
.inspector .item .key {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    word-break: break-all;
    flex: 1;
}
.inspector .item .status-label {
    font-family: var(--font-mono);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-mute);
    font-weight: 500;
}
.inspector .item.missing .status-label { color: var(--status-error); }
.inspector .item.empty .status-label,
.inspector .item.keyAsValue .status-label { color: var(--status-warn); }
.inspector .item.sameAsRef .status-label { color: var(--status-suspect); }
.inspector .item.translated .status-label { color: var(--status-ok); }
.inspector .item .vals { padding-left: 15px; font-size: 11px; }
.inspector .item .vals .row {
    display: flex;
    gap: 8px;
    padding: 2px 0;
}
.inspector .item .vals .lng {
    width: 28px;
    color: var(--text-mute);
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 500;
    flex-shrink: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.inspector .item .vals .val {
    color: var(--text-dim);
    word-break: break-word;
    flex: 1;
    font-family: var(--font-sans);
}
.inspector .item .vals .val.absent {
    color: var(--text-faint);
    font-style: italic;
}

/* ─── Missing keys list ──────────────────────────────── */
.missing-list {
    max-height: 300px;
    overflow-y: auto;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 6px;
}
.missing-list::-webkit-scrollbar { width: 6px; }
.missing-list::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 3px;
}
.missing-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-radius: var(--radius-sm);
    font-size: 11px;
    font-family: var(--font-mono);
    cursor: pointer;
    transition: background 150ms var(--ease);
}
.missing-item:hover { background: var(--surface-3); }
.missing-item .lang-tag {
    background: var(--accent-bg);
    color: var(--accent);
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10px;
    margin-right: 8px;
    font-family: var(--font-mono);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
}
.missing-item .key {
    color: var(--text-dim);
    flex: 1;
    word-break: break-all;
}
.missing-item .count {
    color: var(--text-mute);
    margin-left: 8px;
    font-size: 10px;
}

/* ─── Diff popup (legacy missing-key click target) ──── */
.diff-popup {
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px;
    margin-top: 8px;
    font-size: 12px;
    display: none;
}
.diff-popup.open { display: block; }
.diff-popup .diff-key {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--accent);
    margin-bottom: 8px;
    word-break: break-all;
}
.diff-popup .diff-row {
    display: flex;
    padding: 4px 0;
    border-top: 1px solid var(--border);
}
.diff-popup .diff-row .lng {
    width: 36px;
    color: var(--text-mute);
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.diff-popup .diff-row .val {
    flex: 1;
    color: var(--text-dim);
    word-break: break-word;
}
.diff-popup .diff-row .val.missing {
    color: var(--status-error);
    font-style: italic;
}

/* ─── Status legend ──────────────────────────────────── */
.legend {
    display: flex;
    flex-direction: column;
    gap: 11px;
}
.legend-item {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    font-size: 11px;
}
.legend-item .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 6px;
}
.legend-item.missing .dot { background: var(--status-error); }
.legend-item.empty .dot,
.legend-item.keyAsValue .dot { background: var(--status-warn); }
.legend-item.sameAsRef .dot { background: var(--status-suspect); }
.legend-item.translated .dot { background: var(--status-ok); }
.legend-item .legend-text {
    color: var(--text-dim);
    line-height: 1.55;
    font-size: 13px;
    font-family: var(--font-sans);
}
.legend-item .legend-text strong {
    display: inline-block;
    min-width: 118px;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-right: 8px;
}
.legend-item.missing .legend-text strong { color: var(--status-error); }
.legend-item.empty .legend-text strong,
.legend-item.keyAsValue .legend-text strong { color: var(--status-warn); }
.legend-item.sameAsRef .legend-text strong { color: var(--status-suspect); }
.legend-item.translated .legend-text strong { color: var(--status-ok); }

/* ─── Empty state ──────────────────────────────────── */
.empty {
    color: var(--text-mute);
    text-align: center;
    padding: 28px 16px;
    font-size: 13px;
    font-family: var(--font-sans);
    line-height: 1.6;
}
.hint {
    font-size: 12px;
    color: var(--text-mute);
    margin-bottom: 12px;
    font-family: var(--font-sans);
    letter-spacing: 0;
    line-height: 1.5;
}
.hint-faint {
    font-size: 11px;
    color: var(--text-faint);
    font-family: var(--font-mono);
}

/* Compact button used in section headers (e.g. Preload all, Inspector close) */
h2 button {
    padding: 5px 12px;
    font-size: 11px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    border-radius: 999px;
}

/* ─── Jackpot Mock tab ──────────────────────────────── */
.jp-toggle-row {
    display: flex;
    margin-bottom: 10px;
}
.jp-toggle-row button {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
}
#jp-mock-toggle.on {
    color: #ffffff;
    background: var(--accent);
}
#jp-mock-state {
    font-family: var(--font-mono);
    font-weight: 500;
    letter-spacing: 0.05em;
    margin-left: 4px;
}

.jp-control-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.jp-control-row label {
    font-family: var(--font-sans);
    font-size: 11px;
    color: var(--text-mute);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 500;
    flex-shrink: 0;
}
.jp-control-row .jp-label-tight {
    margin-left: 4px;
}
.jp-control-row input[type="number"] {
    width: 72px;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: var(--font-mono);
    font-size: 13px;
    text-align: right;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    -moz-appearance: textfield;
}
.jp-control-row input[type="number"]::-webkit-outer-spin-button,
.jp-control-row input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.jp-control-row input[type="number"]:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.jp-control-row .jp-unit {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-mute);
    margin-left: -2px;
}
.jp-control-row button {
    margin-left: auto;
}

.jp-feedback {
    margin-top: 10px;
    font-family: var(--font-mono);
    font-size: 11px;
    min-height: 14px;
    letter-spacing: 0.02em;
    transition: opacity 200ms var(--ease);
    opacity: 0;
}
.jp-feedback.show { opacity: 1; }
.jp-feedback.ok { color: var(--status-ok); }
.jp-feedback.err { color: var(--status-error); }

#jp-auto-toggle.on {
    color: #ffffff;
    background: var(--status-warn);
}
#jp-auto-toggle.on:hover {
    background: var(--status-warn);
    opacity: 0.88;
}

.jp-status-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
}
.jp-status-row:last-child { border-bottom: none; }
.jp-status-label {
    color: var(--text-mute);
    font-family: var(--font-sans);
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.08em;
    font-weight: 500;
}
.jp-status-value {
    font-family: var(--font-mono);
    color: var(--text);
    font-size: 13px;
}
.jp-status-value.on { color: var(--status-ok); }
.jp-status-value.warn { color: var(--status-warn); }

/* ─── Statistics mock — jackpot picker + generated-mock list ─ */
.jp-select {
    flex: 1;
    min-width: 0;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: var(--font-sans);
    font-size: 13px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    color-scheme: dark;
    appearance: auto;
}
:root[data-theme="light"] .jp-select { color-scheme: light; }
/*
 * The dropdown list is drawn by the OS, which does NOT inherit the select's own background — only
 * its text colour. With a light popup and near-white inherited text the options were unreadable,
 * so both are set explicitly here, per theme. Must be opaque: a translucent surface token would
 * composite against the OS list background and lose contrast again.
 */
.jp-select option { background: #23272F; color: #F6F7F9; }
:root[data-theme="light"] .jp-select option { background: #FFFFFF; color: #23272F; }
.jp-select:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.jp-winstats-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
}
.jp-winstats-actions button { flex: 1; }
.jp-winstats-list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.jp-winstats-item {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 12px;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text);
}
.jp-winstats-item .jp-winstats-item-meta { color: var(--text-mute); }

/* ─── Jackpot values editor — nested cards inside .section ─ */
.jp-values-card {
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 12px 14px;
    margin-bottom: 10px;
}
.jp-values-card:last-child { margin-bottom: 0; }
.jp-values-card-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
}
.jp-values-name {
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}
.jp-values-currency {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-faint);
    letter-spacing: 0.05em;
}
.jp-values-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
}
.jp-values-row + .jp-values-row {
    border-top: 1px solid var(--border);
    margin-top: 2px;
    padding-top: 8px;
}
.jp-values-level {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    color: var(--text-mute);
    letter-spacing: 0.08em;
    min-width: 60px;
}
.jp-values-row input[type="number"] {
    flex: 1;
    background: var(--surface-3);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: var(--font-mono);
    font-size: 13px;
    text-align: right;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    -moz-appearance: textfield;
}
.jp-values-row input[type="number"]::-webkit-outer-spin-button,
.jp-values-row input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.jp-values-row input[type="number"]:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-2);
}

/* ─── Mini Backoffice tab ──────────────────────────── */
.bo-reset-error {
    color: var(--status-error);
    font-size: 12px;
    margin-bottom: 12px;
    padding: 10px 14px;
    background: var(--surface-2);
    border-left: 3px solid var(--status-error);
    border-radius: var(--radius-sm);
}
.bo-section-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 48px;
}
/* Each section card is a top-level solid card */
.bo-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    overflow: hidden;
}
.bo-section.locked {
    border-color: var(--border-strong);
}
.bo-section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    cursor: pointer;
    -webkit-user-select: none;
    user-select: none;
}
.bo-section-header:hover { background: var(--surface-2); }
/* Re-allow text cursor inside the editable name field */
.bo-section-header .bo-name-input { cursor: text; }
.bo-collapse {
    background: transparent;
    border: none;
    padding: 0;
    width: 14px;
    height: 14px;
    color: var(--text-mute);
    font-size: 10px;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bo-collapse:hover {
    color: var(--text);
    background: transparent;
}
/* Drag handle for section + game rows. Only the handle is draggable —
 * not the row — so clicks on other row controls (rename, arrows, delete)
 * still feel like clicks, not drag gestures. */
.bo-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 18px;
    color: var(--text-mute);
    font-size: 12px;
    line-height: 1;
    cursor: grab;
    flex-shrink: 0;
    -webkit-user-select: none;
    user-select: none;
}
.bo-drag-handle:hover { color: var(--text); }
.bo-drag-handle.disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
}
.bo-drag-handle.dragging,
.bo-drag-handle:active { cursor: grabbing; }
/* The row being dragged gets faded so the user sees what they're holding. */
.bo-section.dragging,
.bo-game-row.dragging { opacity: 0.4; }
/* Drop indicator — thin accent line above or below the hovered target.
 * Pseudo-elements rather than a real DOM node so the indicator can flip
 * sides without re-rendering the list. */
.bo-section.drop-above,
.bo-game-row.drop-above { position: relative; }
.bo-section.drop-below,
.bo-game-row.drop-below { position: relative; }
.bo-section.drop-above::before,
.bo-game-row.drop-above::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -1px;
    height: 2px;
    background: var(--accent);
    pointer-events: none;
    z-index: 2;
}
.bo-section.drop-below::after,
.bo-game-row.drop-below::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: var(--accent);
    pointer-events: none;
    z-index: 2;
}
.bo-name-input {
    flex: 1;
    min-width: 0;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 7px 10px;
    font-family: var(--font-mono);
    font-size: 12px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
}
.bo-name-input:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.bo-name-locked {
    flex: 1;
    min-width: 0;
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 12px;
    padding: 7px 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Wraps the name + pencil so they cluster together on the left */
.bo-name-wrap {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
}
.bo-name-display {
    min-width: 0;
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 12px;
    padding: 7px 4px 7px 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bo-name-edit-btn {
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    width: 26px;
    height: 26px;
    padding: 0;
    color: var(--text-mute);
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 180ms var(--ease), color 180ms var(--ease);
}
.bo-name-edit-btn:hover {
    color: #ffffff;
    background: var(--accent);
}
.bo-game-count {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-mute);
    flex-shrink: 0;
    letter-spacing: 0.02em;
}
.bo-locked-badge {
    background: var(--surface-3);
    color: var(--text-mute);
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 9px;
    font-family: var(--font-mono);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    flex-shrink: 0;
    cursor: help;
}
.bo-section-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}
.bo-section-actions button {
    width: 26px;
    height: 26px;
    padding: 0;
    font-size: 11px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bo-section-actions button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}
.bo-section-body {
    padding: 12px 14px 14px;
    border-top: 1px solid var(--border);
    background: var(--surface-2);
}
.bo-game-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.bo-game-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: var(--radius-sm);
    transition: background 150ms var(--ease);
}
.bo-game-row:hover { background: var(--surface-3); }
.bo-game-row button {
    width: 22px;
    height: 22px;
    padding: 0;
    font-size: 10px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.bo-game-row button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}
.bo-game-name {
    flex: 1;
    min-width: 0;
    color: var(--text);
    font-size: 13px;
    font-family: var(--font-sans);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.bo-game-key {
    color: var(--text-faint);
    font-family: var(--font-mono);
    font-size: 10px;
    flex-shrink: 0;
}
.bo-game-remove {
    color: #ffffff !important;
    background: var(--status-error) !important;
}
.bo-game-remove:hover {
    background: var(--status-error) !important;
    opacity: 0.88;
}
.bo-add-row {
    display: flex;
    margin-top: 10px;
}
.bo-add-select {
    flex: 1;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: var(--font-sans);
    font-size: 12px;
    cursor: pointer;
    transition: border-color 200ms var(--ease);
}
.bo-add-select:hover { border-color: var(--border-bright); }
.bo-add-select:focus {
    outline: none;
    border-color: var(--accent);
}
.bo-warning {
    color: var(--status-warn);
    font-size: 11px;
    padding: 8px 12px;
    margin-top: 8px;
    background: var(--surface-2);
    border-left: 3px solid var(--status-warn);
    border-radius: var(--radius-sm);
    line-height: 1.5;
}

/* ─── Sub-tabs inside Mini Backoffice — pill segmented ─── */
/* Sits INSIDE a white card. Wrapper uses --surface-2 (gray-fill) so it
 * reads as an inset segmented control. Inactive pills are transparent
 * with a faint hover tint; active is solid indigo with white text. */
.subtab-nav {
    display: flex;
    gap: 2px;
    margin-bottom: 18px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 3px;
}
.subtab {
    flex: 1;
    background: transparent;
    color: var(--text-dim);
    border: none;
    border-radius: 7px;
    padding: 7px 12px;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    transition: background 180ms var(--ease), color 180ms var(--ease);
}
.subtab:hover {
    color: var(--text);
    background: var(--surface-3);
}
.subtab.active,
.subtab.active:hover {
    color: #ffffff;
    background: var(--accent);
}
.subtab:active { opacity: 0.85; }
.subtab:disabled { opacity: 0.4; cursor: not-allowed; }
.subtab-panel { display: none; }
.subtab-panel.active { display: block; animation: fadeIn 200ms var(--ease); }

/* ─── Game picker (Games sub-tab) ─────────────────── */
.bo-game-search {
    width: 100%;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 10px 12px;
    font-family: var(--font-sans);
    font-size: 13px;
    margin-bottom: 10px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
}
.bo-game-search::placeholder { color: var(--text-faint); }
.bo-game-search:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.bo-game-picker {
    max-height: 260px;
    overflow-y: auto;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 6px;
}
.bo-game-picker::-webkit-scrollbar { width: 6px; }
.bo-game-picker::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 3px;
}
.bo-game-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background 150ms var(--ease);
    font-size: 13px;
}
.bo-game-item:hover { background: var(--surface-3); }
.bo-game-item.selected {
    background: var(--accent);
    color: #ffffff;
    padding: 8px 12px;
}
.bo-game-item-name {
    color: var(--text);
    font-family: var(--font-sans);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.bo-game-item.selected .bo-game-item-name { color: #ffffff; }
.bo-game-item-key {
    color: var(--text-faint);
    font-family: var(--font-mono);
    font-size: 10px;
    flex-shrink: 0;
}
.bo-game-item.selected .bo-game-item-key { color: rgba(255, 255, 255, 0.7); }

/* ─── Game edit form ──────────────────────────────── */
.bo-edit-field {
    margin-bottom: 16px;
}
.bo-edit-field:last-child { margin-bottom: 0; }
.bo-edit-field > label {
    display: block;
    font-family: var(--font-sans);
    font-size: 10px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 6px;
    font-weight: 600;
}
.bo-field-hint {
    font-size: 10px;
    color: var(--text-faint);
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    margin-left: 6px;
    font-style: italic;
}
.bo-edit-field input[type="text"],
.bo-edit-field input[type="date"],
.bo-edit-field input[type="datetime-local"],
.bo-edit-field input[type="number"] {
    width: 100%;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-family: var(--font-sans);
    font-size: 13px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    color-scheme: dark;
}
:root[data-theme="light"] .bo-edit-field input[type="date"],
:root[data-theme="light"] .bo-edit-field input[type="datetime-local"],
:root[data-theme="light"] .bo-edit-field input[type="number"] {
    color-scheme: light;
}
.bo-edit-field input[type="text"]::placeholder { color: var(--text-faint); }
.bo-edit-field input[type="text"]:focus,
.bo-edit-field input[type="date"]:focus,
.bo-edit-field input[type="datetime-local"]:focus,
.bo-edit-field input[type="number"]:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.bo-edit-field select {
    width: 100%;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 12px;
    font-family: var(--font-sans);
    font-size: 13px;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    color-scheme: dark;
    appearance: auto;
}
:root[data-theme="light"] .bo-edit-field select {
    color-scheme: light;
}
.bo-edit-field select:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.bo-edit-readonly {
    background: var(--surface-2);
    color: var(--text-dim);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}
.bo-edit-readonly.on { color: var(--accent); border-color: var(--accent); }
.bo-edit-flags {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
    padding-top: 2px;
}
.bo-edit-flags > label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
    text-transform: none;
    letter-spacing: 0;
    margin: 0;
    font-weight: 400;
}
.bo-edit-flags input[type="checkbox"] {
    margin: 0;
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
    cursor: pointer;
}
.bo-edit-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    min-height: 32px;
}
.bo-edit-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--accent);
    color: #ffffff;
    border: none;
    border-radius: 999px;
    padding: 3px 4px 3px 10px;
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.bo-edit-tag .bo-tag-remove {
    background: transparent;
    border: none;
    color: #ffffff;
    cursor: pointer;
    padding: 0 6px;
    font-size: 12px;
    line-height: 1;
}
.bo-edit-tag .bo-tag-remove:hover { color: var(--status-error); background: transparent; }
.bo-edit-tags-empty {
    color: var(--text-faint);
    font-size: 11px;
    font-style: italic;
    padding: 4px 6px;
}
.bo-edit-tag-add {
    display: flex;
    gap: 6px;
    margin-top: 8px;
}
.bo-edit-tag-add input {
    flex: 1;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    font-family: var(--font-mono);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.bo-edit-tag-add input::placeholder {
    color: var(--text-faint);
    text-transform: none;
    letter-spacing: 0;
}
.bo-edit-tag-add input:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}

/* Toggle-button used for "Jackpot / Bonus", "fullscreen / ingame", etc. */
.bo-toggle-btn {
    padding: 9px 18px;
    font-size: 12px;
    font-family: var(--font-sans);
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    cursor: pointer;
    background: var(--surface-2);
    color: var(--text);
    border: none;
    border-radius: var(--radius-sm);
    transition: background 180ms var(--ease), color 180ms var(--ease);
}
.bo-toggle-btn:hover {
    background: var(--surface-3);
}
.bo-toggle-btn.on {
    color: #ffffff;
    background: var(--accent);
}
.bo-toggle-btn.on:hover { background: var(--accent-dim); }

/* Measure tab on/off buttons: label text plus a pill switch drawn with
 * pseudo-elements, so the JS can keep setting textContent. */
.ms-switch {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 7px 12px 7px 14px;
    font-size: 12px;
    font-family: var(--font-sans);
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    cursor: pointer;
    background: var(--surface-2);
    color: var(--text);
    border: none;
    border-radius: 20px;
    transition: background 180ms var(--ease);
}
.ms-switch:hover { background: var(--surface-3); }
.ms-switch::after {
    content: '';
    flex: none;
    width: 34px;
    height: 20px;
    box-sizing: border-box;
    border-radius: 10px;
    border: 2px solid var(--border-bright);
    background: var(--surface-3);
    transition: background 180ms var(--ease), border-color 180ms var(--ease);
}
.ms-switch::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 30px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--text-mute);
    transform: translateY(-50%);
    transition: right 180ms var(--ease), background 180ms var(--ease);
}
.ms-switch.on::after {
    border-color: var(--accent);
    background: var(--accent);
}
.ms-switch.on::before {
    right: 15px;
    background: #ffffff;
}

/* ─── Environment tab ─────────────────────────────── */
.env-casino-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}
.env-casino-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 9px 14px;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.2;
    text-align: center;
    transition: background 180ms var(--ease), border-color 180ms var(--ease), color 180ms var(--ease);
}
.env-casino-btn:hover {
    background: var(--surface-3);
}
.env-casino-btn.selected {
    background: var(--accent-bg);
    border-color: var(--accent);
    color: var(--accent);
}
.env-pill-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.env-pill {
    min-width: 36px;
    padding: 6px 10px;
    background: var(--surface-2);
    color: var(--text-dim);
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    transition: background 180ms var(--ease), border-color 180ms var(--ease), color 180ms var(--ease);
}
.env-pill:hover {
    background: var(--surface-3);
    color: var(--text);
}
.env-pill.selected {
    background: var(--accent-bg);
    border-color: var(--accent);
    color: var(--accent);
}
.env-field-hidden { display: none; }

/* ─── Sentry tab ──────────────────────────────────── */
.sn-pill-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.sn-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 999px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
}
.sn-event-pill {
    background: var(--accent);
    border-color: transparent;
    color: #ffffff;
}
.sn-empty {
    color: var(--text-faint);
    font-style: italic;
    font-size: 12px;
    padding: 4px 0;
}
.sn-key-tags {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-right: 6px;
}
.sn-tag {
    font-family: var(--font-mono);
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.sn-tag.multi {
    color: #ffffff;
    background: #0a84ff;
    border: 1px solid transparent;
}
.sn-tag.single {
    color: #ffffff;
    background: var(--status-warn);
    border: 1px solid transparent;
}
.sn-tag.unknown {
    color: var(--text-mute);
    background: var(--surface-2);
    border: 1px solid var(--border);
}
.sn-tag.section {
    color: #ffffff;
    background: var(--accent);
    border: 1px solid transparent;
}
.sn-total {
    border-top: 1px solid var(--border);
    margin-top: 10px;
    padding-top: 12px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
}
.sn-total-label {
    color: var(--accent);
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.sn-total-value {
    color: var(--accent);
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 600;
}
.sn-actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
}
.sn-actions button {
    flex: 1;
}
.sn-footer {
    margin-top: 8px;
    color: var(--text-faint);
    font-family: var(--font-mono);
    font-size: 11px;
}

/* ─── Jira tab ──────────────────────────────────── */
.ji-row {
    border-bottom: 1px solid var(--border);
}
.ji-row:last-child {
    border-bottom: none;
}
.ji-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px;
    cursor: pointer;
    transition: background 150ms var(--ease);
    border-radius: var(--radius-sm);
}
.ji-header:hover {
    background: var(--surface-2);
}
.ji-row-left,
.ji-row-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    align-self: center;
}
.ji-row-left { padding-left: 4px; }
.ji-chevron {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    color: var(--text-mute);
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ji-key {
    flex: 0 0 auto;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
    text-decoration: none;
    padding: 3px 10px;
    border: none;
    background: var(--accent);
    border-radius: 999px;
    transition: background 180ms var(--ease), opacity 120ms var(--ease);
    white-space: nowrap;
}
.ji-key:hover {
    background: var(--accent-dim);
    color: #ffffff;
}
.ji-key:active { opacity: 0.85; }
.ji-meta {
    flex: 1 1 auto;
    min-width: 0;
}
.ji-subjects {
    color: var(--text);
    font-size: 13px;
    line-height: 1.45;
    word-break: break-word;
}
.ji-subjects div + div {
    margin-top: 2px;
}
.ji-commits {
    margin-top: 3px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-faint);
    word-break: break-all;
}
.ji-filecount {
    flex: 0 0 auto;
    color: var(--text-faint);
    font-size: 11px;
    white-space: nowrap;
}
.ji-files {
    list-style: none;
    margin: 0 0 8px 0;
    padding: 4px 4px 4px 30px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
}
.ji-files li {
    padding: 2px 0;
    word-break: break-all;
}
.ji-empty {
    color: var(--text-faint);
    font-style: italic;
    font-size: 12px;
    padding: 4px 0;
}
/* Verified-counter line, sits between the range hint and the area chips. */
.ji-verified-line {
    margin-top: 4px;
    font-size: 11px;
    color: var(--text-mute);
    font-family: var(--font-mono);
}
.ji-verified-line:empty { display: none; }
/* "Open all in Jira" — single JQL search link in the tab header area.
   [hidden] is honoured by the renderer when there are no tickets. */
.ji-all-link {
    display: inline-block;
    margin-top: 6px;
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    color: #ffffff;
    text-decoration: none;
    padding: 3px 10px;
    border: none;
    background: var(--accent);
    border-radius: 999px;
    transition: background 180ms var(--ease), opacity 120ms var(--ease);
    white-space: nowrap;
}
.ji-all-link:hover { background: var(--accent-dim); color: #ffffff; }
.ji-all-link:active { opacity: 0.85; }
.ji-all-link[hidden] { display: none; }
/* Hot-areas chip strip — small, calm, informational. */
.ji-areas {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 10px 0 4px;
}
.ji-areas:empty { display: none; }
.ji-area-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--surface-2);
    color: var(--text-dim);
    font-family: var(--font-mono);
    font-size: 10px;
    line-height: 1.4;
    border: 1px solid var(--border);
}
.ji-area-chip .ji-area-count {
    color: var(--text);
    font-weight: 500;
}
/* Author tag — small, inline after the file count. */
.ji-authors {
    flex: 0 0 auto;
    color: var(--text-mute);
    font-size: 11px;
    white-space: nowrap;
    font-style: italic;
}
/* Per-name Teams deep-link. Inherits italic from .ji-authors so the line's
 * visual rhythm stays unchanged; only the color picks up the accent token
 * to signal clickability. Underline-on-hover matches .ji-commit-list a. */
.ji-author-link {
    color: var(--accent);
    text-decoration: none;
    font-style: inherit;
}
.ji-author-link:hover { text-decoration: underline; }
/* Verified checkbox column on the left of every ticket row. */
.ji-verified-cell {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
}
.ji-verified-cell input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: var(--accent);
}
.ji-row.verified .ji-subjects {
    color: var(--text-mute);
    text-decoration: line-through;
    text-decoration-color: var(--text-faint);
}
/* Expanded commit list — one per row, each linkable. */
.ji-commit-list {
    list-style: none;
    margin: 0 0 8px 0;
    padding: 4px 4px 4px 30px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
}
.ji-commit-list li {
    padding: 2px 0;
    word-break: break-all;
}
.ji-commit-list a {
    color: var(--accent);
    text-decoration: none;
}
.ji-commit-list a:hover { text-decoration: underline; }
.ji-commit-list .ji-commit-hash-text {
    color: var(--text-dim);
}
.ji-commit-list .ji-pr-link {
    margin-left: 6px;
    color: var(--accent-dim);
}

/* ─── Console tab ──────────────────────────────────── */
.cn-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 70vh;
    overflow-y: auto;
}
.cn-row {
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    cursor: pointer;
    transition: background 160ms var(--ease);
}
.cn-row:hover { background: var(--surface-3, var(--surface-2)); }
.cn-row-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.cn-pill {
    display: inline-block;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 999px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #ffffff;
    flex: 0 0 auto;
}
.cn-pill.level-error { background: var(--status-error); }
.cn-pill.level-warn  { background: var(--status-warn); color: #111; }
.cn-pill.cat {
    background: var(--surface);
    color: var(--text-dim);
    border: 1px solid var(--border);
}
.cn-pill.cat.cat-game-client { color: var(--accent); border-color: var(--accent); }
.cn-pill.cat.cat-asset       { color: var(--status-warn); border-color: var(--status-warn); }
.cn-pill.cat.cat-api         { color: var(--status-error); border-color: var(--status-error); }
.cn-pill.cat.cat-lobby-ui    { color: var(--accent-dim); border-color: var(--accent-dim); }
.cn-pill.cat.cat-lobby-logic { color: var(--text); border-color: var(--text-dim); }
.cn-msg {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text);
    word-break: break-word;
}
.cn-row:not(.expanded) .cn-msg {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.cn-count {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    flex: 0 0 auto;
}
.cn-time {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-dim);
    flex: 0 0 auto;
}
.cn-expanded {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed var(--border);
}
.cn-expanded .cn-source {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    margin-bottom: 6px;
    word-break: break-all;
}
.cn-owner {
    font-size: 11px;
    color: var(--text-dim);
    margin-bottom: 6px;
    line-height: 1.4;
}
.cn-owner-label {
    font-weight: 600;
    color: var(--text);
    margin-right: 4px;
}
.cn-stack {
    font-family: var(--font-mono);
    font-size: 11px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 10px;
    margin: 0;
    overflow-x: auto;
    white-space: pre;
    color: var(--text);
}
.cn-empty {
    text-align: center;
    color: var(--text-dim);
    font-size: 12px;
    padding: 24px 8px;
}

/* ─── Figma tab ──────────────────────────────────── */
.fg-drop-zone {
    margin-top: 8px;
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    outline: none;
    cursor: pointer;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
}
.fg-drop-zone:focus-visible { border-color: var(--accent); }
.fg-drop-zone[data-empty="0"] {
    cursor: default;
    border-style: solid;
    border-color: var(--border-strong);
    padding: 12px;
    min-height: 0;
}
.fg-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--text-dim);
    text-align: center;
    font-size: 12px;
    user-select: none;
}
.fg-empty-glyph {
    font-size: 24px;
    color: var(--text-mute);
    line-height: 1;
}
.fg-loaded {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
}
.fg-thumb {
    max-width: 200px;
    max-height: 150px;
    object-fit: contain;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface-3);
    display: block;
}
.fg-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
}
.fg-dims {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-dim);
    font-variant-numeric: tabular-nums;
}
.fg-rendered {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-mute);
    font-variant-numeric: tabular-nums;
}
.fg-status {
    margin-top: 6px;
    font-size: 12px;
    color: var(--text-dim);
    min-height: 16px;
}
.fg-status[data-tone="err"] { color: var(--status-error); }
.fg-status[data-tone="warn"] { color: var(--status-warn); }
.fg-opacity-row {
    display: flex;
    align-items: center;
    gap: 12px;
}
.fg-opacity-row input[type="range"] {
    flex: 1;
    accent-color: var(--accent);
    cursor: pointer;
}
.fg-opacity-value {
    min-width: 42px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--text-dim);
    font-size: 12px;
}
.fg-xy-group {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.fg-xy-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.fg-xy-axis {
    color: var(--text-mute);
    font-size: 11px;
    font-weight: 500;
}
.fg-xy-group input[type="number"] {
    width: 64px;
    background: var(--surface-2);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 6px 8px;
    font-family: var(--font-mono);
    font-size: 13px;
    text-align: right;
    transition: border-color 200ms var(--ease), background 200ms var(--ease);
    -moz-appearance: textfield;
}
.fg-xy-group input[type="number"]:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--surface-3);
}
.fg-xy-group input[type="number"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.fg-xy-unit {
    color: var(--text-mute);
    font-size: 11px;
    font-family: var(--font-mono);
}

/* ─── Measure tab ─────────────────────────────────── */
.ms-label {
    margin: 14px 0 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.ms-guide-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
}
.ms-guide-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ms-guide-axis {
    min-width: 124px;
    color: var(--text-dim);
    font-size: 12px;
}
/* .row / .hint set display, which beats the hidden attribute - restore it in this tab. */
[data-panel="measure"] [hidden] {
    display: none;
}
.ms-guide-row button {
    padding: 4px 10px;
}
.ms-wrap-tag {
    color: #0EA5E9;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
}
/* Snapped positions can be fractional (260.5) - wider than the Figma inputs. */
[data-panel="measure"] .fg-xy-group input[type="number"] {
    width: 80px;
}
.ms-empty {
    color: var(--text-faint);
    font-size: 12px;
    font-style: italic;
}

/* ─── Phone overlay — usable at ~390px ──────────────────── */
/* The panel was built for a 720px popup; on a phone it renders as a
 * full-viewport overlay iframe. Tighten gutters, let dense rows reflow, and
 * keep tap targets comfortable. Targets the iPhone-class viewport. */
@media (max-width: 480px) {
    body {
        /* Honor the notch / home-indicator insets on iOS Safari. */
        padding: max(16px, env(safe-area-inset-top)) 14px
                 max(28px, env(safe-area-inset-bottom)) 14px;
    }

    h1 {
        font-size: 16px;
        flex-wrap: wrap;
        gap: 8px;
    }

    /* Tab strip stays one horizontal scroller, but pad the trailing edge so
     * the last tab clears the overlay's close button, and give a little more
     * vertical breathing room for thumbs. */
    .tab-nav {
        margin: 16px 0 18px;
        gap: 8px;
        padding-right: 48px;
        /* iOS Safari momentum scroll for the strip. */
        -webkit-overflow-scrolling: touch;
    }
    .tab-nav button {
        padding: 10px 14px;
        font-size: 13px;
    }

    /* Generic two-column rows wrap by default (.row already has flex-wrap);
     * make full-width controls stretch so they stay tappable. */
    .row > button,
    .row > select,
    .row > input {
        flex: 1 1 auto;
        min-height: 40px;
    }

    /* Jira rows: the right cluster (file count + authors) collides with the
     * middle column at this width. Let the header wrap and drop the right
     * cluster onto its own line aligned with the meta column. */
    .ji-header {
        flex-wrap: wrap;
        row-gap: 6px;
    }
    .ji-row-right {
        flex-basis: 100%;
        padding-left: 26px;
        justify-content: flex-start;
    }

    /* Mini Backoffice dense rows: enlarge the inline action buttons so the
     * ✎ / delete / drag affordances stay tappable, and let game rows wrap. */
    .bo-game-row {
        flex-wrap: wrap;
        row-gap: 4px;
    }
    .bo-game-row button,
    .bo-section-actions button {
        min-width: 36px;
        min-height: 36px;
    }

    /* Any settings/preset action button rows: full-width, easy to hit. */
    button {
        padding: 10px 14px;
    }
    button.ms-switch {
        flex: none;
    }
}

</style>
</head>
<body>

<h1>
    <span>Lobby QA Panel <span id="version"></span></span>
    <span class="header-controls">
        <button id="theme-btn" type="button" title="Toggle theme">☀</button>
        <button id="reload-btn" type="button" title="Refresh panel state">↻</button>
        <span class="conn off" id="conn-status">disconnected</span>
    </span>
</h1>

<nav class="tab-nav" id="tab-nav">
    <button type="button" class="tab active" data-tab="jira">Jira</button>
    <button type="button" class="tab" data-tab="languages">Languages</button>
    <button type="button" class="tab" data-tab="feature2">Jackpot Mock</button>
    <button type="button" class="tab" data-tab="jpwidget">Jackpot Widgets</button>
    <button type="button" class="tab" data-tab="backoffice">Mini Backoffice</button>
    <button type="button" class="tab" data-tab="campaigns">Campaigns</button>
    <button type="button" class="tab" data-tab="sentry">Sentry</button>
    <button type="button" class="tab" data-tab="console">Console<span class="tab-badge" id="cn-tab-badge" hidden>0</span></button>
    <button type="button" class="tab" data-tab="environment">Environment</button>
    <button type="button" class="tab" data-tab="measure">Measure</button>
    <!-- Figma tab disabled — re-enable by uncommenting -->
    <!-- <button type="button" class="tab" data-tab="figma">Figma</button> -->
    <button type="button" class="tab" data-tab="settings">Settings</button>
</nav>

<!-- ════════════ TAB: JIRA ════════════ -->
<!-- Read-only list of Jira tickets included in this build, harvested at  -->
<!-- build time from git log between HEAD and the most recent prior       -->
<!-- version-bump commit. Each ticket key links out to the project's Jira -->
<!-- instance. Static data — no controls, no save/reset wiring.           -->
<div class="tab-panel active" data-panel="jira">
    <h2>Tickets in this build <span class="badge" id="ji-count">0</span></h2>
    <div class="section">
        <div class="hint" id="ji-range-hint">Reading build metadata…</div>
        <a class="ji-all-link" id="ji-all-link" target="_blank" rel="noopener noreferrer" hidden>Open all in Jira ↗</a>
        <div class="ji-verified-line" id="ji-verified-line"></div>
        <div class="ji-areas" id="ji-areas"></div>
        <div id="ji-list"></div>
    </div>
</div>

<!-- ════════════ TAB: LANGUAGES ════════════ -->
<!-- Everything translation-related lives here. Future QA tools should go in -->
<!-- their own tab panels (see the Feature 2 placeholder below for the      -->
<!-- exact pattern).                                                        -->
<div class="tab-panel" data-panel="languages">
    <h2>Display mode</h2>
    <div class="section">
        <div class="row">
            <button data-mode="normal">Normal</button>
            <button data-mode="pseudoLoc" title="Wraps every t() call in markers + length expansion. Hardcoded strings won't get the marker; layout breaks become obvious.">Pseudo-loc</button>
            <button data-mode="showKeys" title="Replaces every translated string with its i18n key. Match on-screen text to JSON entries instantly.">Show keys</button>
        </div>
    </div>

    <h2>Current language <span class="badge" id="current-lang">—</span></h2>
    <div class="section">
        <div class="lang-grid" id="lang-grid"></div>
    </div>

    <h2>Coverage matrix <button id="preload-all">Preload all</button></h2>
    <div class="section">
        <div class="hint">Click any row to inspect missing or translated keys.</div>
        <table class="coverage-table">
            <thead>
                <tr>
                    <th>Lang</th>
                    <th>Present</th>
                    <th>Missing</th>
                    <th>%</th>
                </tr>
            </thead>
            <tbody id="coverage-body"></tbody>
        </table>
    </div>

    <h2>Status legend</h2>
    <div class="section">
        <div class="legend">
            <div class="legend-item missing"><span class="dot"></span><div class="legend-text"><strong>MISSING</strong>key absent in target language</div></div>
            <div class="legend-item empty"><span class="dot"></span><div class="legend-text"><strong>EMPTY</strong>value is empty or whitespace</div></div>
            <div class="legend-item keyAsValue"><span class="dot"></span><div class="legend-text"><strong>KEY AS VALUE</strong>value matches the key name (i18next fell back)</div></div>
            <div class="legend-item sameAsRef"><span class="dot"></span><div class="legend-text"><strong>SAME AS REF</strong>identical to English — likely untranslated copy (or proper noun)</div></div>
            <div class="legend-item translated"><span class="dot"></span><div class="legend-text"><strong>TRANSLATED</strong>value differs from English, looks legitimate</div></div>
        </div>
    </div>

    <h2 id="inspector-title" style="display:none">
        <span>Inspector — <span id="inspector-lang"></span></span>
        <button id="inspector-close">Close</button>
    </h2>
    <div class="inspector" id="inspector-section" style="display:none">
        <div class="toolbar">
            <button data-filter="all" class="active">All</button>
            <button data-filter="missing">Missing</button>
            <button data-filter="suspect">Suspect</button>
            <button data-filter="translated">Translated</button>
        </div>
        <input type="text" id="inspector-search" placeholder="Filter by key name…" autocomplete="off" />
        <div class="stats" id="inspector-stats"></div>
        <div class="list" id="inspector-list"></div>
    </div>

    <h2>Missing keys <span class="badge" id="missing-count">0</span></h2>
    <div class="section">
        <div class="row" style="margin-bottom:8px">
            <button id="export-csv" class="primary">Export CSV</button>
            <button id="clear-missing" class="danger">Clear list</button>
        </div>
        <div class="missing-list" id="missing-list"></div>
        <div class="diff-popup" id="diff-popup">
            <div class="diff-key" id="diff-key"></div>
            <div id="diff-rows"></div>
        </div>
    </div>

</div>

<!-- ════════════ TAB: JACKPOT MOCK ════════════ -->
<!-- Lets QA inject synthetic jackpot value updates to trigger the         -->
<!-- odometer animations on demand. Goes through the jackpotsStore's       -->
<!-- existing public API — no production code touched.                     -->
<div class="tab-panel" data-panel="feature2">
    <h2>Jackpot / Bonus</h2>
    <div class="section">
        <div class="hint">Toggles the 'noJackpot' restriction. When ON, the lobby swaps "Jackpot" vocabulary for "Bonus" — affects section titles, tab names, filter labels, help path, and linked-jackpot display.</div>
        <div class="jp-toggle-row">
            <button type="button" id="jp-nojackpot-toggle" class="bo-toggle-btn">Jackpot (default)</button>
        </div>
    </div>

    <h2>Instant jackpot</h2>
    <div class="section">
        <div class="hint">The InstantLink instance is appended to the poll response by a production-only backend, so on QA/dev envs it never arrives. Injecting creates a synthetic instance in the jackpots store and auto-enables mock mode (stops polling). Toggle mocking off to resume polling — the synthetic instance is dropped by the next real poll.</div>
        <div class="jp-status-row">
            <span class="jp-status-label">InstantLink</span>
            <span class="jp-status-value" id="jp-instant-status">&mdash;</span>
        </div>
        <div id="jp-instant-section-hint" class="hint" style="display:none">No section on this env is configured with jackpotName InstantLink — the store mock will apply, but no INSTANT panel will render in the lobby.</div>
        <div class="jp-control-row" style="margin-top:10px">
            <label for="jp-instant-value">Grand</label>
            <input id="jp-instant-value" type="number" min="0" step="1" style="flex:1;width:auto" placeholder="e.g. 1612639">
            <button id="jp-instant-inject" type="button">Inject</button>
        </div>
        <div class="hint">Value is in store units — the lobby displays value / 100 (1612639 renders as 16,126.39). Written for the active balance currency + EGT.</div>
        <div id="jp-instant-feedback" class="jp-feedback"></div>
    </div>

    <h2>Statistics mock</h2>
    <div class="section">
        <div class="hint">Takes over the statistics drawer that opens when a jackpot level is tapped. While mocking is ON the drawer shows generated wins and the real /api/jackpot/win/stats request is switched off, so nothing 404s. Only jackpots you generate for have data — every other jackpot reads as "no wins", so its drawer opens on the "nobody has won yet" placeholder.</div>
        <div class="jp-status-row">
            <span class="jp-status-label">Win statistics</span>
            <span class="jp-status-value" id="jp-winstats-status">&mdash;</span>
        </div>
        <div class="jp-toggle-row" style="margin-top:10px">
            <button type="button" id="jp-winstats-toggle" class="bo-toggle-btn">Mocking off</button>
        </div>
        <div class="jp-control-row">
            <label for="jp-winstats-jackpot">Jackpot</label>
            <select id="jp-winstats-jackpot" class="jp-select"></select>
        </div>
        <div class="jp-control-row" style="margin-top:10px">
            <label for="jp-winstats-count">Wins per level</label>
            <input id="jp-winstats-count" type="number" min="0" max="100" step="1" value="14">
        </div>
        <div class="bo-edit-flags" style="margin-top:12px">
            <label><input type="checkbox" id="jp-winstats-zero" checked /> Leave the lowest level on zero wins (3+ level families only)</label>
        </div>
        <div class="jp-winstats-actions">
            <button id="jp-winstats-generate" type="button">Generate</button>
            <button id="jp-winstats-clear" type="button">Clear this one</button>
            <button id="jp-winstats-clear-all" type="button">Clear all</button>
        </div>
        <div class="hint">Generate also freezes the jackpot value poll (mock mode ON) and writes the matching win counters into it, so the panel level becomes clickable and the drawer agrees with it. The zero-win level gets a chip with a 0 counter; selecting it shows the "nobody has won yet" placeholder — that is the point of it. It is skipped for single-level jackpots, where zeroing the only level would just empty the drawer. Turning mocking off restores both the counters and the real request.</div>
        <div id="jp-winstats-list" class="jp-winstats-list"></div>
        <div id="jp-winstats-feedback" class="jp-feedback"></div>
    </div>

    <h2>Auto trigger</h2>
    <div class="section">
        <div class="jp-control-row">
            <label for="jp-auto-interval">Every</label>
            <input id="jp-auto-interval" type="number" value="3" min="1" step="1">
            <span class="jp-unit">s</span>
            <label for="jp-auto-percent" class="jp-label-tight">apply</label>
            <input id="jp-auto-percent" type="number" value="2" step="1">
            <span class="jp-unit">%</span>
        </div>
        <div class="jp-control-row" style="margin-top:10px">
            <button id="jp-auto-toggle" type="button" style="flex:1">Start auto</button>
        </div>
        <div class="hint">Each tick applies the percentage to the current values and compounds. Negative percentages simulate jackpot resets after a win.</div>
        <div id="jp-auto-feedback" class="jp-feedback"></div>
    </div>

    <h2>Status</h2>
    <div class="section">
        <div class="jp-status-row">
            <span class="jp-status-label">Jackpots loaded</span>
            <span class="jp-status-value" id="jp-status-count">—</span>
        </div>
        <div class="jp-status-row">
            <span class="jp-status-label">Auto trigger</span>
            <span class="jp-status-value" id="jp-status-auto">stopped</span>
        </div>
    </div>

    <h2>Jackpot values</h2>
    <div class="section">
        <div class="hint" id="jp-values-hint"></div>
        <div id="jp-values-list"></div>
    </div>
</div>

<!-- ════════════ TAB: JACKPOT WIDGETS ════════════ -->
<!-- Injects the separate jackpot-widget product (the operator-facing bundle)  -->
<!-- as a section on the lobby page, for visual QA in the real layout. Not an  -->
<!-- integration: the lobby renders its own jackpots from its own store, and   -->
<!-- this whole tab is dead-stripped in prod with the rest of the panel.       -->
<!-- The dropdown options come from the loaded bundle, never from a list       -->
<!-- written down here - that is what stops them drifting from what it         -->
<!-- actually registers.                                                       -->
<div class="tab-panel" data-panel="jpwidget">
    <h2>Bundle</h2>
    <div class="section">
        <div class="hint">Pick the widget <code>.zip</code> you were given. It is unpacked in your browser — nothing is uploaded, no host or server is needed. To test another version, just pick a different zip. The version and jackpot list come from the bundle itself.</div>
        <div class="jp-control-row">
            <input id="jpw-zip" type="file" accept=".zip,application/zip,application/x-zip-compressed" style="flex:1">
        </div>
        <div class="hint" id="jpw-zip-status">No zip loaded.</div>
        <div class="jp-control-row" style="margin-top:10px">
            <button id="jpw-apply" type="button" style="flex:1" disabled>Apply</button>
            <button id="jpw-disable" type="button">Remove</button>
        </div>
        <div id="jpw-feedback" class="jp-feedback"></div>
    </div>

    <h2>Data</h2>
    <div class="section">
        <div class="jp-toggle-row">
            <button type="button" id="jpw-mock-toggle" class="bo-toggle-btn">Live data</button>
        </div>
        <div class="jp-control-row" style="margin-top:10px">
            <label for="jpw-api">API</label>
            <input id="jpw-api" type="text" spellcheck="false" style="flex:1;width:auto" placeholder="defaults to the lobby's apiHost">
        </div>
        <div class="hint">The widget polls <code>/api/jackpot/stats</code>; the lobby polls <code>/api/jackpot/stats/internal</code>. Two independent polls at different intervals, so the injected widgets and the lobby's own panels will not tick in lockstep — that is expected. Mock data is served in-page and leaves the lobby's own poll alone.</div>
    </div>

    <h2>Widgets</h2>
    <div class="section">
        <div class="jp-control-row">
            <label for="jpw-type">Variant</label>
            <select id="jpw-type" style="flex:1"></select>
        </div>
        <div class="jp-control-row" style="margin-top:10px">
            <label for="jpw-currency">Currency</label>
            <select id="jpw-currency" style="width:110px"></select>
            <label for="jpw-units" class="jp-label-tight">units</label>
            <input id="jpw-units" type="number" min="1" step="1" style="width:90px">
            <label for="jpw-language" class="jp-label-tight">lang</label>
            <select id="jpw-language"></select>
        </div>
        <div class="hint" id="jpw-lists-hint">Currency, language and API default to this lobby session. The lists fill in from the bundle after the first Apply.</div>
        <div id="jpw-jackpots"></div>
    </div>

    <h2>Status</h2>
    <div class="section">
        <div class="jp-status-row">
            <span class="jp-status-label">State</span>
            <span class="jp-status-value" id="jpw-status">idle</span>
        </div>
        <div class="jp-status-row">
            <span class="jp-status-label">Bundle version</span>
            <span class="jp-status-value" id="jpw-version">&mdash;</span>
        </div>
        <div class="hint" id="jpw-reload-hint" style="display:none">Remove only takes the section off the page. The shipped bundle has no unmount API, so the widgets stay subscribed and keep polling — reload the lobby for a clean slate.</div>
    </div>
</div>

<!-- ════════════ TAB: MINI BACKOFFICE ════════════ -->
<!-- Live editor for the parsed sections + games in appStore. Goes through  -->
<!-- setSections / setSupportedGames — no production code touched.          -->
<div class="tab-panel" data-panel="backoffice">
    <nav class="subtab-nav">
        <button type="button" class="subtab active" data-subtab="bo-sections">Sections</button>
        <button type="button" class="subtab" data-subtab="bo-games">Games</button>
        <button type="button" class="subtab" data-subtab="bo-casino">Casino</button>
    </nav>

    <!-- ── Sub-tab: Sections ─────────────────────── -->
    <div class="subtab-panel active" data-subpanel="bo-sections">
        <h2>
            Sections
            <button id="bo-reset-btn" type="button">Reset to API</button>
        </h2>
        <div class="hint">Live edits — applied to the lobby instantly. Locked sections (well-known names or special config) can't be renamed or deleted, but their games can still be edited.</div>
        <div id="bo-reset-error" class="bo-reset-error" style="display:none"></div>
        <div class="section" style="margin-bottom:10px">
            <div class="hint">Injects a synthetic HOT_AND_COLD section using donor games from other sections — lets QA preview the merged hot/cold UI without waiting for the BE stats endpoint. Transient: cleared by Reset to API.</div>
            <div class="jp-toggle-row">
                <button type="button" id="bo-hotcold-mock-toggle" class="bo-toggle-btn">Inject HOT_AND_COLD mock</button>
            </div>
        </div>
        <div id="bo-section-list" class="bo-section-list"></div>
    </div>

    <!-- ── Sub-tab: Games ────────────────────────── -->
    <div class="subtab-panel" data-subpanel="bo-games">
        <h2>Pick a game</h2>
        <div class="section">
            <input type="text" id="bo-game-search" class="bo-game-search" placeholder="Search by name or key…" autocomplete="off" />
            <div id="bo-game-picker" class="bo-game-picker"></div>
        </div>

        <h2 id="bo-edit-title" style="display:none"><span id="bo-edit-title-text">Edit game</span></h2>
        <div class="section" id="bo-edit-form" style="display:none">
            <div class="bo-edit-field">
                <label>Activated on</label>
                <input type="date" id="bo-edit-activatedOn" autocomplete="off" />
            </div>
            <div class="bo-edit-field">
                <label>Added to lobby on</label>
                <input type="date" id="bo-edit-addedToLobbyOn" autocomplete="off" />
            </div>
            <div class="bo-edit-field">
                <label>NEW badge status <span class="bo-field-hint">derived via isGameNew()</span></label>
                <div class="bo-edit-readonly" id="bo-edit-newgame-state">—</div>
            </div>
            <div class="bo-edit-field">
                <label>Game features</label>
                <div id="bo-edit-features" class="bo-edit-tags"></div>
                <div class="bo-edit-tag-add">
                    <input type="text" id="bo-edit-features-input" placeholder="Add feature (e.g. HIGH_RTP)" autocomplete="off" />
                    <button type="button" id="bo-edit-features-add">Add</button>
                </div>
            </div>
            <div class="bo-edit-field">
                <label>Other flags</label>
                <div class="bo-edit-flags">
                    <label><input type="checkbox" id="bo-edit-topGame" /> Top game</label>
                    <label><input type="checkbox" id="bo-edit-isInTournament" /> In tournament</label>
                </div>
            </div>
            <div class="bo-edit-field">
                <label>Jackpot name</label>
                <input type="text" id="bo-edit-jackpotName" placeholder="e.g. progressive" autocomplete="off" />
            </div>
            <div class="bo-edit-field">
                <label>Display jackpot name</label>
                <input type="text" id="bo-edit-displayJackpotName" autocomplete="off" />
            </div>
            <div class="bo-edit-field">
                <label>Current players <span class="bo-field-hint">QA override — badge hides when 0</span></label>
                <div class="bo-edit-flags">
                    <label><input type="checkbox" id="bo-edit-currentPlayers-toggle" /> Show</label>
                    <input type="number" id="bo-edit-currentPlayers" min="0" step="1" autocomplete="off" style="max-width:120px" />
                </div>
            </div>
            <div class="bo-edit-field">
                <label><span id="bo-edit-bet-label">Bet range</span> <span class="bo-field-hint" id="bo-edit-bet-hint">Applied to the current currency only — set both to 0 to hide</span></label>
                <div class="bo-edit-flags">
                    <label>Min <input type="number" id="bo-edit-minBet" min="0" step="0.01" autocomplete="off" style="max-width:120px" /></label>
                    <label>Max <input type="number" id="bo-edit-maxBet" min="0" step="0.01" autocomplete="off" style="max-width:120px" /></label>
                </div>
            </div>
        </div>

        <h2>Thumbnail checker</h2>
        <div class="section">
            <div class="hint">Checks every game thumbnail on the CDN via HEAD requests. Games whose thumbnail returns a non-200 response are listed below.</div>
            <button id="bo-thumb-check-btn" type="button" class="primary">Check missing thumbnails</button>
            <div id="bo-thumb-status" style="display:none;margin-top:8px;font-size:11px;color:var(--text-dim)"></div>
            <div id="bo-thumb-results" style="display:none;margin-top:10px"></div>
        </div>
    </div>

    <!-- ── Sub-tab: Casino ──────────────────────────── -->
    <div class="subtab-panel" data-subpanel="bo-casino">
        <h2>Lobby mode</h2>
        <div class="section">
            <div class="hint">Switches between fullscreen and ingame mode. Ingame shows the InGameHeader and adjusts layouts as if the lobby were opened from within a game.</div>
            <div class="bo-edit-field">
                <label>Open type</label>
                <div class="bo-edit-flags">
                    <button type="button" id="bo-casino-opentype" class="bo-toggle-btn">fullscreen</button>
                </div>
            </div>
        </div>

        <h2>Seasonal branding</h2>
        <div class="section">
            <div class="hint">Overrides the seasonalBranding feature flag. Affects game thumbnails, badges, jackpot panels, intro and effects (layered snow, fairy lights, frost and amber section icons for christmas; bats, webs and fog for halloween). Neither ships a palette — the lobby colours stay stock.</div>
            <div class="bo-edit-field">
                <label>Branding</label>
                <select id="bo-casino-branding" class="jp-select">
                    <option value="">None</option>
                    <option value="christmas">christmas</option>
                    <option value="halloween">halloween</option>
                    <option value="crocobet">crocobet</option>
                    <option value="easter">easter</option>
                    <option value="winbet">winbet</option>
                    <option value="betano">betano</option>
                </select>
            </div>
        </div>

        <h2>Currency</h2>
        <div class="section">
            <div class="hint">Switches the player's currency at runtime. Affects formatted balance, formatted prizes, and per-currency bet ranges on game cards. Options are a fixed QA list — the current platform-assigned currency is appended when it falls outside the list.</div>
            <div class="bo-edit-field">
                <label>Currency <span class="bo-field-hint" id="bo-casino-currency-hint"></span></label>
                <select id="bo-casino-currency" class="jp-select"></select>
            </div>
        </div>

        <h2>Campaign feature flags</h2>
        <div class="section">
            <div class="hint">Gates whether each campaign section renders at all. Mirrors featureFlagsStore — changes here apply immediately, for this session only.</div>
            <div class="bo-edit-field">
                <div class="bo-edit-flags">
                    <label><input type="checkbox" id="bo-flag-prizePop" /> PrizePop campaign</label>
                    <label><input type="checkbox" id="bo-flag-tournaments" /> Tournaments campaign</label>
                    <label><input type="checkbox" id="bo-flag-rebate" /> Rebate campaign</label>
                </div>
            </div>
        </div>
    </div>
</div>

<!-- ════════════ TAB: CAMPAIGNS ════════════ -->
<!-- Inject synthetic ("mocked") campaigns so the lobby renders them as if   -->
<!-- they were real, without a real backoffice API call. Tournaments and    -->
<!-- PrizePop sub-tabs are independent (different stores, different mocks). -->
<div class="tab-panel" data-panel="campaigns">
    <nav class="subtab-nav">
        <button type="button" class="subtab active" data-subtab="cm-tournaments">Tournaments</button>
        <button type="button" class="subtab" data-subtab="cm-prizepop">PrizePop</button>
        <button type="button" class="subtab" data-subtab="cm-rebate">Rebate</button>
    </nav>

    <!-- ── Sub-tab: Tournaments ─────────────────────── -->
    <!-- Three states, all rendered inline by the JS via cmRenderState():    -->
    <!--   A) mockMode === false: empty state + "Enable mocking" CTA         -->
    <!--   B) mockMode === true, no list yet: paste creds + Fetch / Skip     -->
    <!--   C) mockMode === true, list present: editable list + add wizard    -->
    <div class="subtab-panel active" data-subpanel="cm-tournaments">
        <div id="cm-tournaments-root"></div>
        <div id="cm-tn-feedback" class="jp-feedback"></div>
    </div>

    <!-- ── Sub-tab: PrizePop ─────────────────────────── -->
    <!-- Mirrors the Tournaments sub-tab three-state pattern via ppRenderState(): -->
    <!--   A) mockMode === false: empty state + "Enable mocking" CTA         -->
    <!--   B) mockMode === true, no list yet: paste creds + Fetch / Skip     -->
    <!--   C) mockMode === true, list present: editable list + add form      -->
    <div class="subtab-panel" data-subpanel="cm-prizepop">
        <div id="pp-prizepop-root"></div>
        <div id="pp-feedback" class="jp-feedback"></div>
    </div>

    <!-- ── Sub-tab: Rebate ─────────────────────────────── -->
    <!-- Mirrors the PrizePop sub-tab three-state pattern via rbRenderState(): -->
    <div class="subtab-panel" data-subpanel="cm-rebate">
        <div id="rb-rebate-root"></div>
        <div id="rb-feedback" class="jp-feedback"></div>
    </div>
</div>

<!-- ════════════ TAB: SENTRY ════════════ -->
<!-- Read-only inspector for the data the tracker singletons are staging   -->
<!-- for the next Sentry transaction. Polls every 2s — restores the        -->
<!-- inspection capability of the deleted ClickTrackingDebugWidget plus    -->
<!-- three new sections (section navigation, search queries, span trace).  -->
<div class="tab-panel" data-panel="sentry">
    <h2>Event order <span class="badge" id="sn-event-count">0</span></h2>
    <div class="section">
        <div class="hint">Last 20 events as they fired (oldest first).</div>
        <div id="sn-event-order" class="sn-pill-row"></div>
    </div>

    <h2>Navigation tabs</h2>
    <div class="section">
        <div id="sn-nav-tabs"></div>
    </div>

    <h2>Game clicks</h2>
    <div class="section">
        <div id="sn-game-launches"></div>
    </div>

    <h2>Jackpot statistics opens</h2>
    <div class="section">
        <div id="sn-jackpot-stats-opens"></div>
    </div>

    <h2>Other clicks</h2>
    <div class="section">
        <div id="sn-other-clicks"></div>
    </div>

    <h2>User data</h2>
    <div class="section">
        <div id="sn-user-data"></div>
    </div>

    <h2>Game transaction</h2>
    <div class="section">
        <div id="sn-transaction"></div>
    </div>

    <h2>Section navigation <span class="badge">new</span></h2>
    <div class="section">
        <div class="hint">Counts and history from SectionNavigationTracker — added since the widget was deleted.</div>
        <div id="sn-section-navigation"></div>
        <div class="hint">history (source:section, newest last)</div>
        <div id="sn-section-nav-history" class="sn-pill-row"></div>
    </div>

    <h2>Dynamic sections <span class="badge">new</span></h2>
    <div class="section">
        <div class="hint">Configured dynamic sections (HOT_AND_COLD, TOP_WIN_GAMES) and whether each is currently active (BE returned enough data to display).</div>
        <div id="sn-dynamic-sections"></div>
    </div>

    <h2>Search queries <span class="badge">new</span></h2>
    <div class="section">
        <div class="hint">Search-box history staged onto the active span.</div>
        <div id="sn-search-queries" class="sn-pill-row"></div>
    </div>

    <h2>Active span <span class="badge">new</span></h2>
    <div class="section">
        <div class="hint">Trace id of the span the trackers will attach to. Reads via Sentry's spanContext() and falls back to (no active span) if unavailable.</div>
        <div class="jp-status-row">
            <span class="jp-status-label">trace id</span>
            <span class="jp-status-value" id="sn-trace-id">—</span>
        </div>
    </div>

    <h2>Total interactions</h2>
    <div class="section">
        <div class="sn-total">
            <span class="sn-total-label">Total</span>
            <span class="sn-total-value" id="sn-total">0</span>
        </div>
    </div>

    <h2>Actions</h2>
    <div class="section">
        <div class="sn-actions">
            <button type="button" id="sn-sync-btn" class="primary">Force-sync to Sentry</button>
            <button type="button" id="sn-clear-btn" class="danger">Clear all</button>
        </div>
        <div class="sn-footer" id="sn-footer">Auto-refresh every 2s</div>
    </div>
</div>

<!-- ════════════ TAB: CONSOLE ════════════ -->
<!-- Runtime-captured errors and warnings, categorized by heuristic source -->
<!-- (game-client, asset, api, lobby-ui, lobby-logic, unknown). Cleared on -->
<!-- lobby navigation. Drives the red badge on the tab strip.              -->
<div class="tab-panel" data-panel="console">
    <h2>Captured errors <span class="badge" id="cn-count">0</span></h2>
    <div class="section">
        <div class="hint" id="cn-summary">No errors captured yet.</div>
        <div class="row" style="margin-top:8px">
            <button type="button" id="cn-clear-btn" class="danger">Clear all</button>
        </div>
    </div>
    <h2>Entries</h2>
    <div class="section">
        <div id="cn-list" class="cn-list"></div>
    </div>
</div>

<!-- ════════════ TAB: ENVIRONMENT ════════════ -->
<!-- Casino picker + launch-params form. Opens the deployed game-client URL  -->
<!-- for the chosen casino in a new window. The lobby is embedded inside      -->
<!-- that game-client — same shape as real QA traffic. The launch URL is     -->
<!-- constructed in the popup and dispatched via window.open; no lobby-side  -->
<!-- mutation happens.                                                       -->
<div class="tab-panel" data-panel="environment">
    <h2>Casinos</h2>
    <div class="section">
        <div class="env-casino-grid" id="env-casino-grid"></div>
    </div>

    <h2>Experimentals</h2>
    <div class="section">
        <div class="env-pill-row" id="env-experimental-row"></div>
    </div>

    <h2>Launch parameters</h2>
    <div class="section">
        <div class="bo-edit-field" id="env-field-gameKey">
            <label>Game key <span class="bo-field-hint">(optional)</span></label>
            <input type="text" id="env-gameKey" placeholder="Add game key" autocomplete="off" />
        </div>
        <div class="bo-edit-field" id="env-field-playerId">
            <label>Player id</label>
            <input type="text" id="env-playerId" placeholder="Add player id" autocomplete="off" />
        </div>
        <div class="bo-edit-field" id="env-field-sessionToken">
            <label>Session token <span class="bo-field-hint">(optional)</span></label>
            <input type="text" id="env-sessionToken" placeholder="Add session token" autocomplete="off" />
        </div>
        <div class="row" style="margin-top:8px">
            <button type="button" id="env-launch-btn" class="primary" disabled>Open in new window</button>
        </div>
        <div class="hint" id="env-launch-hint" style="margin-top:8px"></div>
    </div>
</div>

<!-- ════════════ TAB: MEASURE ════════════ -->
<!-- Ruler + guide lines drawn over the lobby for alignment checks.      -->
<!-- The overlay DOM lives in the LOBBY document (measure/measureOverlay); -->
<!-- this tab is the remote control. Positions are always CSS px; the     -->
<!-- unit only changes how the overlay labels distances.                  -->
<div class="tab-panel" data-panel="measure">
    <h2>Overlay</h2>
    <div class="section">
        <div class="row">
            <button type="button" id="ms-overlay" class="ms-switch">Overlay</button>
            <button type="button" id="ms-snap" class="ms-switch">Snap to edges</button>
            <button type="button" id="ms-highlight" class="ms-switch">Highlight aligned</button>
            <button type="button" id="ms-clear-saved" class="danger" title="Delete qa-panel-measure-state from localStorage">Clear saved</button>
        </div>
        <div class="hint" style="margin-top:8px">Highlight aligned: elements with an edge exactly on a guide get a green outline; edges that miss it by up to 3px get a red outline with the offset.</div>
        <div class="ms-label">Unit</div>
        <div class="env-pill-row" id="ms-units"></div>
        <div class="hint" id="ms-unit-hint" style="margin-top:8px"></div>
        <div class="hint" id="ms-reference" style="margin-top:8px" hidden></div>
    </div>

    <h2>Ruler</h2>
    <div class="section">
        <div class="row">
            <button type="button" id="ms-ruler-visible" class="ms-switch">Ruler</button>
            <button type="button" id="ms-ruler-rotate">Rotate</button>
            <button type="button" id="ms-ruler-reset">Reset</button>
        </div>
        <div class="row" style="margin-top:12px;align-items:center">
            <span class="fg-xy-group">
                <span class="fg-xy-cell">
                    <span class="fg-xy-axis">X</span>
                    <input type="number" id="ms-ruler-x" step="1" />
                </span>
                <span class="fg-xy-cell">
                    <span class="fg-xy-axis">Y</span>
                    <input type="number" id="ms-ruler-y" step="1" min="0" />
                </span>
                <span class="fg-xy-cell">
                    <span class="fg-xy-axis">Length</span>
                    <input type="number" id="ms-ruler-length" step="1" min="40" />
                </span>
                <span class="fg-xy-unit">px</span>
            </span>
        </div>
        <div class="hint" style="margin-top:8px">Drag the ruler to move it, the dark grip at its end to resize it. Point anywhere on the lobby to read the distance from the 0 tick. Hold Shift while dragging to skip snapping. Click the ruler, then use the arrow keys to nudge it 1px (Shift = 10px).</div>
    </div>

    <h2>Guides <span class="badge" id="ms-guide-count">0</span></h2>
    <div class="section">
        <div class="row">
            <button type="button" id="ms-add-h">+ Horizontal</button>
            <button type="button" id="ms-add-v">+ Vertical</button>
            <button type="button" id="ms-pick" class="primary">Wrap element…</button>
            <button type="button" id="ms-clear" class="danger">Clear all</button>
        </div>
        <div class="ms-guide-list" id="ms-guide-list"></div>
        <div class="hint" style="margin-top:8px">On the lobby: H adds a horizontal guide under the pointer, V a vertical one. Alt+click an element (or use Wrap element…) to put a blue guide on each of its edges; Alt+click the same spot again to wrap its parent. Click a guide, then arrows nudge it 1px (Shift = 10px), Delete removes it, Esc deselects. Drag a guide off-screen to delete it.</div>
    </div>
</div>

<!-- ════════════ TAB: FIGMA (disabled — re-enable by uncommenting) ════════════ -->
<!--
<div class="tab-panel" data-panel="figma">
    <h2>Figma</h2>
    <div class="section">
        <div class="hint">Copy a frame from Figma (right-click → Copy as PNG) and paste it here. The image will appear as a draggable overlay on the lobby at its native pixel dimensions.</div>
        <div id="fg-drop-zone" class="fg-drop-zone" tabindex="0" data-empty="1" aria-label="Paste Figma image here">
            <div class="fg-empty" id="fg-empty">
                <div class="fg-empty-glyph" aria-hidden="true">paste</div>
                <div>Click here, then paste with Cmd/Ctrl+V</div>
            </div>
            <div class="fg-loaded" id="fg-loaded" hidden>
                <img id="fg-thumb" class="fg-thumb" alt="" />
                <div class="fg-info">
                    <span id="fg-dims" class="fg-dims">— x —</span>
                    <span id="fg-rendered" class="fg-rendered" hidden></span>
                    <div class="row">
                        <button id="fg-clear" type="button" class="danger">Clear</button>
                    </div>
                </div>
            </div>
        </div>
        <div class="fg-status" id="fg-status" role="status"></div>
    </div>

    <h2>Overlay controls</h2>
    <div class="section">
        <div class="hint">Adjust opacity here or directly on the overlay's floating titlebar.</div>
        <div class="fg-opacity-row">
            <label for="fg-opacity">Opacity</label>
            <input type="range" id="fg-opacity" min="0" max="100" step="1" value="50" />
            <span class="fg-opacity-value" id="fg-opacity-value">50%</span>
        </div>
        <div class="row" style="margin-top:12px;align-items:center;flex-wrap:nowrap">
            <label>Position</label>
            <span class="fg-xy-group">
                <span class="fg-xy-cell">
                    <span class="fg-xy-axis">X</span>
                    <input type="number" id="fg-x" step="1" value="0" />
                </span>
                <span class="fg-xy-cell">
                    <span class="fg-xy-axis">Y</span>
                    <input type="number" id="fg-y" step="1" min="0" value="0" />
                </span>
                <span class="fg-xy-unit">px</span>
            </span>
        </div>
        <div class="row" style="margin-top:12px;align-items:center;flex-wrap:nowrap">
            <label>Scale</label>
            <span class="fg-xy-group">
                <span class="fg-xy-cell">
                    <input type="number" id="fg-scale" min="10" max="500" step="1" />
                </span>
                <span class="fg-xy-unit">%</span>
            </span>
        </div>
        <div class="row" style="margin-top:12px">
            <button type="button" id="fg-reset" class="bo-toggle-btn">Reset position and scale</button>
        </div>
    </div>
</div>
-->


<!-- ════════════ TAB: SETTINGS ════════════ -->
<!-- Export / import presets + auto-save to localStorage so QA edits  -->
<!-- survive the 5-7 min session expiry in QA environments.          -->
<div class="tab-panel" data-panel="settings">
    <h2>Presets</h2>
    <div class="section">
        <div class="hint">Export the current QA panel state as a JSON file, or import a previously saved preset to restore it.</div>
        <div class="row" style="margin-bottom:8px">
            <button id="preset-export" type="button" class="primary">Export preset</button>
        </div>
        <div class="row">
            <label class="primary" style="cursor:pointer;display:inline-flex;align-items:center;gap:6px;background:var(--accent);border:none;border-radius:var(--radius-sm);padding:8px 14px;font-family:var(--font-sans);font-size:13px;font-weight:500;color:#ffffff;transition:background 180ms var(--ease)">
                Import preset
                <input type="file" id="preset-import" accept=".json" style="display:none" />
            </label>
        </div>
        <div id="preset-feedback" class="jp-feedback"></div>
    </div>

    <h2>Auto-save</h2>
    <div class="section">
        <div class="hint">When enabled, QA panel edits are automatically saved to localStorage and restored when the panel reopens. Survives session expiry.</div>
        <div class="bo-edit-flags" style="margin-bottom:8px">
            <label><input type="checkbox" id="preset-autosave" /> Enable auto-save</label>
        </div>
        <div id="preset-autosave-status" class="hint-faint"></div>
        <div class="row" style="margin-top:8px">
            <button id="preset-clear" type="button" class="danger">Reset storage</button>
        </div>
    </div>
</div>

<script>
(function () {
    'use strict';

    var NAMESPACE = ${JSON.stringify(MESSAGE_NAMESPACE)};
    var state = ${initialStateJson};

    var connEl = document.getElementById('conn-status');
    var versionEl = document.getElementById('version');
    var currentLangEl = document.getElementById('current-lang');
    var langGridEl = document.getElementById('lang-grid');
    var coverageBodyEl = document.getElementById('coverage-body');
    var missingCountEl = document.getElementById('missing-count');
    var missingListEl = document.getElementById('missing-list');
    var diffPopupEl = document.getElementById('diff-popup');
    var diffKeyEl = document.getElementById('diff-key');
    var diffRowsEl = document.getElementById('diff-rows');
    var modeButtons = document.querySelectorAll('button[data-mode]');
    var inspectorTitleEl = document.getElementById('inspector-title');
    var inspectorLangEl = document.getElementById('inspector-lang');
    var inspectorSectionEl = document.getElementById('inspector-section');
    var inspectorStatsEl = document.getElementById('inspector-stats');
    var inspectorListEl = document.getElementById('inspector-list');
    var inspectorSearchEl = document.getElementById('inspector-search');
    var inspectorFilterButtons = document.querySelectorAll('.inspector .toolbar button[data-filter]');
    var inspectorCloseEl = document.getElementById('inspector-close');

    var inspection = null;       // last received LanguageInspection payload
    var inspectorFilter = 'all'; // 'all' | 'missing' | 'suspect' | 'translated'
    var inspectorQuery = '';     // search box value

    /* ── Jackpot Mock tab ────────────────────────────────────── */
    var jpAutoIntervalEl = document.getElementById('jp-auto-interval');
    var jpAutoPercentEl = document.getElementById('jp-auto-percent');
    var jpAutoToggleEl = document.getElementById('jp-auto-toggle');
    var jpAutoFeedbackEl = document.getElementById('jp-auto-feedback');
    var jpStatusCountEl = document.getElementById('jp-status-count');
    var jpStatusAutoEl = document.getElementById('jp-status-auto');
    var jpValuesHintEl = document.getElementById('jp-values-hint');
    var jpValuesListEl = document.getElementById('jp-values-list');
    var jpNoJackpotBtn = document.getElementById('jp-nojackpot-toggle');
    var jpInstantStatusEl = document.getElementById('jp-instant-status');
    var jpInstantSectionHintEl = document.getElementById('jp-instant-section-hint');
    var jpInstantValueEl = document.getElementById('jp-instant-value');
    var jpInstantInjectBtn = document.getElementById('jp-instant-inject');
    var jpInstantFeedbackEl = document.getElementById('jp-instant-feedback');
    var jpWinStatsStatusEl = document.getElementById('jp-winstats-status');
    var jpWinStatsToggleBtn = document.getElementById('jp-winstats-toggle');
    var jpWinStatsJackpotEl = document.getElementById('jp-winstats-jackpot');
    var jpWinStatsCountEl = document.getElementById('jp-winstats-count');
    var jpWinStatsZeroEl = document.getElementById('jp-winstats-zero');
    var jpWinStatsGenerateBtn = document.getElementById('jp-winstats-generate');
    var jpWinStatsClearBtn = document.getElementById('jp-winstats-clear');
    var jpWinStatsClearAllBtn = document.getElementById('jp-winstats-clear-all');
    var jpWinStatsListEl = document.getElementById('jp-winstats-list');
    var jpWinStatsFeedbackEl = document.getElementById('jp-winstats-feedback');

    var jackpotState = {
        mockEnabled: false,
        autoActive: false,
        autoIntervalSec: 3,
        autoPercent: 2,
        hasData: false,
        jackpotCount: 0,
        jackpots: [],
        instantStatus: 'absent',
        instantSectionConfigured: true,
        instantValue: 0,
        winsMockEnabled: false,
        winsMocks: [],
    };
    var jpFeedbackTimer = null;
    var jpInstantFeedbackTimer = null;
    var jpWinStatsFeedbackTimer = null;

    /* ── Mini Backoffice tab ─────────────────────────────────── */
    var boSectionListEl = document.getElementById('bo-section-list');
    var boResetBtn = document.getElementById('bo-reset-btn');
    var boResetErrorEl = document.getElementById('bo-reset-error');
    var boHotColdMockBtn = document.getElementById('bo-hotcold-mock-toggle');
    var backofficeState = { sections: [], availableGames: [], isResetting: false, resetError: null, openType: 'fullscreen', seasonalBranding: null, noJackpot: false, currency: '', availableCurrencies: [], hotColdMockActive: false, enablePrizePopCampaign: true, enableTournamentsCampaign: true, enableRebateCampaign: false };
    var expandedSections = {};
    var editingSectionName = null;
    var cancelNextBlur = false;

    /* Drag-and-drop state for section / game reorder.
     *
     * isDragging — true between dragstart and dragend on a drag handle.
     *   While true, renderBackoffice() short-circuits to prevent the
     *   DOM from rebuilding under an in-flight gesture.
     * pendingBackofficeRender — set when a render was requested while
     *   suspended. Replayed once on dragend.
     * cancelDragOnEnd — set when a backoffice.state broadcast arrived
     *   mid-drag. dragend will skip dispatching the reorder so we don't
     *   commit a stale order against a refreshed list.
     * dragState — coordinates source/target across the dnd event sequence
     *   without relying on dataTransfer (which is lossy across events).
     */
    var isDragging = false;
    var pendingBackofficeRender = false;
    var cancelDragOnEnd = false;
    var dragState = null;

    // Casino sub-tab
    var boOpenTypeBtn = document.getElementById('bo-casino-opentype');
    var boBrandingSelect = document.getElementById('bo-casino-branding');
    var boCurrencySelect = document.getElementById('bo-casino-currency');
    var boCurrencyHintEl = document.getElementById('bo-casino-currency-hint');
    var boFlagPrizePop = document.getElementById('bo-flag-prizePop');
    var boFlagTournaments = document.getElementById('bo-flag-tournaments');
    var boFlagRebate = document.getElementById('bo-flag-rebate');

    // Sub-tab + Games-tab state
    var boSubtabButtons = document.querySelectorAll('.subtab-nav button[data-subtab]');
    var boSubtabPanels = document.querySelectorAll('.subtab-panel[data-subpanel]');
    var boGameSearchEl = document.getElementById('bo-game-search');
    var boGamePickerEl = document.getElementById('bo-game-picker');
    var boEditTitleEl = document.getElementById('bo-edit-title');
    var boEditTitleTextEl = document.getElementById('bo-edit-title-text');
    var boEditFormEl = document.getElementById('bo-edit-form');
    var boEditActivatedOnEl = document.getElementById('bo-edit-activatedOn');
    var boEditAddedToLobbyOnEl = document.getElementById('bo-edit-addedToLobbyOn');
    var boEditNewGameStateEl = document.getElementById('bo-edit-newgame-state');
    var boEditFeaturesEl = document.getElementById('bo-edit-features');
    var boEditFeaturesInputEl = document.getElementById('bo-edit-features-input');
    var boEditFeaturesAddEl = document.getElementById('bo-edit-features-add');
    var boEditTopGameEl = document.getElementById('bo-edit-topGame');
    var boEditIsInTournamentEl = document.getElementById('bo-edit-isInTournament');
    var boEditJackpotNameEl = document.getElementById('bo-edit-jackpotName');
    var boEditDisplayJackpotNameEl = document.getElementById('bo-edit-displayJackpotName');
    var boEditCurrentPlayersEl = document.getElementById('bo-edit-currentPlayers');
    var boEditCurrentPlayersToggleEl = document.getElementById('bo-edit-currentPlayers-toggle');
    var boEditMinBetEl = document.getElementById('bo-edit-minBet');
    var boEditMaxBetEl = document.getElementById('bo-edit-maxBet');
    var boEditBetLabelEl = document.getElementById('bo-edit-bet-label');
    var boEditBetHintEl = document.getElementById('bo-edit-bet-hint');
    var boThumbCheckBtnEl = document.getElementById('bo-thumb-check-btn');
    var boThumbStatusEl = document.getElementById('bo-thumb-status');
    var boThumbResultsEl = document.getElementById('bo-thumb-results');
    var selectedGameKey = null;
    var gameSearchQuery = '';

    /* ── Sentry tab ────────────────────────────────────────── */
    var snEventCountEl = document.getElementById('sn-event-count');
    var snEventOrderEl = document.getElementById('sn-event-order');
    var snNavTabsEl = document.getElementById('sn-nav-tabs');
    var snGameLaunchesEl = document.getElementById('sn-game-launches');
    var snJackpotStatsOpensEl = document.getElementById('sn-jackpot-stats-opens');
    var snOtherClicksEl = document.getElementById('sn-other-clicks');
    var snUserDataEl = document.getElementById('sn-user-data');
    var snTransactionEl = document.getElementById('sn-transaction');
    var snSectionNavigationEl = document.getElementById('sn-section-navigation');
    var snSectionNavHistoryEl = document.getElementById('sn-section-nav-history');
    var snDynamicSectionsEl = document.getElementById('sn-dynamic-sections');
    var snSearchQueriesEl = document.getElementById('sn-search-queries');
    var snTraceIdEl = document.getElementById('sn-trace-id');
    var snTotalEl = document.getElementById('sn-total');
    var snFooterEl = document.getElementById('sn-footer');
    var snSyncBtn = document.getElementById('sn-sync-btn');
    var snClearBtn = document.getElementById('sn-clear-btn');

    var sentryState = {
        eventOrder: [],
        navTabs: {},
        jackpotStatsOpens: {},
        gameLaunches: [],
        otherClicks: {},
        total: 0,
        userData: {},
        transaction: {},
        sectionNavigation: {},
        dynamicSections: {},
        searchQueries: [],
        activeSpanTraceId: null,
        lastUpdate: 0,
    };

    /* ── Jira tab ──────────────────────────────────────────── */
    var JIRA_BASE_URL = 'https://jira.egt-digital.com/browse/';
    /* JQL search endpoint — used to open every build ticket at once
     * (issuekey in (...)) so reviewers see ticket names, not just keys. */
    var JIRA_SEARCH_BASE_URL = 'https://jira.egt-digital.com/issues/?jql=';
    /* Bitbucket Server: repos and PRs both live under the project+repo path.
     * Source: scripts/lobby-pipe.js (same host config QA uses for PR creation). */
    var BITBUCKET_BASE_URL = 'https://bitbucket.egt-digital.com/projects/OG/repos/react-lobby';
    /* Teams 1:1 deep-link. The "users" param accepts a comma-separated list
     * of UPNs; for our purposes it's the git author email (Teams resolves
     * UPN === primary SMTP address for these accounts). */
    var TEAMS_CHAT_BASE_URL = 'https://teams.microsoft.com/l/chat/0/0?users=';
    /* Only build a Teams link when the author email lives in a corporate
     * domain known to map to a Teams UPN. Unknown domains fall back to plain
     * text — safer than handing QA a dead link. */
    var TEAMS_ALLOWED_DOMAINS = ['egt-digital.com'];
    /* localStorage shape:
     *   { "<previousVersion>": ["ON-123", "ON-456"], ... }
     * Keyed by previousVersion so a new rc starts fresh while older
     * verifications linger for cross-rc spot-checks. Capped at the 5 most
     * recently touched versions to keep the entry bounded. */
    var JI_VERIFIED_LS_KEY = 'qa-panel-jira-verified';
    var JI_VERIFIED_MAX_VERSIONS = 5;
    var jiCountEl = document.getElementById('ji-count');
    var jiRangeHintEl = document.getElementById('ji-range-hint');
    var jiListEl = document.getElementById('ji-list');
    var jiAreasEl = document.getElementById('ji-areas');
    var jiVerifiedLineEl = document.getElementById('ji-verified-line');
    var jiAllLinkEl = document.getElementById('ji-all-link');
    var jiraState = { previousVersion: null, tickets: [], fileAreas: [] };
    /* Per-row expand state. Persists across renderJiraTab() re-runs so
     * a snapshot re-broadcast doesn't collapse rows the user just opened. */
    var jiExpandedTickets = Object.create(null);
    /* Per-row verified state, scoped to jiraState.previousVersion. Read once
     * here and lazily synced to localStorage on every toggle. Separate from
     * jiExpandedTickets so the two concerns don't interact. */
    var jiVerifiedTickets = Object.create(null);
    var jiVerifiedLoadedForVersion = null;

    function jiReadVerifiedStore() {
        try {
            var raw = localStorage.getItem(JI_VERIFIED_LS_KEY);
            if (!raw) return {};
            var parsed = JSON.parse(raw);
            return (parsed && typeof parsed === 'object') ? parsed : {};
        } catch (e) {
            return {};
        }
    }

    function jiWriteVerifiedStore(store) {
        try {
            localStorage.setItem(JI_VERIFIED_LS_KEY, JSON.stringify(store));
        } catch (e) {
            /* localStorage may be unavailable in sandboxed contexts */
        }
    }

    function jiLoadVerifiedForVersion(version) {
        jiVerifiedTickets = Object.create(null);
        if (!version) {
            jiVerifiedLoadedForVersion = null;
            return;
        }
        var store = jiReadVerifiedStore();
        var list = store[version];
        if (Array.isArray(list)) {
            for (var i = 0; i < list.length; i++) {
                if (typeof list[i] === 'string') jiVerifiedTickets[list[i]] = true;
            }
        }
        jiVerifiedLoadedForVersion = version;
    }

    function jiPersistVerifiedForVersion(version) {
        if (!version) return;
        var store = jiReadVerifiedStore();
        var keys = [];
        for (var k in jiVerifiedTickets) {
            if (jiVerifiedTickets[k]) keys.push(k);
        }
        if (keys.length === 0) {
            delete store[version];
        } else {
            store[version] = keys;
        }
        /* Cap to most-recent N versions. We don't track touch-time, so just
         * preserve the current version and drop excess oldest-by-insertion-
         * order entries — Object key iteration order is insertion order in
         * every modern engine, and JSON.parse preserves it. */
        var allVersions = Object.keys(store);
        if (allVersions.length > JI_VERIFIED_MAX_VERSIONS) {
            /* Keep the current version explicitly; trim the front of the list. */
            var trimmed = {};
            var keepCount = JI_VERIFIED_MAX_VERSIONS;
            var preserved = 0;
            /* Walk from newest (end) to oldest (front), inserting in reverse. */
            for (var i = allVersions.length - 1; i >= 0 && preserved < keepCount; i--) {
                var v = allVersions[i];
                trimmed[v] = store[v];
                preserved++;
            }
            store = trimmed;
        }
        jiWriteVerifiedStore(store);
    }


    function lobbyWindow() {
        /* Popup container: opener is the lobby. Iframe container: opener is */
        /* null, so fall back to parent. Guard the unframed case so we never */
        /* post back to ourselves. */
        return window.opener || window.parent;
    }

    function send(message) {
        var target = lobbyWindow();
        if (!target || target === window || target.closed) return;
        target.postMessage(
            Object.assign({ namespace: NAMESPACE }, message),
            '*'
        );
    }

    function setConnected(ok) {
        connEl.textContent = ok ? 'connected' : 'disconnected';
        connEl.className = 'conn ' + (ok ? 'ok' : 'off');
    }

    function renderModes() {
        modeButtons.forEach(function (btn) {
            if (btn.getAttribute('data-mode') === state.mode) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });
    }

    function renderLanguages() {
        currentLangEl.textContent = (state.currentLang || '—').toUpperCase();
        langGridEl.innerHTML = '';
        (state.supportedLanguages || []).forEach(function (lang) {
            var btn = document.createElement('button');
            btn.textContent = lang.toUpperCase();
            if (lang.toLowerCase() === (state.currentLang || '').toLowerCase()) {
                btn.classList.add('active');
            }
            btn.addEventListener('click', function () {
                send({ type: 'switchLanguage', payload: { lang: lang } });
            });
            langGridEl.appendChild(btn);
        });
    }

    function renderCoverage() {
        coverageBodyEl.innerHTML = '';
        var entries = Object.values(state.coverage || {});
        if (entries.length === 0) {
            var tr = document.createElement('tr');
            tr.innerHTML = '<td colspan="4" class="empty">No languages loaded yet — switch a language or click "Preload all".</td>';
            coverageBodyEl.appendChild(tr);
            return;
        }
        entries.sort(function (a, b) {
            return (a.lang || '').localeCompare(b.lang || '');
        });
        var selected = inspection ? inspection.lang : null;
        entries.forEach(function (cov) {
            var pct = cov.total > 0 ? Math.round((cov.present / cov.total) * 100) : 0;
            var pctClass = pct === 100 ? 'full' : pct >= 90 ? 'partial' : 'bad';
            var tr = document.createElement('tr');
            if (selected && cov.lang.toUpperCase() === selected.toUpperCase()) {
                tr.className = 'selected';
            }
            tr.innerHTML =
                '<td class="lang">' + cov.lang.toUpperCase() + '</td>' +
                '<td>' + cov.present + ' / ' + cov.total + '</td>' +
                '<td>' + (cov.total - cov.present) + '</td>' +
                '<td class="pct ' + pctClass + '">' + pct + '%</td>';
            tr.addEventListener('click', function () {
                send({ type: 'inspectLanguage', payload: { lang: cov.lang } });
            });
            coverageBodyEl.appendChild(tr);
        });
    }

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    function statusLabel(status) {
        if (status === 'missing') return 'MISSING';
        if (status === 'empty') return 'EMPTY';
        if (status === 'keyAsValue') return 'KEY AS VALUE';
        if (status === 'sameAsRef') return 'SAME AS REF';
        return 'TRANSLATED';
    }

    function passesFilter(item) {
        if (inspectorFilter === 'all') return true;
        if (inspectorFilter === 'missing') return item.status === 'missing';
        if (inspectorFilter === 'translated') return item.status === 'translated';
        if (inspectorFilter === 'suspect') {
            return item.status === 'empty' || item.status === 'keyAsValue' || item.status === 'sameAsRef';
        }
        return true;
    }

    function renderInspector() {
        if (!inspection) {
            inspectorTitleEl.style.display = 'none';
            inspectorSectionEl.style.display = 'none';
            return;
        }
        inspectorTitleEl.style.display = '';
        inspectorSectionEl.style.display = '';
        inspectorLangEl.textContent = inspection.lang + '  vs  ' + inspection.referenceLang;

        var counts = inspection.counts || {};
        inspectorStatsEl.innerHTML =
            '<span class="missing">missing ' + (counts.missing || 0) + '</span>' +
            '<span class="empty">empty ' + (counts.empty || 0) + '</span>' +
            '<span class="keyAsValue">key-as-value ' + (counts.keyAsValue || 0) + '</span>' +
            '<span class="sameAsRef">same-as-ref ' + (counts.sameAsRef || 0) + '</span>' +
            '<span class="translated">translated ' + (counts.translated || 0) + '</span>';

        var query = inspectorQuery.toLowerCase();
        var filtered = (inspection.items || []).filter(function (item) {
            if (!passesFilter(item)) return false;
            if (query && item.key.toLowerCase().indexOf(query) === -1) return false;
            return true;
        });

        var MAX = 200;
        var truncated = filtered.length > MAX;
        var slice = truncated ? filtered.slice(0, MAX) : filtered;

        if (slice.length === 0) {
            inspectorListEl.innerHTML = '<div class="empty">No keys match the current filter.</div>';
            return;
        }

        var html = '';
        slice.forEach(function (item) {
            var refVal = item.refValue == null ? '(absent)' : item.refValue;
            var langVal = item.langValue == null ? '(absent)' : item.langValue;
            var refClass = item.refValue == null ? ' absent' : '';
            var langClass = item.langValue == null ? ' absent' : '';
            html +=
                '<div class="item ' + item.status + '">' +
                  '<div class="head">' +
                    '<span class="dot"></span>' +
                    '<span class="key">' + escapeHtml(item.key) + '</span>' +
                    '<span class="status-label">' + statusLabel(item.status) + '</span>' +
                  '</div>' +
                  '<div class="vals">' +
                    '<div class="row"><span class="lng">' + escapeHtml(inspection.referenceLang) + '</span><span class="val' + refClass + '">' + escapeHtml(refVal) + '</span></div>' +
                    '<div class="row"><span class="lng">' + escapeHtml(inspection.lang) + '</span><span class="val' + langClass + '">' + escapeHtml(langVal) + '</span></div>' +
                  '</div>' +
                '</div>';
        });
        if (truncated) {
            html += '<div class="empty">+ ' + (filtered.length - MAX) + ' more — narrow filter or use search</div>';
        }
        inspectorListEl.innerHTML = html;
    }

    function closeInspector() {
        inspection = null;
        inspectorFilter = 'all';
        inspectorQuery = '';
        if (inspectorSearchEl) inspectorSearchEl.value = '';
        inspectorFilterButtons.forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-filter') === 'all');
        });
        renderInspector();
        renderCoverage(); // clear selected highlight
    }

    function renderMissing() {
        var records = state.missingKeys || [];
        missingCountEl.textContent = String(records.length);
        missingListEl.innerHTML = '';
        if (records.length === 0) {
            var empty = document.createElement('div');
            empty.className = 'empty';
            empty.textContent = 'No missing keys observed yet. Navigate the lobby to populate.';
            missingListEl.appendChild(empty);
            return;
        }
        records.slice(0, 200).forEach(function (rec) {
            var item = document.createElement('div');
            item.className = 'missing-item';
            item.innerHTML =
                '<span class="lang-tag">' + rec.lang.toUpperCase() + '</span>' +
                '<span class="key"></span>' +
                '<span class="count">×' + rec.count + '</span>';
            item.querySelector('.key').textContent = rec.key;
            item.addEventListener('click', function () { showDiff(rec.key); });
            missingListEl.appendChild(item);
        });
        if (records.length > 200) {
            var more = document.createElement('div');
            more.className = 'empty';
            more.textContent = '+ ' + (records.length - 200) + ' more — export CSV for full list';
            missingListEl.appendChild(more);
        }
    }

    function showDiff(key) {
        diffKeyEl.textContent = key;
        diffRowsEl.innerHTML = '';
        var coverage = state.coverage || {};
        var langs = Object.keys(coverage).sort();
        if (langs.length === 0) {
            diffRowsEl.innerHTML = '<div class="empty">Preload languages first to see the diff.</div>';
        } else {
            langs.forEach(function (lang) {
                var present = coverage[lang].missingKeys.indexOf(key) === -1;
                var row = document.createElement('div');
                row.className = 'diff-row';
                row.innerHTML =
                    '<span class="lng">' + lang.toUpperCase() + '</span>' +
                    '<span class="val ' + (present ? '' : 'missing') + '">' +
                        (present ? '(present)' : '(missing)') +
                    '</span>';
                diffRowsEl.appendChild(row);
            });
        }
        diffPopupEl.classList.add('open');
    }

    function applyState(next) {
        state = Object.assign({}, state, next);
        versionEl.textContent = state.appVersion ? 'v' + state.appVersion : '';
        renderModes();
        renderLanguages();
        renderCoverage();
        renderMissing();
        renderInspector();
        setConnected(true);
    }

    /* ── Jackpot mock rendering ─────────────────────────────── */

    function renderJackpotMock() {
        if (!jpAutoToggleEl) return;
        var s = jackpotState;

        // Auto toggle: disabled when no jackpots loaded yet (nothing to mock)
        var canStartAuto = s.hasData && s.jackpotCount > 0;
        jpAutoToggleEl.textContent = s.autoActive ? 'Stop auto' : 'Start auto';
        jpAutoToggleEl.classList.toggle('on', s.autoActive);
        // Only disable when not currently running — running auto can always be stopped.
        jpAutoToggleEl.disabled = !s.autoActive && !canStartAuto;
        jpAutoToggleEl.style.opacity = jpAutoToggleEl.disabled ? '0.4' : '1';
        jpAutoToggleEl.style.cursor = jpAutoToggleEl.disabled ? 'not-allowed' : 'pointer';
        jpAutoIntervalEl.disabled = s.autoActive;
        jpAutoPercentEl.disabled = s.autoActive;

        // Status panel
        jpStatusCountEl.textContent = s.hasData ? String(s.jackpotCount) : 'no data yet';
        jpStatusAutoEl.textContent = s.autoActive
            ? 'every ' + s.autoIntervalSec + 's @ ' + (s.autoPercent >= 0 ? '+' : '') + s.autoPercent + '%'
            : 'stopped';
        jpStatusAutoEl.className = 'jp-status-value' + (s.autoActive ? ' warn' : '');

        // Jackpot / Bonus toggle — reads backofficeState.noJackpot (populated by
        // the same controller snapshot that drives the Mini Backoffice).
        if (jpNoJackpotBtn) {
            var noJackpotOn = !!backofficeState.noJackpot;
            jpNoJackpotBtn.textContent = noJackpotOn ? 'Bonus (noJackpot)' : 'Jackpot (default)';
            jpNoJackpotBtn.classList.toggle('on', noJackpotOn);
        }

        // Instant jackpot block
        if (jpInstantStatusEl) {
            var instantText = 'not present on this env';
            if (s.instantStatus === 'live') instantText = 'live from API';
            else if (s.instantStatus === 'mocked') instantText = 'mocked';
            jpInstantStatusEl.textContent = instantText;
            jpInstantStatusEl.className = 'jp-status-value' + (s.instantStatus === 'mocked' ? ' warn' : '');
        }
        if (jpInstantSectionHintEl) {
            jpInstantSectionHintEl.style.display = s.instantSectionConfigured ? 'none' : '';
        }
        if (jpInstantInjectBtn) {
            jpInstantInjectBtn.textContent = s.instantStatus === 'absent' ? 'Inject' : 'Update';
        }
        // Mirror the live value only when the instance exists (Update
        // semantics), and never while QA is typing (focus guard). When the
        // instance is absent the input is QA's draft — leave it alone.
        if (jpInstantValueEl && s.instantStatus !== 'absent' && document.activeElement !== jpInstantValueEl) {
            jpInstantValueEl.value = String(s.instantValue);
        }

        renderWinStatsMock();
        renderJackpotValues();
    }

    /* ── Statistics mock (win statistics drawer) ─────────────── */

    function renderWinStatsMock() {
        if (!jpWinStatsToggleBtn) return;
        var s = jackpotState;
        var mocks = s.winsMocks || [];

        if (jpWinStatsStatusEl) {
            jpWinStatsStatusEl.textContent = s.winsMockEnabled ? 'mocked' : 'live from API';
            jpWinStatsStatusEl.className = 'jp-status-value' + (s.winsMockEnabled ? ' warn' : '');
        }
        jpWinStatsToggleBtn.textContent = s.winsMockEnabled ? 'Mocking on' : 'Mocking off';
        jpWinStatsToggleBtn.classList.toggle('on', !!s.winsMockEnabled);

        // Jackpot picker — same source as the per-level value editor below. Rebuilt on every
        // broadcast (jackpots arrive asynchronously), preserving the current selection so a
        // mid-edit render does not silently retarget the Generate button.
        if (jpWinStatsJackpotEl && document.activeElement !== jpWinStatsJackpotEl) {
            var previous = jpWinStatsJackpotEl.value;
            // Only jackpots that HAVE a statistics drawer. The single-progressive ones (Burning
            // Hot, Rise of Ra, ...) render a banner panel with no level rows, so nothing could
            // ever open a drawer to show generated wins for them.
            var jackpots = (s.jackpots || []).filter(function (jp) { return jp.hasStatistics; });
            var optionsHtml = '';
            jackpots.forEach(function (jp) {
                optionsHtml += '<option value="' + escapeHtml(jp.name) + '">' + escapeHtml(jp.name) + '</option>';
            });
            if (jackpots.length === 0) {
                optionsHtml = '<option value="">no jackpots with statistics loaded yet</option>';
            }
            jpWinStatsJackpotEl.innerHTML = optionsHtml;
            for (var i = 0; i < jpWinStatsJackpotEl.options.length; i++) {
                if (jpWinStatsJackpotEl.options[i].value === previous) {
                    jpWinStatsJackpotEl.selectedIndex = i;
                    break;
                }
            }
        }

        var hasJackpots = (s.jackpots || []).length > 0;
        if (jpWinStatsJackpotEl) jpWinStatsJackpotEl.disabled = !hasJackpots;
        if (jpWinStatsGenerateBtn) jpWinStatsGenerateBtn.disabled = !hasJackpots;
        if (jpWinStatsClearBtn) jpWinStatsClearBtn.disabled = mocks.length === 0;
        if (jpWinStatsClearAllBtn) jpWinStatsClearAllBtn.disabled = mocks.length === 0;

        if (jpWinStatsListEl) {
            if (mocks.length === 0) {
                jpWinStatsListEl.innerHTML = '';
            } else {
                var listHtml = '';
                mocks.forEach(function (entry) {
                    var meta = entry.winsPerLevel + ' wins/level';
                    if (entry.includeZeroWinLevel) meta += ' + a zero-win level';
                    listHtml += '<div class="jp-winstats-item">';
                    listHtml += '<span>' + escapeHtml(entry.jackpotName) + '</span>';
                    listHtml += '<span class="jp-winstats-item-meta">' + escapeHtml(meta) + '</span>';
                    listHtml += '</div>';
                });
                jpWinStatsListEl.innerHTML = listHtml;
            }
        }
    }

    function showJpWinStatsFeedback(msg, kind) {
        if (!jpWinStatsFeedbackEl) return;
        if (jpWinStatsFeedbackTimer) clearTimeout(jpWinStatsFeedbackTimer);
        jpWinStatsFeedbackEl.textContent = msg;
        jpWinStatsFeedbackEl.className = 'jp-feedback show ' + kind;
        jpWinStatsFeedbackTimer = setTimeout(function () {
            jpWinStatsFeedbackEl.className = 'jp-feedback';
        }, 2400);
    }

    if (jpWinStatsToggleBtn) {
        jpWinStatsToggleBtn.addEventListener('click', function () {
            var next = !jackpotState.winsMockEnabled;
            send({ type: 'jackpot.setWinsMockMode', payload: { enabled: next } });
            showJpWinStatsFeedback(
                next
                    ? 'mocking on — generate for a jackpot to populate its drawer'
                    : 'mocking off — generated data cleared, real request restored',
                'ok'
            );
        });
    }

    if (jpWinStatsGenerateBtn) {
        jpWinStatsGenerateBtn.addEventListener('click', function () {
            var jackpotName = jpWinStatsJackpotEl ? jpWinStatsJackpotEl.value : '';
            if (!jackpotName) {
                showJpWinStatsFeedback('pick a jackpot first', 'err');
                return;
            }
            var parsed = parseInt(jpWinStatsCountEl.value, 10);
            if (isNaN(parsed) || parsed < 1) {
                showJpWinStatsFeedback('wins per level must be at least 1', 'err');
                return;
            }
            send({
                type: 'jackpot.generateWinStats',
                payload: {
                    jackpotName: jackpotName,
                    winsPerLevel: parsed,
                    includeZeroWinLevel: !!(jpWinStatsZeroEl && jpWinStatsZeroEl.checked),
                },
            });
            // The result toast arrives with the lobby's 'jackpot.winStatsGenerated' reply.
            showJpWinStatsFeedback('generating for ' + jackpotName + '…', 'ok');
        });
    }

    if (jpWinStatsClearBtn) {
        jpWinStatsClearBtn.addEventListener('click', function () {
            var jackpotName = jpWinStatsJackpotEl ? jpWinStatsJackpotEl.value : '';
            if (!jackpotName) {
                showJpWinStatsFeedback('pick a jackpot first', 'err');
                return;
            }
            send({ type: 'jackpot.clearWinStats', payload: { jackpotName: jackpotName } });
            showJpWinStatsFeedback('cleared ' + jackpotName, 'ok');
        });
    }

    if (jpWinStatsClearAllBtn) {
        jpWinStatsClearAllBtn.addEventListener('click', function () {
            send({ type: 'jackpot.clearWinStats', payload: { jackpotName: null } });
            showJpWinStatsFeedback('cleared every generated jackpot', 'ok');
        });
    }

    function renderJackpotValues() {
        if (!jpValuesListEl || !jpValuesHintEl) return;
        var s = jackpotState;

        jpValuesHintEl.textContent = s.mockEnabled
            ? 'Mock mode is on — edits persist until you toggle it off or reset.'
            : 'Editing a value will automatically enable mock mode (stops polling).';

        // Focus guard: if QA is mid-keystroke, DON'T clobber the input they're
        // typing into. Other parts of the state broadcast still update (hint,
        // status panel, etc.) — only the list itself is skipped.
        if (document.activeElement && jpValuesListEl.contains(document.activeElement)) {
            return;
        }

        var jackpots = s.jackpots || [];
        if (jackpots.length === 0) {
            jpValuesListEl.innerHTML = '<div class="empty">No jackpots loaded yet — open the lobby and wait for the initial fetch.</div>';
            return;
        }

        var html = '';
        jackpots.forEach(function (jp) {
            html += '<div class="jp-values-card">';
            html += '<div class="jp-values-card-head">';
            html += '<span class="jp-values-name">' + escapeHtml(jp.name) + '</span>';
            html += '<span class="jp-values-currency">' + escapeHtml(jp.currency || '') + '</span>';
            html += '</div>';
            (jp.levels || []).forEach(function (lvl) {
                html += '<div class="jp-values-row">';
                html += '<span class="jp-values-level">' + escapeHtml(lvl.levelName) + '</span>';
                html += '<input type="number" min="0" step="1"';
                html += ' data-jackpot-name="' + escapeHtml(jp.name) + '"';
                html += ' data-level-id="' + String(lvl.levelId) + '"';
                html += ' value="' + String(lvl.value) + '" />';
                html += '</div>';
            });
            html += '</div>';
        });
        jpValuesListEl.innerHTML = html;
    }

    function showJpFeedback(msg, kind) {
        if (jpFeedbackTimer) clearTimeout(jpFeedbackTimer);
        jpAutoFeedbackEl.textContent = msg;
        jpAutoFeedbackEl.className = 'jp-feedback show ' + kind;
        jpFeedbackTimer = setTimeout(function () {
            jpAutoFeedbackEl.className = 'jp-feedback';
        }, 2400);
    }

    if (jpAutoToggleEl) {
        jpAutoToggleEl.addEventListener('click', function () {
            if (jackpotState.autoActive) {
                send({ type: 'jackpot.stopAuto' });
                return;
            }
            var intervalSec = parseFloat(jpAutoIntervalEl.value);
            var percent = parseFloat(jpAutoPercentEl.value);
            if (isNaN(intervalSec) || intervalSec < 0.5) {
                showJpFeedback('interval must be at least 0.5s', 'err');
                return;
            }
            if (isNaN(percent)) {
                showJpFeedback('enter a valid percentage', 'err');
                return;
            }
            send({ type: 'jackpot.startAuto', payload: { intervalSec: intervalSec, percent: percent } });
        });
    }

    if (jpNoJackpotBtn) {
        jpNoJackpotBtn.addEventListener('click', function () {
            var next = !backofficeState.noJackpot;
            send({ type: 'backoffice.setNoJackpot', payload: { noJackpot: next } });
        });
    }

    function showJpInstantFeedback(msg, kind) {
        if (jpInstantFeedbackTimer) clearTimeout(jpInstantFeedbackTimer);
        jpInstantFeedbackEl.textContent = msg;
        jpInstantFeedbackEl.className = 'jp-feedback show ' + kind;
        jpInstantFeedbackTimer = setTimeout(function () {
            jpInstantFeedbackEl.className = 'jp-feedback';
        }, 2400);
    }

    if (jpInstantInjectBtn) {
        jpInstantInjectBtn.addEventListener('click', function () {
            var parsed = parseFloat(jpInstantValueEl.value);
            if (isNaN(parsed) || parsed < 0) {
                showJpInstantFeedback('enter a non-negative value', 'err');
                return;
            }
            var wasAbsent = jackpotState.instantStatus === 'absent';
            send({ type: 'jackpot.injectInstant', payload: { value: parsed } });
            showJpInstantFeedback(wasAbsent ? 'injected — mock mode is now ON' : 'value updated', 'ok');
        });
    }

    // Event delegation on the values list so the listener survives full
    // re-renders. Each input carries its jackpot name + level id as data
    // attributes; the handler parses, clamps negatives to 0, and forwards.
    if (jpValuesListEl) {
        jpValuesListEl.addEventListener('input', function (e) {
            var target = e.target;
            if (!target || target.tagName !== 'INPUT') return;
            var jackpotName = target.getAttribute('data-jackpot-name');
            var levelIdAttr = target.getAttribute('data-level-id');
            if (!jackpotName || !levelIdAttr) return;
            var levelId = parseInt(levelIdAttr, 10);
            if (isNaN(levelId)) return;
            var parsed = parseFloat(target.value);
            var value = isNaN(parsed) || parsed < 0 ? 0 : parsed;
            send({
                type: 'jackpot.setLevelValue',
                payload: { jackpotName: jackpotName, levelId: levelId, value: value },
            });
        });
    }

    // Ask the lobby for an initial jackpot mock state on popup boot.
    send({ type: 'jackpot.requestState' });

    /* ── Mini Backoffice rendering ──────────────────────────── */

    function renderBackoffice() {
        if (!boSectionListEl) return;
        // Suspend re-renders while a drag is in flight — rebuilding the list
        // mid-gesture would lose the source element and the indicator state.
        // The pending flag replays one render on dragend; the cancel flag
        // tells dragend to skip dispatching the user's reorder against the
        // refreshed list (no silent commits with stale indices).
        if (isDragging) {
            pendingBackofficeRender = true;
            cancelDragOnEnd = true;
            return;
        }
        var s = backofficeState;

        if (boResetErrorEl) {
            if (s.resetError) {
                boResetErrorEl.textContent = 'Reset failed: ' + s.resetError;
                boResetErrorEl.style.display = 'block';
            } else {
                boResetErrorEl.style.display = 'none';
            }
        }
        if (boResetBtn) {
            boResetBtn.textContent = s.isResetting ? 'Resetting…' : 'Reset to API';
            boResetBtn.disabled = !!s.isResetting;
            boResetBtn.style.opacity = s.isResetting ? '0.5' : '1';
        }
        if (boHotColdMockBtn) {
            var hotColdOn = !!s.hotColdMockActive;
            boHotColdMockBtn.textContent = hotColdOn ? 'Remove HOT_AND_COLD mock' : 'Inject HOT_AND_COLD mock';
            boHotColdMockBtn.classList.toggle('on', hotColdOn);
        }

        if (!s.sections || s.sections.length === 0) {
            boSectionListEl.innerHTML = '<div class="empty">No sections loaded yet — open the lobby and wait for the initial fetch.</div>';
            return;
        }

        var html = '';
        s.sections.forEach(function (section) {
            var expanded = !!expandedSections[section.name];
            var inSectionKeys = {};
            section.games.forEach(function (g) { inSectionKeys[g.gameKey] = true; });
            var availableForAdd = (s.availableGames || []).filter(function (g) {
                return !inSectionKeys[g.gameKey];
            });
            var willHide = section.games.length === 1;

            html += '<div class="bo-section' + (section.isLocked ? ' locked' : '') + '" data-section-name="' + escapeHtml(section.name) + '">';

            // Header
            html += '<div class="bo-section-header">';
            // Drag handle — only rendered when there's more than one section
            // to reorder. Single-section lists hide the affordance entirely.
            if (s.sections.length > 1) {
                html += '<span class="bo-drag-handle" draggable="true" data-drag-type="section" title="Drag to reorder">⋮⋮</span>';
            }
            html += '<button type="button" class="bo-collapse" title="Toggle">' + (expanded ? '▾' : '▸') + '</button>';
            if (section.isLocked) {
                html += '<span class="bo-name-locked" title="' + escapeHtml(section.name) + '">' + escapeHtml(section.name) + '</span>';
            } else if (editingSectionName === section.name) {
                html += '<input type="text" class="bo-name-input" value="' + escapeHtml(section.name) + '" autocomplete="off" />';
            } else {
                html += '<div class="bo-name-wrap">';
                html += '<span class="bo-name-display" title="' + escapeHtml(section.name) + '">' + escapeHtml(section.name) + '</span>';
                html += '<button type="button" class="bo-name-edit-btn" title="Rename section">✎</button>';
                html += '</div>';
            }
            html += '<span class="bo-game-count">' + section.games.length + ' game' + (section.games.length === 1 ? '' : 's') + '</span>';
            if (section.isLocked) {
                html += '<span class="bo-locked-badge" title="' + escapeHtml(section.lockReason || 'Locked') + '">LOCKED</span>';
            }
            html += '<div class="bo-section-actions">';
            html += '<button type="button" class="bo-section-delete" title="Delete section"' + (section.isLocked ? ' disabled' : '') + '>✕</button>';
            html += '</div>';
            html += '</div>';

            // Body
            if (expanded) {
                html += '<div class="bo-section-body">';

                if (section.games.length > 0) {
                    html += '<div class="bo-game-list">';
                    section.games.forEach(function (game) {
                        html += '<div class="bo-game-row" data-game-key="' + escapeHtml(game.gameKey) + '">';
                        // Drag handle — only when there's more than one game
                        // in the section to reorder.
                        if (section.games.length > 1) {
                            html += '<span class="bo-drag-handle" draggable="true" data-drag-type="game" title="Drag to reorder">⋮⋮</span>';
                        }
                        html += '<span class="bo-game-name" title="' + escapeHtml(game.name) + '">' + escapeHtml(game.name) + '</span>';
                        html += '<span class="bo-game-key">' + escapeHtml(game.gameKey) + '</span>';
                        html += '<button type="button" class="bo-game-remove" title="Remove from section">✕</button>';
                        html += '</div>';
                    });
                    html += '</div>';
                } else {
                    html += '<div class="empty" style="padding:12px">No games in this section.</div>';
                }

                if (willHide) {
                    html += '<div class="bo-warning">Removing the last game will hide this section from the lobby (the store filters out empty sections).</div>';
                }

                if (availableForAdd.length > 0) {
                    html += '<div class="bo-add-row">';
                    html += '<select class="bo-add-select"><option value="">+ Add game…</option>';
                    availableForAdd.forEach(function (g) {
                        html += '<option value="' + escapeHtml(g.gameKey) + '">' + escapeHtml(g.name) + '  ·  ' + escapeHtml(g.gameKey) + '</option>';
                    });
                    html += '</select>';
                    html += '</div>';
                }

                html += '</div>';
            }

            html += '</div>';
        });

        boSectionListEl.innerHTML = html;
    }

    function toggleSection(sectionName) {
        if (expandedSections[sectionName]) {
            delete expandedSections[sectionName];
        } else {
            expandedSections[sectionName] = true;
        }
        renderBackoffice();
    }

    function handleBackofficeClick(e) {
        var target = e.target;
        // Don't ever treat input/select clicks as a section-toggle gesture —
        // those have their own focus/typing/dropdown semantics.
        var tag = target.tagName;
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'OPTION') return;
        // Drag handles are draggable spans; a click on them must not bubble
        // up into the section-header collapse toggle.
        if (target.classList && target.classList.contains('bo-drag-handle')) return;

        var sectionEl = target.closest && target.closest('.bo-section');
        if (!sectionEl) return;
        var sectionName = sectionEl.getAttribute('data-section-name');

        var btn = target.closest('button');
        if (btn) {
            // A button inside the section was clicked — route to its handler.
            if (btn.classList.contains('bo-collapse')) {
                toggleSection(sectionName);
                return;
            }
            if (btn.classList.contains('bo-name-edit-btn')) {
                editingSectionName = sectionName;
                renderBackoffice();
                // Focus the freshly-rendered input on the next tick.
                setTimeout(function () {
                    var input = boSectionListEl.querySelector(
                        '[data-section-name="' + sectionName.replace(/"/g, '\\\\"') + '"] .bo-name-input'
                    );
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }, 0);
                return;
            }
            if (btn.classList.contains('bo-section-delete')) {
                send({ type: 'backoffice.deleteSection', payload: { name: sectionName } });
                return;
            }

            var gameRow = btn.closest('.bo-game-row');
            if (gameRow) {
                var gameKey = gameRow.getAttribute('data-game-key');
                if (btn.classList.contains('bo-game-remove')) {
                    send({ type: 'backoffice.removeGame', payload: { sectionName: sectionName, gameKey: gameKey } });
                    return;
                }
            }
            return;
        }

        // Not a button or input — if the click landed inside the section
        // header, treat it as an expand/collapse toggle. Clicks inside the
        // expanded body (the games list) do nothing.
        if (target.closest('.bo-section-header')) {
            toggleSection(sectionName);
        }
    }

    function handleBackofficeChange(e) {
        if (e.target && e.target.classList && e.target.classList.contains('bo-add-select')) {
            var sectionEl = e.target.closest('.bo-section');
            if (!sectionEl) return;
            var sectionName = sectionEl.getAttribute('data-section-name');
            var gameKey = e.target.value;
            if (gameKey) {
                send({ type: 'backoffice.addGame', payload: { sectionName: sectionName, gameKey: gameKey } });
                e.target.value = '';
            }
        }
    }

    function handleBackofficeBlur(e) {
        if (e.target && e.target.classList && e.target.classList.contains('bo-name-input')) {
            // Escape was pressed — cancel without committing.
            if (cancelNextBlur) {
                cancelNextBlur = false;
                editingSectionName = null;
                renderBackoffice();
                return;
            }
            var sectionEl = e.target.closest('.bo-section');
            if (!sectionEl) return;
            var oldName = sectionEl.getAttribute('data-section-name');
            var newName = e.target.value;
            editingSectionName = null;
            if (newName && newName !== oldName) {
                send({ type: 'backoffice.renameSection', payload: { oldName: oldName, newName: newName } });
                // The store update will trigger a re-render with the new name.
                // If the rename is rejected (duplicate, empty, etc.), nothing
                // happens server-side — re-render anyway so the input is replaced.
                renderBackoffice();
            } else {
                renderBackoffice();
            }
        }
    }

    function handleBackofficeKeydown(e) {
        if (!e.target || !e.target.classList || !e.target.classList.contains('bo-name-input')) return;
        // Enter commits — trigger blur which runs the commit logic.
        if (e.key === 'Enter') {
            e.target.blur();
            return;
        }
        // Escape cancels — set the flag so the upcoming blur skips the commit.
        if (e.key === 'Escape') {
            cancelNextBlur = true;
            e.target.blur();
        }
    }

    if (boSectionListEl) {
        boSectionListEl.addEventListener('click', handleBackofficeClick);
        boSectionListEl.addEventListener('change', handleBackofficeChange);
        // blur doesn't bubble — listen in capture phase
        boSectionListEl.addEventListener('blur', handleBackofficeBlur, true);
        boSectionListEl.addEventListener('keydown', handleBackofficeKeydown);
        boSectionListEl.addEventListener('dragstart', handleDragStart);
        boSectionListEl.addEventListener('dragover', handleDragOver);
        boSectionListEl.addEventListener('dragleave', handleDragLeave);
        boSectionListEl.addEventListener('drop', handleDrop);
        boSectionListEl.addEventListener('dragend', handleDragEnd);
    }

    /* ── Mini Backoffice drag-and-drop reorder ─────────────────
     *
     * Only the drag handle (.bo-drag-handle) is draggable — not the row
     * itself — so clicks on the rename pencil, arrows, and delete button
     * still feel like clicks.
     *
     * Coordination uses the module-level dragState object rather than
     * dataTransfer (which is lossy across the dragenter/dragover/drop
     * event boundary in some browsers). We still call setData('text/plain')
     * so the API is satisfied and Firefox actually fires dragstart.
     */

    function clearDropIndicators() {
        var prev = boSectionListEl.querySelectorAll('.drop-above, .drop-below');
        for (var i = 0; i < prev.length; i++) {
            prev[i].classList.remove('drop-above');
            prev[i].classList.remove('drop-below');
        }
    }

    function getDropTargetRow(target, dragType) {
        if (!target || !target.closest) return null;
        if (dragType === 'section') {
            var sectionEl = target.closest('.bo-section');
            if (!sectionEl) return null;
            // Only consider sections inside the SAME section list — guards
            // against dropping a section onto a game row by mistake.
            return sectionEl;
        }
        if (dragType === 'game') {
            var rowEl = target.closest('.bo-game-row');
            if (!rowEl) return null;
            // Confine game drops to the SAME section as the source.
            var owningSection = rowEl.closest('.bo-section');
            if (!owningSection || !dragState || owningSection.getAttribute('data-section-name') !== dragState.sectionName) {
                return null;
            }
            return rowEl;
        }
        return null;
    }

    function handleDragStart(e) {
        var handle = e.target && e.target.classList && e.target.classList.contains('bo-drag-handle')
            ? e.target
            : null;
        if (!handle) return;
        var dragType = handle.getAttribute('data-drag-type');
        if (dragType !== 'section' && dragType !== 'game') return;

        if (dragType === 'section') {
            var sectionEl = handle.closest('.bo-section');
            if (!sectionEl) return;
            var name = sectionEl.getAttribute('data-section-name');
            dragState = { type: 'section', sectionName: name, rowEl: sectionEl };
            sectionEl.classList.add('dragging');
        } else {
            var gameRow = handle.closest('.bo-game-row');
            var owningSection = gameRow && gameRow.closest('.bo-section');
            if (!gameRow || !owningSection) return;
            dragState = {
                type: 'game',
                sectionName: owningSection.getAttribute('data-section-name'),
                gameKey: gameRow.getAttribute('data-game-key'),
                rowEl: gameRow,
            };
            gameRow.classList.add('dragging');
        }
        handle.classList.add('dragging');

        isDragging = true;
        pendingBackofficeRender = false;
        cancelDragOnEnd = false;

        // dataTransfer.setData is required for Firefox to actually fire the
        // drag. The string itself is unused — we coordinate via dragState.
        if (e.dataTransfer) {
            try { e.dataTransfer.setData('text/plain', dragState.type); } catch (err) { /* ignore */ }
            e.dataTransfer.effectAllowed = 'move';
        }
    }

    function handleDragOver(e) {
        if (!dragState) return;
        var row = getDropTargetRow(e.target, dragState.type);
        if (!row) return;
        e.preventDefault(); // required for drop to fire
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';

        // Determine which half of the row the cursor sits over.
        var rect = row.getBoundingClientRect();
        var midpoint = rect.top + rect.height / 2;
        var above = e.clientY < midpoint;

        // Clear any prior indicator and set the new one. Skip toggling on
        // the source row itself — dropping onto yourself is a no-op.
        clearDropIndicators();
        if (row === dragState.rowEl) return;
        row.classList.add(above ? 'drop-above' : 'drop-below');
    }

    function handleDragLeave(e) {
        if (!dragState) return;
        // Only clear when we've actually left the row, not when crossing
        // between child elements inside the same row.
        var row = getDropTargetRow(e.target, dragState.type);
        if (!row) return;
        var related = e.relatedTarget;
        if (related && row.contains(related)) return;
        row.classList.remove('drop-above');
        row.classList.remove('drop-below');
    }

    function handleDrop(e) {
        if (!dragState) return;
        var row = getDropTargetRow(e.target, dragState.type);
        if (!row) return;
        e.preventDefault();

        if (row === dragState.rowEl) {
            // Dropped on itself — no-op.
            return;
        }

        var rect = row.getBoundingClientRect();
        var midpoint = rect.top + rect.height / 2;
        var above = e.clientY < midpoint;

        if (dragState.type === 'section') {
            // Build the new section order from the live DOM (which still
            // reflects the pre-drop state — we haven't re-rendered yet).
            var sectionEls = boSectionListEl.querySelectorAll('.bo-section');
            var names = [];
            for (var i = 0; i < sectionEls.length; i++) {
                names.push(sectionEls[i].getAttribute('data-section-name'));
            }
            var sourceIdx = names.indexOf(dragState.sectionName);
            var targetIdx = names.indexOf(row.getAttribute('data-section-name'));
            if (sourceIdx === -1 || targetIdx === -1) return;
            names.splice(sourceIdx, 1);
            // After splice the target's index shifts if source was before target.
            var insertAt = names.indexOf(row.getAttribute('data-section-name'));
            if (insertAt === -1) return;
            insertAt = above ? insertAt : insertAt + 1;
            names.splice(insertAt, 0, dragState.sectionName);
            dragState.pendingDispatch = { kind: 'section', names: names };
        } else {
            // Same-section game reorder.
            var sectionEl2 = row.closest('.bo-section');
            if (!sectionEl2) return;
            var rowEls = sectionEl2.querySelectorAll('.bo-game-row');
            var keys = [];
            for (var j = 0; j < rowEls.length; j++) {
                keys.push(rowEls[j].getAttribute('data-game-key'));
            }
            var srcIdx = keys.indexOf(dragState.gameKey);
            var tgtIdx = keys.indexOf(row.getAttribute('data-game-key'));
            if (srcIdx === -1 || tgtIdx === -1) return;
            keys.splice(srcIdx, 1);
            var insertAtG = keys.indexOf(row.getAttribute('data-game-key'));
            if (insertAtG === -1) return;
            insertAtG = above ? insertAtG : insertAtG + 1;
            keys.splice(insertAtG, 0, dragState.gameKey);
            dragState.pendingDispatch = {
                kind: 'game',
                sectionName: dragState.sectionName,
                gameKeys: keys,
            };
        }
    }

    function handleDragEnd() {
        // Always clean up regardless of whether drop succeeded — dragend
        // fires on cancel (Esc, drop outside, etc.) too.
        if (dragState && dragState.rowEl) {
            dragState.rowEl.classList.remove('dragging');
        }
        clearDropIndicators();
        var handles = boSectionListEl.querySelectorAll('.bo-drag-handle.dragging');
        for (var i = 0; i < handles.length; i++) {
            handles[i].classList.remove('dragging');
        }

        var dispatch = dragState && dragState.pendingDispatch;
        var shouldCancel = cancelDragOnEnd;

        isDragging = false;
        dragState = null;
        cancelDragOnEnd = false;

        if (shouldCancel) {
            // A backoffice.state broadcast arrived during the drag — re-render
            // fresh and discard the gesture so we don't commit a stale order.
            pendingBackofficeRender = false;
            renderBackoffice();
            return;
        }

        if (dispatch) {
            if (dispatch.kind === 'section') {
                send({ type: 'backoffice.setSectionOrder', payload: { names: dispatch.names } });
            } else {
                send({
                    type: 'backoffice.setGameOrder',
                    payload: { sectionName: dispatch.sectionName, gameKeys: dispatch.gameKeys },
                });
            }
            // The lobby's state broadcast will rebuild the list with the new
            // order; no need to re-render locally.
            pendingBackofficeRender = false;
            return;
        }

        if (pendingBackofficeRender) {
            pendingBackofficeRender = false;
            renderBackoffice();
        }
    }

    if (boResetBtn) {
        boResetBtn.addEventListener('click', function () {
            send({ type: 'backoffice.resetToApi' });
        });
    }

    if (boHotColdMockBtn) {
        boHotColdMockBtn.addEventListener('click', function () {
            var next = !backofficeState.hotColdMockActive;
            send({ type: 'backoffice.setHotColdMock', payload: { enabled: next } });
        });
    }

    /* ── Sub-tab switching inside Mini Backoffice ────────────── */

    function showBoSubtab(name) {
        boSubtabButtons.forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-subtab') === name);
        });
        boSubtabPanels.forEach(function (p) {
            p.classList.toggle('active', p.getAttribute('data-subpanel') === name);
        });
    }

    boSubtabButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            showBoSubtab(btn.getAttribute('data-subtab'));
        });
    });

    /* ── Casino sub-tab ─────────────────────────────────────── */

    function renderCasinoTab() {
        if (boOpenTypeBtn) {
            boOpenTypeBtn.textContent = backofficeState.openType || 'fullscreen';
            boOpenTypeBtn.classList.toggle('on', backofficeState.openType === 'ingame');
        }
        if (boBrandingSelect) {
            boBrandingSelect.value = backofficeState.seasonalBranding || '';
        }
        if (boCurrencySelect) {
            var currencies = backofficeState.availableCurrencies || [];
            var current = backofficeState.currency || '';
            // Rebuild the option list in place. Avoid a full innerHTML rewrite
            // when the option set hasn't changed — preserves focus / keeps the
            // dropdown stable across re-broadcasts.
            var existing = [];
            for (var ci = 0; ci < boCurrencySelect.options.length; ci++) {
                existing.push(boCurrencySelect.options[ci].value);
            }
            var changed = existing.length !== currencies.length;
            if (!changed) {
                for (var cj = 0; cj < currencies.length; cj++) {
                    if (existing[cj] !== currencies[cj]) { changed = true; break; }
                }
            }
            if (changed) {
                boCurrencySelect.innerHTML = '';
                for (var ck = 0; ck < currencies.length; ck++) {
                    var opt = document.createElement('option');
                    opt.value = currencies[ck];
                    opt.textContent = currencies[ck];
                    boCurrencySelect.appendChild(opt);
                }
            }
            boCurrencySelect.value = current;
            // The hardcoded QA list is never empty — the only reason to
            // disable is no balance loaded yet (controller's setCurrency
            // no-ops + warns in that case).
            var hasBalance = !!current;
            boCurrencySelect.disabled = !hasBalance;
            if (boCurrencyHintEl) {
                boCurrencyHintEl.textContent = hasBalance ? '' : 'balance not loaded yet';
            }
        }
        if (boFlagPrizePop) boFlagPrizePop.checked = !!backofficeState.enablePrizePopCampaign;
        if (boFlagTournaments) boFlagTournaments.checked = !!backofficeState.enableTournamentsCampaign;
        if (boFlagRebate) boFlagRebate.checked = !!backofficeState.enableRebateCampaign;
    }

    if (boOpenTypeBtn) {
        boOpenTypeBtn.addEventListener('click', function () {
            var next = backofficeState.openType === 'ingame' ? 'fullscreen' : 'ingame';
            send({ type: 'backoffice.setOpenType', payload: { openType: next } });
        });
    }

    if (boBrandingSelect) {
        boBrandingSelect.addEventListener('change', function () {
            var val = boBrandingSelect.value || null;
            send({ type: 'backoffice.setSeasonalBranding', payload: { value: val } });
        });
    }

    if (boCurrencySelect) {
        boCurrencySelect.addEventListener('change', function () {
            var val = boCurrencySelect.value || '';
            if (!val) return;
            send({ type: 'backoffice.setCurrency', payload: { currency: val } });
        });
    }

    function wireCampaignFlagCheckbox(el, flag) {
        if (!el) return;
        el.addEventListener('change', function () {
            send({ type: 'backoffice.setCampaignFlag', payload: { flag: flag, value: el.checked } });
        });
    }
    wireCampaignFlagCheckbox(boFlagPrizePop, 'enablePrizePopCampaign');
    wireCampaignFlagCheckbox(boFlagTournaments, 'enableTournamentsCampaign');
    wireCampaignFlagCheckbox(boFlagRebate, 'enableRebateCampaign');

    // Initial render — the backoffice state may already be populated via the
    // initial snapshot embedded in the popup HTML.
    renderCasinoTab();

    /* ── Games sub-tab: picker + edit form ──────────────────── */

    // Walk the current backoffice state looking for the selected game.
    // Games can appear in multiple sections — we only need the first hit
    // because every instance carries the same field values after each edit.
    function getCurrentSelectedGame() {
        if (!selectedGameKey) return null;
        var sections = backofficeState.sections || [];
        for (var i = 0; i < sections.length; i++) {
            var games = sections[i].games || [];
            for (var j = 0; j < games.length; j++) {
                if (games[j].gameKey === selectedGameKey) return games[j];
            }
        }
        return null;
    }

    function renderBackofficeGames() {
        if (!boGamePickerEl) return;

        // Build a deduped list of games that are currently in at least one
        // section. Raw-only games from supportedGames aren't eligible
        // because we'd need the parsed IGameSettings to edit them safely.
        var editableGamesMap = {};
        (backofficeState.sections || []).forEach(function (s) {
            (s.games || []).forEach(function (g) {
                if (!editableGamesMap[g.gameKey]) editableGamesMap[g.gameKey] = g;
            });
        });
        var allEditable = Object.keys(editableGamesMap)
            .map(function (k) { return editableGamesMap[k]; })
            .sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });

        // Search filter
        var query = gameSearchQuery.toLowerCase();
        var filtered = query
            ? allEditable.filter(function (g) {
                return (g.name || '').toLowerCase().indexOf(query) !== -1 ||
                       g.gameKey.toLowerCase().indexOf(query) !== -1;
              })
            : allEditable;

        // Picker list
        var html = '';
        if (allEditable.length === 0) {
            html = '<div class="empty">No games loaded yet — open the lobby first.</div>';
        } else if (filtered.length === 0) {
            html = '<div class="empty">No games match "' + escapeHtml(gameSearchQuery) + '".</div>';
        } else {
            var slice = filtered.slice(0, 200);
            slice.forEach(function (g) {
                var isSelected = g.gameKey === selectedGameKey;
                html += '<div class="bo-game-item' + (isSelected ? ' selected' : '') + '" data-game-key="' + escapeHtml(g.gameKey) + '">';
                html += '<span class="bo-game-item-name">' + escapeHtml(g.name || g.gameKey) + '</span>';
                html += '<span class="bo-game-item-key">' + escapeHtml(g.gameKey) + '</span>';
                html += '</div>';
            });
            if (filtered.length > 200) {
                html += '<div class="empty">+ ' + (filtered.length - 200) + ' more — refine search</div>';
            }
        }
        boGamePickerEl.innerHTML = html;

        // Edit form
        var selected = getCurrentSelectedGame();
        if (!selected) {
            boEditTitleEl.style.display = 'none';
            boEditFormEl.style.display = 'none';
            return;
        }
        boEditTitleEl.style.display = '';
        boEditTitleTextEl.textContent = 'Edit — ' + (selected.name || selected.gameKey);
        boEditFormEl.style.display = '';

        // Skip fields that are currently focused so the user's typing
        // isn't wiped by an incoming state message.
        var focused = document.activeElement;
        function setIfUnfocused(el, value) {
            if (el && el !== focused) el.value = value == null ? '' : value;
        }
        // <input type="date"> only accepts YYYY-MM-DD — strip any time/TZ suffix.
        function toDateInputValue(raw) {
            if (!raw || typeof raw !== 'string') return '';
            var m = raw.match(/^\\d{4}-\\d{2}-\\d{2}/);
            return m ? m[0] : '';
        }
        setIfUnfocused(boEditActivatedOnEl, toDateInputValue(selected.activatedOn));
        setIfUnfocused(boEditAddedToLobbyOnEl, toDateInputValue(selected.addedToLobbyOn));
        setIfUnfocused(boEditJackpotNameEl, selected.jackpotName);
        setIfUnfocused(boEditDisplayJackpotNameEl, selected.displayJackpotName);

        if (boEditTopGameEl && boEditTopGameEl !== focused) {
            boEditTopGameEl.checked = !!selected.topGame;
        }
        if (boEditIsInTournamentEl && boEditIsInTournamentEl !== focused) {
            boEditIsInTournamentEl.checked = !!selected.isInTournament;
        }

        var currentPlayersValue = typeof selected.currentPlayers === 'number' ? selected.currentPlayers : 0;
        if (boEditCurrentPlayersEl && boEditCurrentPlayersEl !== focused) {
            boEditCurrentPlayersEl.value = String(currentPlayersValue);
        }
        if (boEditCurrentPlayersToggleEl && boEditCurrentPlayersToggleEl !== focused) {
            boEditCurrentPlayersToggleEl.checked = currentPlayersValue > 0;
        }

        var betCurrency = typeof selected.currency === 'string' ? selected.currency : '';
        var minBetValue = typeof selected.minBet === 'number' ? selected.minBet : 0;
        var maxBetValue = typeof selected.maxBet === 'number' ? selected.maxBet : 0;
        if (boEditBetLabelEl) {
            boEditBetLabelEl.textContent = betCurrency ? 'Bet range (' + betCurrency + ')' : 'Bet range';
        }
        if (boEditBetHintEl) {
            boEditBetHintEl.textContent = betCurrency
                ? 'Applied to the current currency only — set both to 0 to hide'
                : 'No currency loaded — editing disabled';
        }
        if (boEditMinBetEl && boEditMinBetEl !== focused) {
            boEditMinBetEl.value = String(minBetValue);
        }
        if (boEditMaxBetEl && boEditMaxBetEl !== focused) {
            boEditMaxBetEl.value = String(maxBetValue);
        }
        if (boEditMinBetEl) boEditMinBetEl.disabled = !betCurrency;
        if (boEditMaxBetEl) boEditMaxBetEl.disabled = !betCurrency;

        if (boEditNewGameStateEl) {
            boEditNewGameStateEl.textContent = selected.newGame ? 'NEW badge on' : 'off';
            boEditNewGameStateEl.classList.toggle('on', !!selected.newGame);
        }

        // Features tags — always re-render (no focused state to preserve)
        if (boEditFeaturesEl) {
            var features = selected.gameFeatures || [];
            if (features.length === 0) {
                boEditFeaturesEl.innerHTML = '<span class="bo-edit-tags-empty">No features</span>';
            } else {
                var tagsHtml = '';
                features.forEach(function (f) {
                    tagsHtml += '<span class="bo-edit-tag">' + escapeHtml(f) +
                        '<button type="button" class="bo-tag-remove" data-feature="' + escapeHtml(f) + '">×</button></span>';
                });
                boEditFeaturesEl.innerHTML = tagsHtml;
            }
        }
    }

    // Game picker click → select
    if (boGamePickerEl) {
        boGamePickerEl.addEventListener('click', function (e) {
            var item = e.target.closest && e.target.closest('.bo-game-item');
            if (!item) return;
            selectedGameKey = item.getAttribute('data-game-key');
            renderBackofficeGames();
        });
    }

    // Search input
    if (boGameSearchEl) {
        boGameSearchEl.addEventListener('input', function () {
            gameSearchQuery = boGameSearchEl.value;
            renderBackofficeGames();
        });
    }

    // Send a patch to the lobby for the currently-selected game.
    function sendGameUpdate(patch) {
        if (!selectedGameKey) return;
        send({ type: 'backoffice.updateGame', payload: { gameKey: selectedGameKey, patch: patch } });
    }

    // Text inputs — commit on blur and on Enter
    function wireTextField(el, fieldName) {
        if (!el) return;
        el.addEventListener('blur', function () {
            if (!selectedGameKey) return;
            var value = el.value;
            var patch = {};
            patch[fieldName] = value === '' ? null : value;
            sendGameUpdate(patch);
        });
        el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') el.blur();
        });
    }
    wireTextField(boEditActivatedOnEl, 'activatedOn');
    wireTextField(boEditAddedToLobbyOnEl, 'addedToLobbyOn');
    wireTextField(boEditJackpotNameEl, 'jackpotName');
    wireTextField(boEditDisplayJackpotNameEl, 'displayJackpotName');

    // Checkboxes — commit on change
    if (boEditTopGameEl) {
        boEditTopGameEl.addEventListener('change', function () {
            sendGameUpdate({ topGame: boEditTopGameEl.checked });
        });
    }
    if (boEditIsInTournamentEl) {
        boEditIsInTournamentEl.addEventListener('change', function () {
            sendGameUpdate({ isInTournament: boEditIsInTournamentEl.checked });
        });
    }

    // Current players toggle: unchecking → 0 (hides badge), checking → current
    // input value (default 1 when blank / 0). The toggle reflects whether the
    // count is > 0, but the number input remains the source of truth.
    if (boEditCurrentPlayersToggleEl) {
        boEditCurrentPlayersToggleEl.addEventListener('change', function () {
            if (boEditCurrentPlayersToggleEl.checked) {
                var parsed = parseInt(boEditCurrentPlayersEl ? boEditCurrentPlayersEl.value : '', 10);
                var next = isNaN(parsed) || parsed <= 0 ? 1 : parsed;
                if (boEditCurrentPlayersEl) boEditCurrentPlayersEl.value = String(next);
                sendGameUpdate({ currentPlayers: next });
            } else {
                if (boEditCurrentPlayersEl) boEditCurrentPlayersEl.value = '0';
                sendGameUpdate({ currentPlayers: 0 });
            }
        });
    }

    // Number input: commit on input so QA sees the badge update live.
    // Keeps the toggle in sync with the value.
    if (boEditCurrentPlayersEl) {
        boEditCurrentPlayersEl.addEventListener('input', function () {
            var parsed = parseInt(boEditCurrentPlayersEl.value, 10);
            var next = isNaN(parsed) || parsed < 0 ? 0 : parsed;
            if (boEditCurrentPlayersToggleEl) {
                boEditCurrentPlayersToggleEl.checked = next > 0;
            }
            sendGameUpdate({ currentPlayers: next });
        });
    }

    // Min/max bet inputs: commit on input so the MinMaxBet badge updates live
    // against the currently-loaded currency. Fractional values are allowed
    // (step 0.01 — Map values are already in display units from settings.api).
    if (boEditMinBetEl) {
        boEditMinBetEl.addEventListener('input', function () {
            var parsed = parseFloat(boEditMinBetEl.value);
            var next = isNaN(parsed) || parsed < 0 ? 0 : parsed;
            sendGameUpdate({ minBet: next });
        });
    }
    if (boEditMaxBetEl) {
        boEditMaxBetEl.addEventListener('input', function () {
            var parsed = parseFloat(boEditMaxBetEl.value);
            var next = isNaN(parsed) || parsed < 0 ? 0 : parsed;
            sendGameUpdate({ maxBet: next });
        });
    }

    // Features: add new tag via button or Enter
    if (boEditFeaturesAddEl && boEditFeaturesInputEl) {
        var addFeature = function () {
            var raw = boEditFeaturesInputEl.value.trim();
            if (!raw) return;
            var selected = getCurrentSelectedGame();
            if (!selected) return;
            var features = (selected.gameFeatures || []).slice();
            if (features.indexOf(raw) !== -1) {
                boEditFeaturesInputEl.value = '';
                return;
            }
            features.push(raw);
            sendGameUpdate({ gameFeatures: features });
            boEditFeaturesInputEl.value = '';
        };
        boEditFeaturesAddEl.addEventListener('click', addFeature);
        boEditFeaturesInputEl.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); addFeature(); }
        });
    }

    // Features: remove a tag (event delegation)
    if (boEditFeaturesEl) {
        boEditFeaturesEl.addEventListener('click', function (e) {
            var btn = e.target.closest && e.target.closest('.bo-tag-remove');
            if (!btn) return;
            var feature = btn.getAttribute('data-feature');
            var selected = getCurrentSelectedGame();
            if (!selected) return;
            var features = (selected.gameFeatures || []).filter(function (f) { return f !== feature; });
            sendGameUpdate({ gameFeatures: features });
        });
    }

    // Thumbnail checker
    var thumbCheckRunning = false;

    function renderThumbResults(result) {
        if (!boThumbResultsEl || !boThumbStatusEl) return;
        boThumbStatusEl.style.display = '';
        boThumbResultsEl.style.display = '';

        if (result.missing.length === 0) {
            boThumbStatusEl.textContent = 'All ' + result.total + ' thumbnails are present on the CDN.';
            boThumbStatusEl.style.color = 'var(--accent)';
            boThumbResultsEl.innerHTML = '';
        } else {
            boThumbStatusEl.textContent = 'Found ' + result.missing.length + ' game(s) with missing thumbnails out of ' + result.total + ' total.';
            boThumbStatusEl.style.color = 'var(--status-warn)';
            var html = '<div style="max-height:260px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius-sm);margin-top:4px">';
            html += '<table style="width:100%;border-collapse:collapse;font-size:11px">';
            html += '<thead><tr style="position:sticky;top:0;background:var(--surface-2);border-bottom:1px solid var(--border)">';
            html += '<th style="text-align:left;padding:5px 8px;color:var(--text-dim)">Game</th>';
            html += '<th style="text-align:left;padding:5px 8px;color:var(--text-dim)">Key</th>';
            html += '</tr></thead><tbody>';
            result.missing.forEach(function (entry) {
                html += '<tr style="border-bottom:1px solid var(--border)">';
                html += '<td style="padding:4px 8px;color:var(--text)">' + escapeHtml(entry.name) + '</td>';
                html += '<td style="padding:4px 8px"><code style="font-size:10px;color:var(--text-faint)">' + escapeHtml(entry.gameKey) + '</code></td>';
                html += '</tr>';
            });
            html += '</tbody></table></div>';
            boThumbResultsEl.innerHTML = html;
        }
    }

    if (boThumbCheckBtnEl) {
        boThumbCheckBtnEl.addEventListener('click', function () {
            if (thumbCheckRunning) return;
            thumbCheckRunning = true;
            boThumbCheckBtnEl.disabled = true;
            boThumbCheckBtnEl.textContent = 'Checking\u2026';
            if (boThumbStatusEl) {
                boThumbStatusEl.style.display = '';
                boThumbStatusEl.textContent = 'Sending HEAD requests to the CDN\u2026';
                boThumbStatusEl.style.color = 'var(--text-dim)';
            }
            if (boThumbResultsEl) boThumbResultsEl.style.display = 'none';
            send({ type: 'backoffice.checkThumbnails' });
        });
    }

    // Ask the lobby for an initial backoffice state on popup boot.
    send({ type: 'backoffice.requestState' });

    /* ── Settings tab: presets + auto-save ──────────────────── */

    var presetExportBtn = document.getElementById('preset-export');
    var presetImportInput = document.getElementById('preset-import');
    var presetFeedbackEl = document.getElementById('preset-feedback');
    var presetAutosaveEl = document.getElementById('preset-autosave');
    var presetAutosaveStatusEl = document.getElementById('preset-autosave-status');
    var presetClearBtn = document.getElementById('preset-clear');
    var presetFeedbackTimer = null;

    var presetState = {
        autoSaveEnabled: false,
        hasStoredPreset: false,
        lastSavedAt: null,
    };

    function showPresetFeedback(msg, kind) {
        if (presetFeedbackTimer) clearTimeout(presetFeedbackTimer);
        if (!presetFeedbackEl) return;
        presetFeedbackEl.textContent = msg;
        presetFeedbackEl.className = 'jp-feedback show ' + kind;
        presetFeedbackTimer = setTimeout(function () {
            presetFeedbackEl.className = 'jp-feedback';
        }, 3500);
    }

    function renderPresetState() {
        if (presetAutosaveEl) {
            presetAutosaveEl.checked = presetState.autoSaveEnabled;
        }
        if (presetAutosaveStatusEl) {
            if (presetState.autoSaveEnabled && presetState.lastSavedAt) {
                var d = new Date(presetState.lastSavedAt);
                presetAutosaveStatusEl.textContent = 'Last saved: ' + d.toLocaleTimeString();
            } else if (presetState.autoSaveEnabled && presetState.hasStoredPreset) {
                presetAutosaveStatusEl.textContent = 'Stored preset found.';
            } else if (presetState.autoSaveEnabled) {
                presetAutosaveStatusEl.textContent = 'Auto-save is on. No preset stored yet.';
            } else {
                presetAutosaveStatusEl.textContent = presetState.hasStoredPreset
                    ? 'Auto-save is off. A stored preset exists.'
                    : 'No stored preset.';
            }
        }
    }

    function downloadJson(json, filename) {
        var blob = new Blob([json], { type: 'application/json' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    if (presetExportBtn) {
        presetExportBtn.addEventListener('click', function () {
            send({ type: 'preset.requestExport' });
        });
    }

    if (presetImportInput) {
        presetImportInput.addEventListener('change', function () {
            var file = presetImportInput.files && presetImportInput.files[0];
            if (!file) return;
            var reader = new FileReader();
            reader.onload = function () {
                try {
                    var preset = JSON.parse(reader.result);
                    if (!preset || preset.version !== 1) throw new Error('Invalid preset format');
                    send({ type: 'preset.import', payload: { preset: preset } });
                } catch (e) {
                    showPresetFeedback('Invalid preset file: ' + e.message, 'err');
                }
                presetImportInput.value = '';
            };
            reader.readAsText(file);
        });
    }

    if (presetAutosaveEl) {
        presetAutosaveEl.addEventListener('change', function () {
            send({ type: 'preset.setAutoSave', payload: { enabled: presetAutosaveEl.checked } });
        });
    }

    if (presetClearBtn) {
        presetClearBtn.addEventListener('click', function () {
            send({ type: 'preset.clearStorage' });
            showPresetFeedback('Storage cleared.', 'ok');
        });
    }

    // Ask for initial preset state.
    send({ type: 'preset.requestState' });

    /* ── Console tab ──────────────────────────────────────────── */

    var cnCountEl = document.getElementById('cn-count');
    var cnSummaryEl = document.getElementById('cn-summary');
    var cnListEl = document.getElementById('cn-list');
    var cnClearBtnEl = document.getElementById('cn-clear-btn');
    var cnTabBadgeEl = document.getElementById('cn-tab-badge');

    var consoleState = { entries: [], totalCaptured: 0 };
    /* Expand state survives renderConsoleTab() re-runs so a broadcast
     * doesn't collapse rows the user just opened. Keyed by entry id. */
    var cnExpandedRows = Object.create(null);

    function cnEscape(s) {
        var div = document.createElement('div');
        div.textContent = s == null ? '' : String(s);
        return div.innerHTML;
    }

    function cnRelativeTime(ts) {
        if (!ts) return '';
        var diff = Math.max(0, Date.now() - ts);
        var sec = Math.floor(diff / 1000);
        if (sec < 60) return sec + 's ago';
        var min = Math.floor(sec / 60);
        if (min < 60) return min + 'm ago';
        var hr = Math.floor(min / 60);
        return hr + 'h ago';
    }

    function cnCatLabel(cat) {
        return cat || 'unknown';
    }

    /* Per-category ownership explainer shown inline in expanded rows so QA
     * can route an error to the right team without reading the stack. Keys
     * mirror the ConsoleCategory union in consoleController.ts. */
    var CN_OWNER_HELP = {
        'game-client': 'Lobby crashed while talking to the parent app (postMessage, ExternalCommunication). Likely an integration / parent-app bug — flag to the game-client team.',
        'asset': 'A static asset (image, video, CSS, JSON) failed to load. Check the URL: wrong path, CDN issue, or missing file. Ping whoever published the asset.',
        'api': 'A backend call failed (fetch, HTTP error, timeout). Check the network tab for the actual endpoint. Flag to the backend team.',
        'lobby-ui': 'A React component crashed while rendering. Lobby frontend bug — capture the component name from the stack and file with the lobby team.',
        'lobby-logic': 'Lobby code threw outside of rendering — store update, hook, util. Lobby frontend bug.',
        'unknown': "Couldn't auto-classify. Read the stack manually to decide ownership."
    };

    function cnOwnerHelp(cat) {
        return CN_OWNER_HELP[cat] || CN_OWNER_HELP.unknown;
    }

    function renderConsoleTab() {
        var entries = (consoleState && consoleState.entries) || [];
        var errorCount = 0;
        for (var ei = 0; ei < entries.length; ei++) {
            if (entries[ei].level === 'error') errorCount += 1;
        }

        if (cnCountEl) cnCountEl.textContent = String(entries.length);
        if (cnSummaryEl) {
            if (entries.length === 0) {
                cnSummaryEl.textContent = 'No errors captured yet.';
            } else {
                var warnCount = entries.length - errorCount;
                cnSummaryEl.textContent =
                    entries.length + ' unique entries (' + errorCount + ' error, ' +
                    warnCount + ' warn) — ' + (consoleState.totalCaptured || 0) + ' total observations.';
            }
        }

        /* Tab-strip badge: shows error count, hidden when 0. Counts errors
         * only (warns are common and noisy — a red dot for every warn would
         * cry wolf). */
        if (cnTabBadgeEl) {
            if (errorCount > 0) {
                cnTabBadgeEl.textContent = String(errorCount);
                cnTabBadgeEl.hidden = false;
            } else {
                cnTabBadgeEl.hidden = true;
            }
        }

        /* Prune expanded keys for entries that no longer exist (clear/evict). */
        var liveIds = Object.create(null);
        for (var li = 0; li < entries.length; li++) liveIds[entries[li].id] = true;
        for (var ek in cnExpandedRows) {
            if (!liveIds[ek]) delete cnExpandedRows[ek];
        }

        if (!cnListEl) return;

        if (entries.length === 0) {
            cnListEl.innerHTML = '<div class="cn-empty">Nothing yet. Captured errors will appear here.</div>';
            return;
        }

        var html = '';
        for (var i = 0; i < entries.length; i++) {
            var e = entries[i];
            var expanded = !!cnExpandedRows[e.id];
            var levelClass = e.level === 'error' ? 'level-error' : 'level-warn';
            var catClass = 'cat cat-' + cnEscape(e.category);
            html += '<div class="cn-row' + (expanded ? ' expanded' : '') + '" data-cn-id="' + cnEscape(e.id) + '">';
            html += '<div class="cn-row-head">';
            html += '<span class="cn-pill ' + levelClass + '">' + cnEscape(e.level) + '</span>';
            html += '<span class="cn-pill ' + catClass + '">' + cnEscape(cnCatLabel(e.category)) + '</span>';
            html += '<span class="cn-msg">' + cnEscape(e.message) + '</span>';
            if (e.count > 1) {
                html += '<span class="cn-count">x' + e.count + '</span>';
            }
            html += '<span class="cn-time">' + cnEscape(cnRelativeTime(e.lastSeenAt)) + '</span>';
            html += '</div>';
            if (expanded) {
                html += '<div class="cn-expanded">';
                html += '<div class="cn-owner"><span class="cn-owner-label">Who owns this?</span>' +
                        cnEscape(cnOwnerHelp(e.category)) + '</div>';
                html += '<div class="cn-source">' + cnEscape((e.source && e.source.type) || '') +
                        ' — ' + cnEscape((e.source && e.source.detail) || '') + '</div>';
                if (e.stack) {
                    html += '<pre class="cn-stack">' + cnEscape(e.stack) + '</pre>';
                } else {
                    html += '<div class="cn-source">(no stack available)</div>';
                }
                html += '</div>';
            }
            html += '</div>';
        }
        cnListEl.innerHTML = html;
    }

    if (cnListEl) {
        cnListEl.addEventListener('click', function (event) {
            var target = event.target;
            while (target && target !== cnListEl) {
                if (target.classList && target.classList.contains('cn-row')) {
                    var id = target.getAttribute('data-cn-id');
                    if (id) {
                        if (cnExpandedRows[id]) {
                            delete cnExpandedRows[id];
                        } else {
                            cnExpandedRows[id] = true;
                        }
                        renderConsoleTab();
                    }
                    return;
                }
                target = target.parentNode;
            }
        });
    }

    if (cnClearBtnEl) {
        cnClearBtnEl.addEventListener('click', function () {
            send({ type: 'console.clear' });
        });
    }

    // Ask for initial console state.
    send({ type: 'console.requestState' });

    /* ── Environment tab ──────────────────────────────────────── */

    var envGridEl = document.getElementById('env-casino-grid');
    var envExperimentalRowEl = document.getElementById('env-experimental-row');
    var envGameKeyEl = document.getElementById('env-gameKey');
    var envPlayerIdEl = document.getElementById('env-playerId');
    var envSessionTokenEl = document.getElementById('env-sessionToken');
    var envFieldPlayerIdEl = document.getElementById('env-field-playerId');
    var envFieldSessionTokenEl = document.getElementById('env-field-sessionToken');
    var envLaunchBtnEl = document.getElementById('env-launch-btn');
    var envLaunchHintEl = document.getElementById('env-launch-hint');

    /* Mirror of EnvironmentState. The lobby is authoritative — every
     * environment.state broadcast overwrites this. */
    var environmentState = {
        casinos: [],
        experimentals: [],
        currentPlayerId: null,
        currentPlayerName: null,
        currentLanguage: 'en',
        currentGameId: '',
        currentMultiplayer: 'false',
        currentDemo: 'false'
    };
    /* Selected env model. Casino and experimental are mutually exclusive —
     * picking one clears the other group's selection. */
    var envSelectedKind = null; // 'casino' | 'experimental' | null
    var envSelectedId = null;

    function envEscape(s) {
        var d = document.createElement('div');
        d.textContent = s == null ? '' : String(s);
        return d.innerHTML;
    }

    function envFindCasino(id) {
        var list = environmentState.casinos || [];
        for (var i = 0; i < list.length; i++) {
            if (list[i].id === id) return list[i];
        }
        return null;
    }

    function envFindExperimental(id) {
        var list = environmentState.experimentals || [];
        for (var i = 0; i < list.length; i++) {
            if (list[i].id === id) return list[i];
        }
        return null;
    }

    function envApplyFieldVisibility() {
        // Casinos need playerId + sessionToken; experimentals only use gameKey.
        // When nothing's selected we still show the playerId/sessionToken
        // fields, but disabled — clearer affordance than collapsing them and
        // having the form jump on selection.
        var isExperimental = envSelectedKind === 'experimental';
        if (envFieldPlayerIdEl) {
            envFieldPlayerIdEl.classList.toggle('env-field-hidden', isExperimental);
        }
        if (envFieldSessionTokenEl) {
            envFieldSessionTokenEl.classList.toggle('env-field-hidden', isExperimental);
        }
        var noSelection = envSelectedKind == null;
        if (envPlayerIdEl) envPlayerIdEl.disabled = noSelection;
        if (envSessionTokenEl) envSessionTokenEl.disabled = noSelection;
    }

    function envSyncLaunchButton() {
        if (!envLaunchBtnEl) return;
        if (envSelectedKind === 'casino') {
            var hasPlayerId = envPlayerIdEl && envPlayerIdEl.value.trim().length > 0;
            envLaunchBtnEl.disabled = !hasPlayerId;
        } else if (envSelectedKind === 'experimental') {
            envLaunchBtnEl.disabled = false;
        } else {
            envLaunchBtnEl.disabled = true;
        }
    }

    function envUpdateHint() {
        if (!envLaunchHintEl) return;
        if (envSelectedKind == null) {
            envLaunchHintEl.textContent = 'Select a casino or experimental environment.';
            return;
        }
        if (envSelectedKind === 'casino') {
            var hasPlayerId = envPlayerIdEl && envPlayerIdEl.value.trim().length > 0;
            envLaunchHintEl.textContent = hasPlayerId ? '' : 'Player id is required for casinos.';
            return;
        }
        envLaunchHintEl.textContent = 'Game key optional — paste a value to start with a specific game.';
    }

    function envRenderGrid() {
        if (!envGridEl) return;
        var list = environmentState.casinos || [];
        var html = '';
        for (var i = 0; i < list.length; i++) {
            var c = list[i];
            var selectedCls = envSelectedKind === 'casino' && c.id === envSelectedId ? ' selected' : '';
            html += '<button type="button" class="env-casino-btn' + selectedCls +
                '" data-env-casino="' + envEscape(c.id) + '">' +
                envEscape(c.label) +
                '</button>';
        }
        envGridEl.innerHTML = html;
    }

    function envRenderExperimentalRow() {
        if (!envExperimentalRowEl) return;
        var list = environmentState.experimentals || [];
        var html = '';
        for (var i = 0; i < list.length; i++) {
            var e = list[i];
            var selectedCls = envSelectedKind === 'experimental' && e.id === envSelectedId ? ' selected' : '';
            html += '<button type="button" class="env-pill' + selectedCls +
                '" data-env-experimental="' + envEscape(e.id) + '">' +
                envEscape(e.label) +
                '</button>';
        }
        envExperimentalRowEl.innerHTML = html;
    }

    function envSelectCasino(id) {
        var casino = envFindCasino(id);
        if (!casino) return;
        envSelectedKind = 'casino';
        envSelectedId = id;

        // Prefill rule: only fill empty fields. If QA has already typed
        // something into a field, don't clobber it on re-selection.
        // playerId prefers the current session's live playerId (from
        // appStore.settings.playerId) over the casino default — matches the
        // "you're already logged in here" intent.
        if (envPlayerIdEl && envPlayerIdEl.value.trim() === '') {
            var live = environmentState.currentPlayerId;
            envPlayerIdEl.value = live && live.length > 0 ? live : (casino.defaultPlayerId || '');
        }

        envRenderGrid();
        envRenderExperimentalRow();
        envApplyFieldVisibility();
        envSyncLaunchButton();
        envUpdateHint();
    }

    function envSelectExperimental(id) {
        var exp = envFindExperimental(id);
        if (!exp) return;
        envSelectedKind = 'experimental';
        envSelectedId = id;

        envRenderGrid();
        envRenderExperimentalRow();
        envApplyFieldVisibility();
        envSyncLaunchButton();
        envUpdateHint();
    }

    function envBuildUrl() {
        if (envSelectedKind === 'casino') {
            var casino = envFindCasino(envSelectedId);
            if (!casino || !casino.gameClientUrl) return null;

            // Param order mirrors the canonical example URL QA shared. Empty
            // session creds are *omitted entirely* (no &sessionToken=) — done
            // via explicit "if (val) params.set(...)" because URLSearchParams
            // otherwise writes the key with an empty value.
            var params = new URLSearchParams();
            params.set('host', casino.host);
            var st = envSessionTokenEl ? envSessionTokenEl.value.trim() : '';
            if (st) params.set('sessionToken', st);
            params.set('casinoId', envSelectedId);
            params.set('playerName', environmentState.currentPlayerName || '');
            params.set('playerId', envPlayerIdEl ? envPlayerIdEl.value.trim() : '');
            params.set('gameKey', envGameKeyEl ? envGameKeyEl.value.trim() : '');
            params.set('currencyCode', casino.currencyCode);
            params.set('protocol', 'https');
            params.set('language', environmentState.currentLanguage || 'en');
            params.set('gameId', environmentState.currentGameId || '');
            params.set('multiplayer', environmentState.currentMultiplayer || 'false');
            params.set('demo', environmentState.currentDemo || 'false');

            // gameClientUrl already ends with a trailing slash — append "?" directly.
            return casino.gameClientUrl + '?' + params.toString();
        }
        if (envSelectedKind === 'experimental') {
            var exp = envFindExperimental(envSelectedId);
            if (!exp || !exp.gameClientUrl) return null;
            // Experimentals take only a gameKey. Always emit it — even empty —
            // so the URL ends with a literal "?gameKey=" matching QA's example.
            var gk = envGameKeyEl ? envGameKeyEl.value.trim() : '';
            return exp.gameClientUrl + '?gameKey=' + encodeURIComponent(gk);
        }
        return null;
    }

    function envLaunch() {
        var url = envBuildUrl();
        if (!url) {
            envUpdateHint();
            return;
        }
        var opened = window.open(url, '_blank');
        if (!opened && envLaunchHintEl) {
            envLaunchHintEl.textContent = 'Popup blocked — allow popups for this origin.';
        }
    }

    if (envGridEl) {
        envGridEl.addEventListener('click', function (event) {
            var target = event.target;
            while (target && target !== envGridEl) {
                if (target.getAttribute && target.getAttribute('data-env-casino')) {
                    envSelectCasino(target.getAttribute('data-env-casino'));
                    return;
                }
                target = target.parentNode;
            }
        });
    }
    if (envExperimentalRowEl) {
        envExperimentalRowEl.addEventListener('click', function (event) {
            var target = event.target;
            while (target && target !== envExperimentalRowEl) {
                if (target.getAttribute && target.getAttribute('data-env-experimental')) {
                    envSelectExperimental(target.getAttribute('data-env-experimental'));
                    return;
                }
                target = target.parentNode;
            }
        });
    }

    if (envGameKeyEl) envGameKeyEl.addEventListener('input', function () {
        envSyncLaunchButton();
        envUpdateHint();
    });
    if (envPlayerIdEl) envPlayerIdEl.addEventListener('input', function () {
        envSyncLaunchButton();
        envUpdateHint();
    });
    if (envLaunchBtnEl) envLaunchBtnEl.addEventListener('click', envLaunch);

    function renderEnvironmentTab() {
        envRenderGrid();
        envRenderExperimentalRow();
        envApplyFieldVisibility();
        envSyncLaunchButton();
        envUpdateHint();
    }

    // Initial paint + ask the lobby for the env catalog and prefill data.
    renderEnvironmentTab();
    send({ type: 'environment.requestState' });

    /* ── Jackpot Widgets tab ────────────────────────────────── */
    // Mirrors JackpotWidgetController's state. Every edit is pushed to the
    // lobby as a config patch; the controller is the single source of truth and
    // broadcasts back, so this object is only ever a cache for rendering.

    var jpwState = null;
    var jpwZipEl = document.getElementById('jpw-zip');
    var jpwZipStatusEl = document.getElementById('jpw-zip-status');
    var jpwApplyEl = document.getElementById('jpw-apply');
    var jpwApiEl = document.getElementById('jpw-api');
    var jpwTypeEl = document.getElementById('jpw-type');
    var jpwCurrencyEl = document.getElementById('jpw-currency');
    var jpwUnitsEl = document.getElementById('jpw-units');
    var jpwLanguageEl = document.getElementById('jpw-language');
    var jpwJackpotsEl = document.getElementById('jpw-jackpots');
    var jpwMockEl = document.getElementById('jpw-mock-toggle');
    var jpwStatusEl = document.getElementById('jpw-status');
    var jpwVersionEl = document.getElementById('jpw-version');
    var jpwFeedbackEl = document.getElementById('jpw-feedback');
    var jpwListsHintEl = document.getElementById('jpw-lists-hint');
    var jpwReloadHintEl = document.getElementById('jpw-reload-hint');

    // Currency is not baked into the bundle (unlike jackpots/variants/languages),
    // so the dropdown is a fixed QA list.
    var JPW_CURRENCIES = ['EUR', 'BGN', 'USD', 'GBP', 'RON', 'TRY', 'EGT'];

    function jpwSetConfig(patch) {
        send({ type: 'jackpotWidget.setConfig', payload: { patch: patch } });
    }

    // Options are rebuilt from the bundle's own lists. Before anything is
    // loaded the select holds just the current value, so nothing here has to
    // know what variants exist.
    function jpwFillSelect(el, values, current) {
        if (!el) return;
        var list = values && values.length ? values : (current ? [current] : []);
        var html = '';
        for (var i = 0; i < list.length; i++) {
            var v = String(list[i]);
            html += '<option value="' + escapeHtml(v) + '"' + (v === current ? ' selected' : '') + '>' + escapeHtml(v) + '</option>';
        }
        el.innerHTML = html;
        el.disabled = list.length <= 1;
    }

    function renderJackpotWidgetTab() {
        if (!jpwState || !jpwZipEl) return;

        var cfg = jpwState.config || {};
        var available = jpwState.available;

        // Focus guards: a state broadcast mid-typing would otherwise wipe the
        // half-typed value out from under the tester.
        if (document.activeElement !== jpwApiEl) jpwApiEl.value = cfg.apiHost || '';
        if (document.activeElement !== jpwUnitsEl) jpwUnitsEl.value = cfg.units || 100;

        // Currency dropdown: the fixed QA list only. A junk session currency is
        // not added; Apply reads whatever option is selected.
        jpwFillSelect(jpwCurrencyEl, JPW_CURRENCIES, cfg.currency);

        jpwFillSelect(jpwTypeEl, available ? available.types : null, cfg.type);
        jpwFillSelect(jpwLanguageEl, available ? available.languages : null, cfg.language);

        jpwMockEl.classList.toggle('active', !!cfg.mock);
        jpwMockEl.textContent = cfg.mock ? 'Mock data (no API)' : 'Live data';
        jpwApiEl.disabled = !!cfg.mock;

        // Zip status line + Apply gating: Apply only means something once a zip
        // has been unpacked and its bundle is ready.
        if (jpwZipStatusEl) {
            if (jpwState.status === 'loading' && !jpwState.loaded) {
                jpwZipStatusEl.textContent = 'Unpacking ' + (jpwState.zipName || 'zip') + ' \u2026';
            } else if (jpwState.loaded) {
                jpwZipStatusEl.textContent = 'Loaded ' + (jpwState.zipName || 'zip') + ' \u2014 v' + (jpwState.version || '?');
            } else {
                jpwZipStatusEl.textContent = 'No zip loaded.';
            }
        }
        if (jpwApplyEl) jpwApplyEl.disabled = !jpwState.loaded;

        // Jackpot picker. Nothing ticked means "every jackpot in the bundle",
        // which is the useful default for a first look.
        if (available && available.jackpots && available.jackpots.length) {
            if (jpwListsHintEl) jpwListsHintEl.textContent = 'No jackpot ticked means all of them.';
            var picked = cfg.jackpots || [];
            var rows = '';
            for (var j = 0; j < available.jackpots.length; j++) {
                var name = available.jackpots[j];
                var on = picked.indexOf(name) !== -1;
                rows += '<label class="jp-status-row" style="cursor:pointer;gap:8px">' +
                    '<input type="checkbox" data-jpw-jackpot="' + escapeHtml(name) + '"' + (on ? ' checked' : '') + '>' +
                    '<span class="jp-status-label" style="flex:1">' + escapeHtml(name) + '</span>' +
                    '</label>';
            }
            jpwJackpotsEl.innerHTML = rows;
        }

        jpwStatusEl.textContent = jpwState.error
            ? 'error'
            : (jpwState.status === 'ready' ? 'mounted on the lobby'
                : (jpwState.status === 'loading' ? 'working\u2026'
                    : (jpwState.loaded ? 'ready to mount' : 'idle')));
        jpwVersionEl.textContent = jpwState.version || '\u2014';

        jpwFeedbackEl.textContent = jpwState.error || '';
        jpwFeedbackEl.style.display = jpwState.error ? 'block' : 'none';

        if (jpwReloadHintEl) {
            jpwReloadHintEl.style.display = jpwState.everMounted ? 'block' : 'none';
        }
    }

    if (jpwZipEl) {
        jpwZipEl.addEventListener('change', function () {
            var file = jpwZipEl.files && jpwZipEl.files[0];
            if (!file) return;
            if (jpwZipStatusEl) jpwZipStatusEl.textContent = 'Reading ' + file.name + ' \u2026';
            // Read the bytes in the popup and hand them to the lobby, where the
            // controller unpacks and loads. ArrayBuffer survives structured
            // clone across the popup -> lobby postMessage.
            file.arrayBuffer().then(function (bytes) {
                send({ type: 'jackpotWidget.loadZip', payload: { name: file.name, bytes: bytes } });
            }, function () {
                if (jpwZipStatusEl) jpwZipStatusEl.textContent = 'Could not read ' + file.name + '.';
            });
        });
        jpwApiEl.addEventListener('change', function () {
            jpwSetConfig({ apiHost: jpwApiEl.value.trim() });
        });
        jpwCurrencyEl.addEventListener('change', function () {
            jpwSetConfig({ currency: jpwCurrencyEl.value });
        });
        jpwUnitsEl.addEventListener('change', function () {
            var units = parseInt(jpwUnitsEl.value, 10);
            // Empty or nonsense input is skipped rather than shipped as NaN;
            // the next broadcast repopulates the field.
            if (units > 0) jpwSetConfig({ units: units });
        });
        jpwTypeEl.addEventListener('change', function () {
            jpwSetConfig({ type: jpwTypeEl.value });
        });
        jpwLanguageEl.addEventListener('change', function () {
            jpwSetConfig({ language: jpwLanguageEl.value });
        });
        jpwMockEl.addEventListener('click', function () {
            jpwSetConfig({ mock: !(jpwState && jpwState.config && jpwState.config.mock) });
        });
        jpwJackpotsEl.addEventListener('change', function (event) {
            var box = event.target;
            if (!box || !box.getAttribute || !box.getAttribute('data-jpw-jackpot')) return;
            var picked = [];
            var boxes = jpwJackpotsEl.querySelectorAll('input[data-jpw-jackpot]');
            for (var i = 0; i < boxes.length; i++) {
                if (boxes[i].checked) picked.push(boxes[i].getAttribute('data-jpw-jackpot'));
            }
            jpwSetConfig({ jackpots: picked });
        });
        jpwApplyEl.addEventListener('click', function () {
            // Flush whatever is in the focused field first - clicking Apply
            // straight after typing would otherwise apply the old value.
            jpwSetConfig({
                apiHost: jpwApiEl.value.trim(),
                currency: jpwCurrencyEl.value,
            });
            send({ type: 'jackpotWidget.apply' });
        });
        document.getElementById('jpw-disable').addEventListener('click', function () {
            send({ type: 'jackpotWidget.disable' });
        });
    }

    send({ type: 'jackpotWidget.requestState' });

    /* ── Measure tab ───────────────────────────────────────────── */

    /* Mirror of the lobby MeasureController state; replaced wholesale on
     * every measure.state broadcast. Positions are CSS px of the lobby
     * viewport. The popup cannot convert to rem / % itself (it does not
     * know the lobby root font-size or viewport), so inputs stay in px and
     * the overlay renders the unit labels. */
    var msState = null;
    var MS_UNITS = [
        { unit: 'px', hint: 'Absolute CSS px - the same numbers as the code, regardless of device pixel ratio.' },
        { unit: 'rem', hint: 'Relative to the font-size of the lobby html element.' },
        { unit: 'em', hint: 'Relative to the font-size of the reference element (the last one wrapped with Alt+click). Falls back to rem until an element is wrapped.' },
        { unit: '%', hint: 'Percent of the lobby viewport along the measured axis (width for horizontal distances, height for vertical).' },
        { unit: 'vw', hint: 'Percent of the lobby viewport width.' },
        { unit: 'vh', hint: 'Percent of the lobby viewport height.' },
        { unit: 'dvh', hint: 'Percent of the dynamic viewport height - follows the iOS address bar as it shows and hides.' }
    ];
    /* On / off buttons - each flips one boolean field of the state. */
    var MS_TOGGLES = [
        { id: 'ms-overlay', field: 'overlayOn', on: 'Overlay on', off: 'Overlay off' },
        { id: 'ms-snap', field: 'snap', on: 'Snap to edges: on', off: 'Snap to edges: off' },
        { id: 'ms-highlight', field: 'highlightAligned', on: 'Highlight aligned: on', off: 'Highlight aligned: off' },
        { id: 'ms-ruler-visible', field: 'rulerVisible', on: 'Ruler shown', off: 'Ruler hidden' }
    ];
    var msReferenceEl = document.getElementById('ms-reference');
    var msUnitsEl = document.getElementById('ms-units');
    var msUnitHintEl = document.getElementById('ms-unit-hint');
    var msRulerXEl = document.getElementById('ms-ruler-x');
    var msRulerYEl = document.getElementById('ms-ruler-y');
    var msRulerLengthEl = document.getElementById('ms-ruler-length');
    var msGuideCountEl = document.getElementById('ms-guide-count');
    var msGuideListEl = document.getElementById('ms-guide-list');
    /* Guide ids currently rendered, joined. When unchanged, rows are patched
     * in place so a focused input is never rebuilt under the cursor. */
    var msRenderedGuideKey = '';

    /* Toggles and settings all go through one message; the lobby ignores unchanged values. */
    function msSet(change) {
        send({ type: 'measure.set', payload: { change: change } });
    }

    /* Focus guard: never overwrite an input the user is typing into. */
    function msSetInput(input, value) {
        if (!input || document.activeElement === input) return;
        var next = String(Math.round(value * 100) / 100);
        if (input.value !== next) input.value = next;
    }

    function msRenderGuides() {
        var guides = msState.guides;
        msGuideCountEl.textContent = String(guides.length);
        /* The wrap flag is in the key: moving a wrap guide makes it manual,
         * and the row's tag has to follow. */
        var key = guides.map(function (g) { return g.id + (g.wrap ? ':w' : ''); }).join('|');
        if (key !== msRenderedGuideKey) {
            msRenderedGuideKey = key;
            if (guides.length === 0) {
                msGuideListEl.innerHTML = '<div class="ms-empty">No guides yet.</div>';
                return;
            }
            var html = '';
            guides.forEach(function (g) {
                html +=
                    '<div class="ms-guide-row">' +
                        '<span class="ms-guide-axis">' + (g.axis === 'x' ? 'Vertical X' : 'Horizontal Y') +
                            (g.wrap ? ' <span class="ms-wrap-tag">wrap</span>' : '') + '</span>' +
                        '<span class="fg-xy-group">' +
                            '<input type="number" step="1" min="0" data-ms-guide-input="' + escapeHtml(g.id) + '" />' +
                            '<span class="fg-xy-unit">px</span>' +
                        '</span>' +
                        '<button type="button" data-ms-guide-remove="' + escapeHtml(g.id) + '" title="Remove guide">×</button>' +
                    '</div>';
            });
            msGuideListEl.innerHTML = html;
        }
        guides.forEach(function (g) {
            msSetInput(msGuideListEl.querySelector('input[data-ms-guide-input="' + g.id + '"]'), g.pos);
        });
    }

    function renderMeasureTab() {
        if (!msState) return;
        MS_TOGGLES.forEach(function (t) {
            var on = !!msState[t.field];
            t.el.classList.toggle('on', on);
            t.el.textContent = on ? t.on : t.off;
        });
        var html = '';
        MS_UNITS.forEach(function (u) {
            var selected = u.unit === msState.unit ? ' selected' : '';
            html += '<button type="button" class="env-pill' + selected + '" data-ms-unit="' +
                escapeHtml(u.unit) + '">' + escapeHtml(u.unit) + '</button>';
        });
        msUnitsEl.innerHTML = html;
        var active = MS_UNITS.filter(function (u) { return u.unit === msState.unit; })[0];
        msUnitHintEl.textContent = active ? active.hint : '';
        msSetInput(msRulerXEl, msState.ruler.x);
        msSetInput(msRulerYEl, msState.ruler.y);
        msSetInput(msRulerLengthEl, msState.ruler.length);
        /* The reference line only matters for em. */
        msReferenceEl.hidden = msState.unit !== 'em';
        var ref = msState.reference;
        msReferenceEl.textContent = ref
            ? 'Reference: ' + ref.label + ' - ' + Math.round(ref.width) + ' x ' + Math.round(ref.height) +
              'px, font ' + ref.fontSize + 'px. Alt+click another element to change it.'
            : 'No reference yet - Alt+click an element (or use Wrap element...) to measure against it.';
        msRenderGuides();
    }

    MS_TOGGLES.forEach(function (t) {
        t.el = document.getElementById(t.id);
        t.el.addEventListener('click', function () {
            var change = {};
            change[t.field] = !(msState && msState[t.field]);
            msSet(change);
        });
    });
    msUnitsEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-ms-unit]') : null;
        if (btn) msSet({ unit: btn.getAttribute('data-ms-unit') });
    });
    document.getElementById('ms-pick').addEventListener('click', function () {
        send({ type: 'measure.startPick' });
    });
    document.getElementById('ms-ruler-rotate').addEventListener('click', function () {
        if (!msState) return;
        send({
            type: 'measure.setRuler',
            payload: { patch: { orientation: msState.ruler.orientation === 'h' ? 'v' : 'h' } }
        });
    });
    document.getElementById('ms-ruler-reset').addEventListener('click', function () {
        send({ type: 'measure.resetRuler' });
    });

    /* Empty / partial input is skipped rather than shipping NaN; the next
     * broadcast refills the field once focus leaves. */
    function msSendRulerField(input, field) {
        var raw = parseFloat(input.value);
        if (!isFinite(raw)) return;
        var patch = {};
        patch[field] = raw;
        send({ type: 'measure.setRuler', payload: { patch: patch } });
    }
    msRulerXEl.addEventListener('input', function () { msSendRulerField(msRulerXEl, 'x'); });
    msRulerYEl.addEventListener('input', function () { msSendRulerField(msRulerYEl, 'y'); });
    /* Length on change, not input - typing 300 would otherwise flash the
     * ruler through the 40px minimum for the 3 and 30 keystrokes. */
    msRulerLengthEl.addEventListener('change', function () { msSendRulerField(msRulerLengthEl, 'length'); });

    document.getElementById('ms-add-h').addEventListener('click', function () {
        send({ type: 'measure.addGuide', payload: { axis: 'y' } });
    });
    document.getElementById('ms-add-v').addEventListener('click', function () {
        send({ type: 'measure.addGuide', payload: { axis: 'x' } });
    });
    document.getElementById('ms-clear').addEventListener('click', function () {
        send({ type: 'measure.clearGuides' });
    });
    document.getElementById('ms-clear-saved').addEventListener('click', function () {
        if (confirm('Delete the saved Measure state (guides, ruler position, unit, toggles) and reset to defaults?')) {
            send({ type: 'measure.clearSaved' });
        }
    });
    msGuideListEl.addEventListener('input', function (e) {
        var input = e.target;
        var id = input && input.getAttribute ? input.getAttribute('data-ms-guide-input') : null;
        if (!id) return;
        var pos = parseFloat(input.value);
        if (isFinite(pos)) send({ type: 'measure.setGuide', payload: { id: id, pos: pos } });
    });
    msGuideListEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-ms-guide-remove]') : null;
        if (btn) send({ type: 'measure.removeGuide', payload: { id: btn.getAttribute('data-ms-guide-remove') } });
    });

    send({ type: 'measure.requestState' });

    /* ── Figma tab ─────────────────────────────────────────────── */

    var fgDropZoneEl = document.getElementById('fg-drop-zone');
    var fgEmptyEl = document.getElementById('fg-empty');
    var fgLoadedEl = document.getElementById('fg-loaded');
    var fgThumbEl = document.getElementById('fg-thumb');
    var fgDimsEl = document.getElementById('fg-dims');
    var fgRenderedEl = document.getElementById('fg-rendered');
    var fgClearBtn = document.getElementById('fg-clear');
    var fgStatusEl = document.getElementById('fg-status');
    var fgOpacityEl = document.getElementById('fg-opacity');
    var fgOpacityValueEl = document.getElementById('fg-opacity-value');
    var fgResetEl = document.getElementById('fg-reset');
    var fgXEl = document.getElementById('fg-x');
    var fgYEl = document.getElementById('fg-y');
    var fgScaleEl = document.getElementById('fg-scale');

    /* Lightweight mirror of FigmaState. The lobby is authoritative; this
     * gets overwritten on every figma.state broadcast. */
    var figmaState = {
        hasImage: false,
        overlayOn: false,
        opacity: 0.5,
        position: { x: 0, y: 0 },
        scale: 1,
    };
    /* Image bytes mirror — populated by figma.image broadcasts only. */
    var figmaImage = null;
    /* Cached natural dimensions for the current image so the dims label
     * doesn't have to wait for a re-decode on every render. */
    var figmaDims = null;

    function fgSetStatus(msg, tone) {
        if (!fgStatusEl) return;
        fgStatusEl.textContent = msg || '';
        if (tone) {
            fgStatusEl.setAttribute('data-tone', tone);
        } else {
            fgStatusEl.removeAttribute('data-tone');
        }
    }

    function fgDecodeDims(image, cb) {
        if (!image) { cb(null); return; }
        try {
            var probe = new Image();
            probe.onload = function () {
                cb({ w: probe.naturalWidth, h: probe.naturalHeight });
            };
            probe.onerror = function () { cb(null); };
            probe.src = image;
        } catch (e) {
            cb(null);
        }
    }

    function renderFigmaTab() {
        if (fgDropZoneEl) {
            fgDropZoneEl.setAttribute('data-empty', figmaState.hasImage ? '0' : '1');
        }
        if (fgEmptyEl) {
            fgEmptyEl.hidden = !!figmaState.hasImage;
        }
        if (fgLoadedEl) {
            fgLoadedEl.hidden = !figmaState.hasImage;
        }
        if (fgThumbEl) {
            if (figmaImage) {
                if (fgThumbEl.getAttribute('src') !== figmaImage) {
                    fgThumbEl.setAttribute('src', figmaImage);
                }
            } else {
                fgThumbEl.removeAttribute('src');
            }
        }
        if (fgDimsEl) {
            if (figmaDims) {
                fgDimsEl.textContent = figmaDims.w + ' × ' + figmaDims.h + ' px (natural)';
            } else {
                fgDimsEl.textContent = figmaState.hasImage ? 'measuring…' : '— × —';
            }
        }
        if (fgRenderedEl) {
            /* Show what natural * scale actually produces so QA doesn't
             * have to do the math in their head. Hidden until dims are
             * known — the second line would just be noise otherwise. */
            if (figmaDims && figmaState.hasImage) {
                var scaleNum = Number(figmaState.scale);
                if (!isFinite(scaleNum) || scaleNum <= 0) scaleNum = 1;
                var rW = Math.round(figmaDims.w * scaleNum);
                var rH = Math.round(figmaDims.h * scaleNum);
                var pctText = Math.round(scaleNum * 100);
                fgRenderedEl.textContent =
                    'Renders at ' + rW + ' × ' + rH + ' px (' + pctText + '%)';
                fgRenderedEl.hidden = false;
            } else {
                fgRenderedEl.textContent = '';
                fgRenderedEl.hidden = true;
            }
        }
        if (fgOpacityEl) {
            var pct = Math.round((figmaState.opacity || 0) * 100);
            if (Number(fgOpacityEl.value) !== pct) fgOpacityEl.value = String(pct);
        }
        if (fgOpacityValueEl) {
            fgOpacityValueEl.textContent = Math.round((figmaState.opacity || 0) * 100) + '%';
        }
        if (fgResetEl) {
            var enabled = !!figmaState.hasImage && !!figmaState.overlayOn;
            fgResetEl.disabled = !enabled;
            fgResetEl.style.opacity = enabled ? '' : '0.5';
            fgResetEl.style.cursor = enabled ? 'pointer' : 'not-allowed';
        }
        /* Sync X/Y/Scale inputs from authoritative state, but skip the
         * input the user is currently typing into so we don't clobber
         * mid-edit values (e.g. an in-flight broadcast caused by the
         * user's own typing). */
        var pos = figmaState.position || { x: 0, y: 0 };
        var xyEnabled = !!figmaState.overlayOn;
        if (fgXEl) {
            if (document.activeElement !== fgXEl) {
                var nextX = String(Math.round(pos.x || 0));
                if (fgXEl.value !== nextX) fgXEl.value = nextX;
            }
            fgXEl.disabled = !xyEnabled;
        }
        if (fgYEl) {
            if (document.activeElement !== fgYEl) {
                var nextY = String(Math.round(pos.y || 0));
                if (fgYEl.value !== nextY) fgYEl.value = nextY;
            }
            fgYEl.disabled = !xyEnabled;
        }
        if (fgScaleEl) {
            if (document.activeElement !== fgScaleEl) {
                var scaleVal = Number(figmaState.scale);
                if (!isFinite(scaleVal) || scaleVal <= 0) scaleVal = 1;
                var nextScale = String(Math.round(scaleVal * 100));
                if (fgScaleEl.value !== nextScale) fgScaleEl.value = nextScale;
            }
            fgScaleEl.disabled = !xyEnabled;
        }
    }

    /* Paste handling. Option 2 from the brief: drop-zone owns the paste
     * listener directly and requires the user to click into it first so
     * paste inside other tabs (e.g. someone Cmd+V'ing into a search
     * field) doesn't accidentally target the Figma overlay. */
    if (fgDropZoneEl) {
        fgDropZoneEl.addEventListener('click', function () {
            fgDropZoneEl.focus();
        });
        fgDropZoneEl.addEventListener('paste', function (e) {
            var cd = e.clipboardData;
            if (!cd) {
                fgSetStatus('Clipboard unavailable in this browser.', 'err');
                return;
            }
            var items = cd.items;
            if (!items || !items.length) {
                fgSetStatus('No image in clipboard — copy a frame from Figma first.', 'warn');
                return;
            }
            var imageItem = null;
            for (var i = 0; i < items.length; i++) {
                var it = items[i];
                if (it.kind === 'file' && it.type && it.type.indexOf('image/') === 0) {
                    imageItem = it;
                    break;
                }
            }
            if (!imageItem) {
                fgSetStatus('No image in clipboard — copy a frame from Figma first.', 'warn');
                return;
            }
            e.preventDefault();
            var blob = imageItem.getAsFile();
            if (!blob) {
                fgSetStatus('Could not read pasted image.', 'err');
                return;
            }
            fgSetStatus('Reading pasted image…');
            var reader = new FileReader();
            reader.onload = function () {
                var result = reader.result;
                if (typeof result !== 'string') {
                    fgSetStatus('Could not decode pasted image.', 'err');
                    return;
                }
                /* Decode natural dimensions BEFORE shipping the image to the
                 * lobby. The controller needs them to compute the default
                 * overlay size on first paste; if decode fails we still send
                 * the bytes but with 0/0 dimensions, and the controller
                 * preserves its current/initial size. */
                fgDecodeDims(result, function (dims) {
                    if (dims) {
                        send({
                            type: 'figma.setImage',
                            payload: {
                                image: result,
                                naturalWidth: dims.w,
                                naturalHeight: dims.h,
                            },
                        });
                        fgSetStatus('Pasted: ' + dims.w + ' × ' + dims.h + ' px');
                    } else {
                        send({
                            type: 'figma.setImage',
                            payload: {
                                image: result,
                                naturalWidth: 0,
                                naturalHeight: 0,
                            },
                        });
                        fgSetStatus('Pasted image (could not decode dimensions).', 'warn');
                    }
                });
            };
            reader.onerror = function () {
                fgSetStatus('Could not read pasted image.', 'err');
            };
            reader.readAsDataURL(blob);
        });
    }

    if (fgClearBtn) {
        fgClearBtn.addEventListener('click', function () {
            send({
                type: 'figma.setImage',
                payload: { image: null, naturalWidth: 0, naturalHeight: 0 },
            });
            fgSetStatus('');
        });
    }

    if (fgOpacityEl) {
        fgOpacityEl.addEventListener('input', function () {
            var v = Number(fgOpacityEl.value);
            if (!isFinite(v)) return;
            /* Optimistic local label update — the broadcast will reconcile. */
            if (fgOpacityValueEl) fgOpacityValueEl.textContent = Math.round(v) + '%';
            send({ type: 'figma.setOpacity', payload: { opacity: v / 100 } });
        });
    }

    if (fgResetEl) {
        fgResetEl.addEventListener('click', function () {
            if (fgResetEl.disabled) return;
            /* Combined op — the lobby resets position to (0,0) AND
             * recomputes size from the loaded image's aspect ratio against
             * the live lobby viewport width. Doing this as two separate
             * messages would force the popup to know lobby's innerWidth. */
            send({ type: 'figma.resetView' });
        });
    }

    /* X/Y number inputs — precise positioning escape hatch for DevTools
     * mobile-emulation mode where the titlebar drag is unreliable. The
     * controller clamps y>=0 and broadcasts state, which feeds back into
     * the inputs via renderFigmaTab() (focus-guarded). */
    function fgSendPosition() {
        if (!fgXEl || !fgYEl) return;
        var xRaw = parseInt(fgXEl.value, 10);
        var yRaw = parseInt(fgYEl.value, 10);
        /* Empty input → skip; don't ship NaN to the controller. The other
         * input may still be valid, but a partial update isn't meaningful
         * since setPosition takes both axes. */
        if (!isFinite(xRaw) || !isFinite(yRaw)) return;
        send({ type: 'figma.setPosition', payload: { x: xRaw, y: yRaw } });
    }
    if (fgXEl) {
        fgXEl.addEventListener('input', fgSendPosition);
    }
    if (fgYEl) {
        fgYEl.addEventListener('input', fgSendPosition);
    }

    /* Scale number input — single multiplier on the image's natural
     * dimensions. Aspect ratio is preserved by construction (can't be
     * stretched). Same focus-guard / partial-skip pattern as X/Y.
     * Controller clamps to [0.1, 5]; out-of-range or non-finite input
     * is dropped on the popup side so mid-type "9" doesn't ship a 9%
     * frame to the lobby before the user finishes typing "90". */
    function fgSendScale() {
        if (!fgScaleEl) return;
        var raw = parseInt(fgScaleEl.value, 10);
        if (!isFinite(raw)) return;
        if (raw < 10 || raw > 500) return;
        send({ type: 'figma.setScale', payload: { scale: raw / 100 } });
    }
    if (fgScaleEl) {
        fgScaleEl.addEventListener('input', fgSendScale);
    }

    function downloadCsv(csv, filename) {
        var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /* ── Sentry tab rendering ───────────────────────────────────── */

    function snFormatValue(value) {
        if (value === '' || value === undefined || value === null) return '(empty)';
        return String(value);
    }

    function snRenderKvRows(targetEl, data, prefixToStrip) {
        if (!targetEl) return;
        var entries = Object.entries(data || {});
        if (entries.length === 0) {
            targetEl.innerHTML = '<div class="sn-empty">no data</div>';
            return;
        }
        var html = '';
        entries.forEach(function (entry) {
            var key = entry[0];
            var value = entry[1];
            var displayKey = prefixToStrip && key.indexOf(prefixToStrip) === 0
                ? key.slice(prefixToStrip.length)
                : key;
            var displayValue = snFormatValue(value);
            html +=
                '<div class="jp-status-row">' +
                  '<span class="jp-status-label">' + escapeHtml(displayKey) + '</span>' +
                  '<span class="jp-status-value">' + escapeHtml(displayValue) + '</span>' +
                '</div>';
        });
        targetEl.innerHTML = html;
    }

    function snRenderEventOrder() {
        if (!snEventOrderEl) return;
        var order = sentryState.eventOrder || [];
        var slice = order.slice(-20);
        snEventCountEl.textContent = String(order.length);
        if (slice.length === 0) {
            snEventOrderEl.innerHTML = '<div class="sn-empty">no events tracked yet</div>';
            return;
        }
        var html = '';
        slice.forEach(function (event) {
            var label = String(event)
                .replace(/^nav_tab_/, '')
                .replace(/_/g, ' ');
            html += '<span class="sn-pill sn-event-pill">' + escapeHtml(label) + '</span>';
        });
        snEventOrderEl.innerHTML = html;
    }

    function snRenderNavTabs() {
        if (!snNavTabsEl) return;
        var entries = Object.entries(sentryState.navTabs || {});
        if (entries.length === 0) {
            snNavTabsEl.innerHTML = '<div class="sn-empty">no navigation clicks tracked</div>';
            return;
        }
        var html = '';
        entries.forEach(function (entry) {
            html +=
                '<div class="jp-status-row">' +
                  '<span class="jp-status-label">' + escapeHtml(entry[0]) + '</span>' +
                  '<span class="jp-status-value">' + escapeHtml(String(entry[1])) + '</span>' +
                '</div>';
        });
        snNavTabsEl.innerHTML = html;
    }

    function snRenderGameLaunches() {
        if (!snGameLaunchesEl) return;
        var launches = sentryState.gameLaunches || [];
        if (launches.length === 0) {
            snGameLaunchesEl.innerHTML = '<div class="sn-empty">no game clicks tracked</div>';
            return;
        }
        var html = '';
        launches.forEach(function (launch) {
            var viewClass = launch.viewMode === 'MULTI' ? 'multi'
                : launch.viewMode === 'SINGLE' ? 'single'
                : 'unknown';
            html +=
                '<div class="jp-status-row">' +
                  '<span class="jp-status-label">' +
                    '<span class="sn-key-tags">' +
                      '<span class="sn-tag ' + viewClass + '">' + escapeHtml(launch.viewMode) + '</span>' +
                      '<span class="sn-tag section">' + escapeHtml(launch.section) + '</span>' +
                    '</span>' +
                    escapeHtml(launch.game) +
                  '</span>' +
                  '<span class="jp-status-value">' + escapeHtml(String(launch.count)) + '</span>' +
                '</div>';
        });
        snGameLaunchesEl.innerHTML = html;
    }

    function snRenderJackpotStatsOpens() {
        if (!snJackpotStatsOpensEl) return;
        var entries = Object.entries(sentryState.jackpotStatsOpens || {});
        if (entries.length === 0) {
            snJackpotStatsOpensEl.innerHTML = '<div class="sn-empty">no jackpot statistics opens tracked</div>';
            return;
        }
        var html = '';
        entries.forEach(function (entry) {
            var name = String(entry[0]).replace(/_/g, ' ');
            html +=
                '<div class="jp-status-row">' +
                  '<span class="jp-status-label">' + escapeHtml(name) + '</span>' +
                  '<span class="jp-status-value">' + escapeHtml(String(entry[1])) + '</span>' +
                '</div>';
        });
        snJackpotStatsOpensEl.innerHTML = html;
    }

    function snRenderOtherClicks() {
        if (!snOtherClicksEl) return;
        var entries = Object.entries(sentryState.otherClicks || {});
        if (entries.length === 0) {
            snOtherClicksEl.innerHTML = '<div class="sn-empty">no other clicks tracked</div>';
            return;
        }
        var html = '';
        entries.forEach(function (entry) {
            html +=
                '<div class="jp-status-row">' +
                  '<span class="jp-status-label">' + escapeHtml(entry[0]) + '</span>' +
                  '<span class="jp-status-value">' + escapeHtml(String(entry[1])) + '</span>' +
                '</div>';
        });
        snOtherClicksEl.innerHTML = html;
    }

    function snRenderSearchQueries() {
        if (!snSearchQueriesEl) return;
        var queries = sentryState.searchQueries || [];
        if (queries.length === 0) {
            snSearchQueriesEl.innerHTML = '<div class="sn-empty">no search queries staged</div>';
            return;
        }
        var html = '';
        queries.forEach(function (q) {
            html += '<span class="sn-pill">' + escapeHtml(q) + '</span>';
        });
        snSearchQueriesEl.innerHTML = html;
    }

    function snRenderSectionNavHistory() {
        if (!snSectionNavHistoryEl) return;
        var raw = (sentryState.sectionNavigation || {})['egt.lobby.section_navigation.history'];
        var rawStr = typeof raw === 'string' ? raw : '';
        if (!rawStr) {
            snSectionNavHistoryEl.innerHTML = '<div class="sn-empty">no history tracked yet</div>';
            return;
        }
        var entries = rawStr.split(',');
        var html = '';
        entries.forEach(function (entry) {
            html += '<span class="sn-pill">' + escapeHtml(entry) + '</span>';
        });
        snSectionNavHistoryEl.innerHTML = html;
    }

    function renderSentryTab() {
        snRenderEventOrder();
        snRenderNavTabs();
        snRenderGameLaunches();
        snRenderJackpotStatsOpens();
        snRenderOtherClicks();
        snRenderKvRows(snUserDataEl, sentryState.userData, 'egt.lobby.user_data.');
        snRenderKvRows(snTransactionEl, sentryState.transaction, 'egt.lobby.transactions.game_client.');
        var sectionNavKv = {};
        Object.keys(sentryState.sectionNavigation || {}).forEach(function (k) {
            if (k !== 'egt.lobby.section_navigation.history') {
                sectionNavKv[k] = sentryState.sectionNavigation[k];
            }
        });
        snRenderKvRows(snSectionNavigationEl, sectionNavKv, 'egt.lobby.section_navigation.');
        snRenderSectionNavHistory();
        snRenderKvRows(snDynamicSectionsEl, sentryState.dynamicSections, 'egt.lobby.dynamic_sections.');
        snRenderSearchQueries();
        if (snTraceIdEl) {
            if (sentryState.activeSpanTraceId) {
                snTraceIdEl.textContent = sentryState.activeSpanTraceId;
                snTraceIdEl.className = 'jp-status-value on';
            } else {
                snTraceIdEl.textContent = '(no active span)';
                snTraceIdEl.className = 'jp-status-value';
            }
        }
        if (snTotalEl) snTotalEl.textContent = String(sentryState.total || 0);
        if (snFooterEl) {
            var ts = sentryState.lastUpdate
                ? new Date(sentryState.lastUpdate).toLocaleTimeString()
                : '—';
            snFooterEl.textContent = 'Last refresh: ' + ts + '  ·  auto-refresh every 2s';
        }
    }

    if (snSyncBtn) {
        snSyncBtn.addEventListener('click', function () {
            if (window.confirm('Force-attach the current tracker data onto the active Sentry span?')) {
                send({ type: 'sentry.syncToSentry' });
            }
        });
    }
    if (snClearBtn) {
        snClearBtn.addEventListener('click', function () {
            if (window.confirm('Clear all staged tracker data? This wipes events, transaction, user data, section navigation, dynamic sections, and search queries.')) {
                send({ type: 'sentry.clearAll' });
            }
        });
    }

    /* ── Jira tab rendering ────────────────────────────────── */

    /* Author renderer — returns a Teams deep-link anchor for allowlisted
     * corporate emails, or plain escaped text otherwise. The row-click
     * handler already excludes a.ji-author-link from row toggle, mirroring
     * the .ji-key and .ji-pr-link convention. */
    function renderAuthor(author) {
        var name = (author && author.name) || '';
        var email = (author && author.email) || '';
        var atIdx = email.lastIndexOf('@');
        var domain = atIdx === -1 ? '' : email.slice(atIdx + 1).toLowerCase();
        var canLink = !!email && TEAMS_ALLOWED_DOMAINS.indexOf(domain) !== -1;
        if (!canLink) return escapeHtml(name);
        var href = TEAMS_CHAT_BASE_URL + encodeURIComponent(email);
        return '<a class="ji-author-link" href="' + escapeHtml(href) +
            '" target="_blank" rel="noopener noreferrer">' +
            escapeHtml(name) + '</a>';
    }

    function renderJiraTab() {
        var tickets = jiraState.tickets || [];
        if (jiCountEl) jiCountEl.textContent = String(tickets.length);
        if (jiRangeHintEl) {
            jiRangeHintEl.textContent = jiraState.previousVersion
                ? 'Commits since ' + jiraState.previousVersion + '.'
                : 'Build metadata unavailable — no prior version-bump commit found.';
        }
        /* "Open all in Jira" — a single JQL search listing every build ticket
         * WITH names. Hidden when there are no tickets (an empty in() would
         * open a broken search). Built from the live ticket keys. */
        if (jiAllLinkEl) {
            if (tickets.length > 0) {
                var jql = 'issuekey in (' +
                    tickets.map(function (t) { return t.key; }).join(', ') +
                    ')';
                jiAllLinkEl.setAttribute('href', JIRA_SEARCH_BASE_URL + encodeURIComponent(jql));
                jiAllLinkEl.removeAttribute('hidden');
            } else {
                jiAllLinkEl.removeAttribute('href');
                jiAllLinkEl.setAttribute('hidden', '');
            }
        }
        /* Reload the verified set when the active previousVersion changes so
         * stale ticks from a prior rc don't leak into the new one. */
        if (jiraState.previousVersion !== jiVerifiedLoadedForVersion) {
            jiLoadVerifiedForVersion(jiraState.previousVersion);
        }
        /* Hot-areas chip strip — empty list collapses via CSS. */
        if (jiAreasEl) {
            var areas = jiraState.fileAreas || [];
            var areasHtml = '';
            areas.forEach(function (a) {
                areasHtml +=
                    '<span class="ji-area-chip">' +
                      escapeHtml(a.label) +
                      ' <span class="ji-area-count">' + String(a.count) + '</span>' +
                    '</span>';
            });
            jiAreasEl.innerHTML = areasHtml;
        }
        /* Verified counter — only meaningful when there's at least one
         * ticket AND we have a previousVersion to scope persistence by. */
        if (jiVerifiedLineEl) {
            if (tickets.length > 0 && jiraState.previousVersion) {
                var verifiedCount = 0;
                tickets.forEach(function (t) {
                    if (jiVerifiedTickets[t.key]) verifiedCount++;
                });
                jiVerifiedLineEl.textContent = verifiedCount + ' / ' + tickets.length + ' verified';
            } else {
                jiVerifiedLineEl.textContent = '';
            }
        }
        if (!jiListEl) return;
        if (tickets.length === 0) {
            jiListEl.innerHTML = '<div class="ji-empty">No tickets in this build.</div>';
            return;
        }
        // Forget expanded entries for tickets that no longer exist in the
        // freshly broadcast list, so the Set doesn't grow unbounded.
        var liveKeys = Object.create(null);
        tickets.forEach(function (t) { liveKeys[t.key] = true; });
        for (var k in jiExpandedTickets) {
            if (!liveKeys[k]) delete jiExpandedTickets[k];
        }
        var html = '';
        tickets.forEach(function (t) {
            var subjects = (t.subjects || []).map(function (s) {
                return '<div>' + escapeHtml(s) + '</div>';
            }).join('');
            var href = JIRA_BASE_URL + encodeURIComponent(t.key);
            var files = t.files || [];
            var commitsArr = t.commits || [];
            var expanded = !!jiExpandedTickets[t.key];
            var chevron = expanded ? '▾' : '▸';
            var fileCountLabel = files.length === 0
                ? '(no files listed)'
                : '(' + files.length + ' file' + (files.length === 1 ? '' : 's') + ')';
            /* Authors — "by Alice", "by Alice, Bob", or "by Alice, Bob +N more".
             * Each visible name renders as a Teams 1:1 deep-link when the
             * author email is in an allowlisted corporate domain; otherwise
             * (or in the back-compat empty-email case) it falls back to
             * plain text. The trailing "+N more" stays plain text. */
            var authorsHtml = '';
            var authors = t.authors || [];
            if (authors.length > 0) {
                var visible = authors.length >= 4 ? authors.slice(0, 2) : authors;
                var rendered = visible.map(renderAuthor).join(', ');
                var authorLabel = 'by ' + rendered;
                if (authors.length >= 4) {
                    authorLabel += ' +' + (authors.length - 2) + ' more';
                }
                authorsHtml = '<span class="ji-authors">' + authorLabel + '</span>';
            }
            /* One-line commit summary (collapsed view). */
            var commitsSummary = commitsArr.map(function (c) { return escapeHtml(c.hash); }).join(', ');
            /* Expanded files + commits. */
            var expandedHtml = '';
            if (expanded) {
                if (files.length > 0) {
                    expandedHtml += '<ul class="ji-files">';
                    files.forEach(function (f) {
                        expandedHtml += '<li>' + escapeHtml(f) + '</li>';
                    });
                    expandedHtml += '</ul>';
                }
                if (commitsArr.length > 0) {
                    expandedHtml += '<ul class="ji-commit-list">';
                    commitsArr.forEach(function (c) {
                        var hashHref = BITBUCKET_BASE_URL + '/commits/' + encodeURIComponent(c.hash);
                        var hashHtml = '<a href="' + escapeHtml(hashHref) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(c.hash) + '</a>';
                        var prHtml = '';
                        if (typeof c.pr === 'number' && c.pr > 0) {
                            var prHref = BITBUCKET_BASE_URL + '/pull-requests/' + encodeURIComponent(String(c.pr));
                            prHtml = ' <a class="ji-pr-link" href="' + escapeHtml(prHref) + '" target="_blank" rel="noopener noreferrer">(PR #' + String(c.pr) + ')</a>';
                        }
                        expandedHtml += '<li>' + hashHtml + prHtml + '</li>';
                    });
                    expandedHtml += '</ul>';
                }
            }
            var isVerified = !!jiVerifiedTickets[t.key];
            var rowClass = 'ji-row' + (isVerified ? ' verified' : '');
            var verifiedDisabled = jiraState.previousVersion ? '' : ' disabled';
            html +=
                '<div class="' + rowClass + '" data-ji-key="' + escapeHtml(t.key) + '">' +
                  '<div class="ji-header" data-ji-toggle="1">' +
                    '<div class="ji-row-left">' +
                      '<span class="ji-verified-cell" data-ji-verified-cell="1">' +
                        '<input type="checkbox" data-ji-verified="1"' +
                          (isVerified ? ' checked' : '') + verifiedDisabled +
                          ' title="Mark this ticket verified for ' + escapeHtml(jiraState.previousVersion || 'this build') + '" />' +
                      '</span>' +
                      '<span class="ji-chevron">' + chevron + '</span>' +
                      '<a class="ji-key" href="' + escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' +
                        escapeHtml(t.key) +
                      '</a>' +
                    '</div>' +
                    '<div class="ji-meta">' +
                      '<div class="ji-subjects">' + subjects + '</div>' +
                      (commitsSummary ? '<div class="ji-commits">' + commitsSummary + '</div>' : '') +
                    '</div>' +
                    '<div class="ji-row-right">' +
                      '<span class="ji-filecount">' + fileCountLabel + '</span>' +
                      authorsHtml +
                    '</div>' +
                  '</div>' +
                  expandedHtml +
                '</div>';
        });
        jiListEl.innerHTML = html;
    }

    if (jiListEl) {
        /* Checkbox change — persist verified state, swap the row's class so
         * the strikethrough updates without a full re-render, and refresh
         * the counter. We avoid renderJiraTab() here because re-running it
         * would yank focus from the checkbox and feel janky on rapid ticks. */
        jiListEl.addEventListener('change', function (e) {
            var target = e.target;
            if (!target || !target.matches || !target.matches('input[data-ji-verified]')) return;
            var row = target.closest && target.closest('.ji-row');
            if (!row) return;
            var key = row.getAttribute('data-ji-key');
            if (!key) return;
            if (target.checked) {
                jiVerifiedTickets[key] = true;
                row.classList.add('verified');
            } else {
                delete jiVerifiedTickets[key];
                row.classList.remove('verified');
            }
            jiPersistVerifiedForVersion(jiraState.previousVersion);
            /* Counter is cheap to update independently. */
            if (jiVerifiedLineEl && jiraState.previousVersion) {
                var verifiedCount = 0;
                var tickets = jiraState.tickets || [];
                tickets.forEach(function (t) { if (jiVerifiedTickets[t.key]) verifiedCount++; });
                jiVerifiedLineEl.textContent = verifiedCount + ' / ' + tickets.length + ' verified';
            }
        });
        jiListEl.addEventListener('click', function (e) {
            var target = e.target;
            // Clicks on the checkbox, the Jira link, and the expanded
            // commit/PR links must NOT toggle the row.
            if (target && target.closest) {
                if (target.closest('a.ji-key')) return;
                if (target.closest('a.ji-author-link')) return;
                if (target.closest('input[data-ji-verified]')) return;
                if (target.closest('[data-ji-verified-cell]')) return;
                if (target.closest('.ji-commit-list a')) return;
            }
            var header = target && target.closest && target.closest('.ji-header');
            if (!header) return;
            var row = header.closest('.ji-row');
            if (!row) return;
            var key = row.getAttribute('data-ji-key');
            if (!key) return;
            if (jiExpandedTickets[key]) {
                delete jiExpandedTickets[key];
            } else {
                jiExpandedTickets[key] = true;
            }
            renderJiraTab();
        });
    }

    /* ── Campaigns (Tournaments mock) tab ────────────────────────── */
    /*
     * Three-state UI driven by campaignsState.mockMode + presence of
     * tournaments. The whole sub-tab is rebuilt via innerHTML on every
     * state broadcast — same approach as the rest of the popup. Module
     * state (wizard, fetch panel toggle, edited-id set, last error) lives
     * in plain vars below.
     */

    var TOURNAMENT_LEVELS = ['GOLD', 'PURPLE', 'RED', 'BLUE', 'GREEN', 'MADNESS'];
    var TOURNAMENT_FORMULAS = [
        'WINNINGS_PLUS_NUMBER_OF_SPINS',
        'WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS',
        'BET_IN_COINS',
        'WINNINGS_D_BETS',
        'WINNINGS_D_BETS_X_100',
        'WINNINGS_PLUS_BETS',
        'WINNINGS_X_5_PLUS_NUMBER_OF_SPINS',
        'BET_IN_COINS_COEF',
        'WINNINGS_PLUS_BETS_D_100',
        'WINNINGS_X_5_PLUS_BETS',
        'WINNINGS_D_BETS_X_100_PLUS_SPINS'
    ];
    var PRIZE_TYPES = ['MONEY', 'GIFT_SPIN', 'ITEM'];

    var cmRootEl = document.getElementById('cm-tournaments-root');
    var cmTnFeedbackEl = document.getElementById('cm-tn-feedback');

    var campaignsState = { mockMode: false, mocks: [], tournaments: [], defaultBaseCurrency: 'EUR' };
    var cmFeedbackTimer = null;

    /* Module-scope UI state (NOT broadcast — local to popup). */
    var cmShowFetchPanel = true;        /* true while user hasn't dismissed the paste UI */
    var cmLastFetchError = null;
    var cmExpandedRowId = null;          /* which list row is expanded */
    var cmEditedIds = Object.create(null); /* id -> true once a real entry has been patched */
    var cmWizardOpen = false;
    var cmWizardStep = 1;
    var cmWizardForm = null;             /* populated from buildDefaultForm()-ish defaults */

    function cmShowFeedback(text, kind) {
        if (!cmTnFeedbackEl) return;
        cmTnFeedbackEl.textContent = text;
        cmTnFeedbackEl.className = 'jp-feedback show ' + (kind || 'ok');
        if (cmFeedbackTimer) {
            window.clearTimeout(cmFeedbackTimer);
        }
        cmFeedbackTimer = window.setTimeout(function () {
            if (cmTnFeedbackEl) {
                cmTnFeedbackEl.textContent = '';
                cmTnFeedbackEl.className = 'jp-feedback';
            }
        }, 3500);
    }

    var CM_ACTIVE_PLAYERS_MAX_DIGITS = 9;

    /* Inline validation message, rendered hidden directly above an Active
     * players input and revealed by cmEnforceActivePlayersLimit. */
    function cmActivePlayersErrorMarkup() {
        return '<div data-cm-active-players-error class="hint" style="display:none;color:var(--status-error);margin:0 0 4px">'
            + 'Max ' + CM_ACTIVE_PLAYERS_MAX_DIGITS + ' digits'
            + '</div>';
    }

    /* Active players is a plain text input (no maxlength attribute) so an over-long
     * entry can be reported instead of silently swallowed by the browser.
     * Trim it back to the cap and show the message above the field. */
    function cmEnforceActivePlayersLimit(el) {
        var value = String(el.value || '');
        var exceeded = value.length > CM_ACTIVE_PLAYERS_MAX_DIGITS;
        if (exceeded) {
            el.value = value.slice(0, CM_ACTIVE_PLAYERS_MAX_DIGITS);
        }
        var parent = el.parentNode;
        var errorEl = parent && parent.querySelector ? parent.querySelector('[data-cm-active-players-error]') : null;
        if (errorEl) {
            errorEl.style.display = exceeded ? 'block' : 'none';
        }
    }

    function cmDefaultWizardForm() {
        var cur = campaignsState.defaultBaseCurrency || 'EUR';
        return {
            name: 'QA Test Tournament',
            terms: '',
            level: 'PURPLE',
            formula: 'WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS',
            minSpinNumber: 25,
            minStake: 25,
            baseCurrency: cur,
            promoStartOffsetMin: -5,
            startOffsetMin: 0,
            endOffsetMin: 240,
            prizes: [{ place: 1, type: 'MONEY', name: '100', currencyCode: cur }],
            prizeFundOverride: null,
            buyBonusOnly: false,
            prizePlaces: 25,
            activePlayers: 25,
        };
    }

    function cmAutoSumPrizeFundFor(prizes) {
        var sum = 0;
        for (var i = 0; i < prizes.length; i++) {
            var p = prizes[i];
            if (p.type !== 'MONEY') continue;
            var n = Number(p.name);
            if (isFinite(n)) sum += n;
        }
        return sum;
    }

    /* ── Render helpers ───────────────────────────────────────────── */

    function cmSelectMarkup(name, options, current, editField) {
        var editAttr = editField ? ' data-cm-edit-field="' + editField + '"' : '';
        var html = '<select name="' + name + '"' + editAttr + '>';
        for (var i = 0; i < options.length; i++) {
            var v = options[i];
            html += '<option value="' + v + '"' + (v === current ? ' selected' : '') + '>' + v + '</option>';
        }
        return html + '</select>';
    }

    function cmRenderEmptyState() {
        return ''
            + '<div class="section" style="text-align:center;padding:24px 16px">'
            +     '<h2 style="margin-bottom:8px">Mocking is off</h2>'
            +     '<button type="button" class="primary" data-cm-action="enable-mocking">Enable mocking</button>'
            +     '<div class="hint" style="margin-top:10px">Pauses polling and lets you fetch + edit + add tournaments.</div>'
            + '</div>';
    }

    function cmRenderBanner() {
        return ''
            + '<div class="hint" style="background:var(--accent-bg);border:1px solid var(--accent-dim);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:10px;display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +     '<span>Mocking enabled — polling paused. Edits and mocks survive until you disable mocking.</span>'
            +     '<button type="button" class="danger" data-cm-action="disable-mocking">Disable mocking</button>'
            + '</div>';
    }

    function cmRenderFetchPanel() {
        var errorHtml = cmLastFetchError
            ? '<div class="hint" style="background:var(--surface-2);border-left:3px solid var(--status-error);color:var(--status-error);border-radius:var(--radius-sm);padding:10px 14px;margin-bottom:10px">' + escapeHtml(cmLastFetchError) + '</div>'
            : '';
        return ''
            + '<div class="section">'
            +     '<h2>Fetch tournaments</h2>'
            +     errorHtml
            +     '<div class="bo-edit-field">'
            +         '<label>Paste session string <span class="bo-field-hint">format: sessionKey=...&playerId=...</span></label>'
            +         '<input type="text" id="cm-fetch-input" autocomplete="off" placeholder="sessionKey=abc&playerId=123" />'
            +     '</div>'
            +     '<div class="row" style="margin-top:8px;gap:8px">'
            +         '<button type="button" class="primary" data-cm-action="fetch">Fetch tournaments</button>'
            +         '<button type="button" data-cm-action="skip-fetch">Skip — start adding mocks</button>'
            +     '</div>'
            + '</div>';
    }

    function cmDateInputMarkup(name, value) {
        // datetime-local needs YYYY-MM-DDTHH:mm. Slice off seconds/Z from
        // ISO strings; if the value is empty/invalid, hand back an empty input.
        var local = '';
        if (value) {
            var d = new Date(value);
            if (!isNaN(d.getTime())) {
                var pad = function (n) { return n < 10 ? '0' + n : String(n); };
                local = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
                    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
            }
        }
        return '<input type="datetime-local" data-cm-edit-field="' + name + '" value="' + escapeHtml(local) + '" />';
    }

    function cmRenderPrizeRows(prizes, prefix) {
        var html = '';
        for (var i = 0; i < prizes.length; i++) {
            var p = prizes[i];
            html += '<div class="bo-edit-field" data-' + prefix + '-row="' + i + '" style="display:flex;gap:6px;align-items:flex-end;flex-wrap:wrap">';
            html +=     '<label style="flex:0 0 70px">Place<input type="number" min="1" step="1" data-' + prefix + '-field="place" value="' + escapeHtml(String(p.place)) + '" style="width:60px" /></label>';
            html +=     '<label style="flex:0 0 110px">Type<select data-' + prefix + '-field="type">';
            for (var j = 0; j < PRIZE_TYPES.length; j++) {
                var pt = PRIZE_TYPES[j];
                html +=     '<option value="' + pt + '"' + (pt === p.type ? ' selected' : '') + '>' + pt + '</option>';
            }
            html +=     '</select></label>';
            html +=     '<label style="flex:1;min-width:120px">Name<input type="text" data-' + prefix + '-field="name" value="' + escapeHtml(p.name) + '" /></label>';
            html +=     '<label style="flex:0 0 90px">Currency<input type="text" data-' + prefix + '-field="currencyCode" value="' + escapeHtml(p.currencyCode || '') + '" style="width:70px" /></label>';
            html +=     '<button type="button" class="danger" data-' + prefix + '-remove="' + i + '">Remove</button>';
            html += '</div>';
        }
        return html;
    }

    function cmRenderEditForm(t) {
        // Inline editor for one tournament summary. All inputs carry
        // data-cm-edit-field=... so the click/input handler can dispatch
        // a single update message per blur. The form is fully controlled
        // by the broadcast state — re-render replaces whatever the user
        // typed since their last save. We accept that as the cost of
        // simplicity (matches the rest of the popup's strategy).
        var modeBuyBonus = !!t.buyBonusOnly;
        return ''
            + '<div class="cm-edit-form" style="padding:8px 0">'
            +     '<div class="bo-edit-field"><label>Name</label><input type="text" data-cm-edit-field="name" value="' + escapeHtml(t.name) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Terms</label><textarea data-cm-edit-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(t.terms || '') + '</textarea></div>'
            +     '<div class="bo-edit-field"><label>Level</label>' + cmSelectMarkup('cm-edit:level', TOURNAMENT_LEVELS, t.level, 'level') + '</div>'
            +     '<div class="bo-edit-field"><label>Formula</label>' + cmSelectMarkup('cm-edit:formula', TOURNAMENT_FORMULAS, t.formula, 'formula') + '</div>'
            +     '<div class="bo-edit-field"><label>Min spin</label><input type="number" min="0" step="1" data-cm-edit-field="minSpinNumber" value="' + escapeHtml(String(t.minSpinNumber)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Min stake</label><input type="number" min="0" step="0.01" data-cm-edit-field="minStake" value="' + escapeHtml(String(t.minStake)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-cm-edit-field="baseCurrency" value="' + escapeHtml(t.baseCurrency) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Active players <span class="bo-field-hint">Ranking tab counter + list (up to 500 rows), max 9 digits</span></label>' + cmActivePlayersErrorMarkup() + '<input type="text" inputmode="numeric" data-cm-edit-field="joinedPlayersCount" value="' + escapeHtml(String(t.joinedPlayersCount)) + '" /></div>'
            +     '<h2 style="margin-top:12px">Dates</h2>'
            +     '<div class="bo-edit-field"><label>Promo start</label>' + cmDateInputMarkup('promoStartDate', t.promoStartDate) + '</div>'
            +     '<div class="bo-edit-field"><label>Start</label>' + cmDateInputMarkup('startDate', t.startDate) + '</div>'
            +     '<div class="bo-edit-field"><label>End</label>' + cmDateInputMarkup('endDate', t.endDate) + '</div>'
            +     '<h2 style="margin-top:12px">Prizes</h2>'
            +     '<div data-cm-edit-prizes>' + cmRenderPrizeRows(t.prizes, 'cm-edit-prize') + '</div>'
            +     '<div class="row" style="margin-top:8px"><button type="button" data-cm-action="edit-add-prize" data-cm-id="' + escapeHtml(t.id) + '">Add prize</button></div>'
            +     '<div class="bo-edit-field"><label>Prize fund</label><input type="number" min="0" step="0.01" data-cm-edit-field="prizeFund" value="' + escapeHtml(String(t.prizeFund)) + '" /></div>'
            +     '<div class="bo-edit-field">'
            +         '<label>Restrictions</label>'
            +         '<div class="bo-edit-flags"><label><input type="checkbox" data-cm-edit-field="buyBonusOnly"' + (modeBuyBonus ? ' checked' : '') + ' /> BUY_BONUS only</label></div>'
            +     '</div>'
            +     '<div class="row" style="margin-top:10px;gap:8px">'
            +         '<button type="button" class="primary" data-cm-action="save-edit">Save</button>'
            +         '<button type="button" data-cm-action="collapse-edit">Collapse</button>'
            +     '</div>'
            + '</div>';
    }

    function cmRenderTournamentRow(t) {
        var isExpanded = cmExpandedRowId === t.id;
        var badge = '';
        if (t.isMock) {
            badge = '<span class="badge" style="background:var(--accent);color:#ffffff">MOCK</span>';
        } else if (cmEditedIds[t.id]) {
            badge = '<span class="badge" style="background:var(--status-warn);color:#ffffff">EDITED</span>';
        }
        var removeBtn = t.isMock
            ? '<button type="button" class="danger" data-cm-action="remove-mock" data-cm-id="' + escapeHtml(t.id) + '">Remove</button>'
            : '';
        return ''
            + '<div class="bo-edit-field" data-cm-row="' + escapeHtml(t.id) + '" style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:6px">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +         '<button type="button" data-cm-action="toggle-row" data-cm-id="' + escapeHtml(t.id) + '" style="flex:1;min-width:0;text-align:left;background:none;border:0;color:var(--text);padding:0;font:inherit;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'
            +             '<strong>' + escapeHtml(t.name) + '</strong> '
            +             badge + ' '
            +             '<span class="bo-game-item-key">' + escapeHtml(t.status) + ' / ' + escapeHtml(t.level) + '</span>'
            +         '</button>'
            +         removeBtn
            +     '</div>'
            +     (isExpanded ? cmRenderEditForm(t) : '')
            + '</div>';
    }

    function cmRenderListPanel() {
        var tournaments = campaignsState.tournaments || [];
        var listHtml = '';
        if (tournaments.length === 0) {
            listHtml = '<div class="hint-faint">No tournaments. Use the wizard or re-fetch with valid creds.</div>';
        } else {
            for (var i = 0; i < tournaments.length; i++) {
                listHtml += cmRenderTournamentRow(tournaments[i]);
            }
        }
        return ''
            + '<div class="section">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between;margin-bottom:8px">'
            +         '<h2 style="margin:0">Tournaments <span class="badge">' + tournaments.length + '</span></h2>'
            +         '<button type="button" data-cm-action="show-fetch">Re-fetch with different creds</button>'
            +     '</div>'
            +     listHtml
            +     '<div class="row" style="margin-top:10px">'
            +         '<button type="button" class="primary" data-cm-action="open-wizard">+ Add mock tournament</button>'
            +     '</div>'
            + '</div>';
    }

    function cmRenderWizardStep1() {
        var f = cmWizardForm;
        return ''
            + '<div class="bo-edit-field"><label>Name</label><input type="text" data-cm-wiz-field="name" value="' + escapeHtml(f.name) + '" /></div>'
            + '<div class="bo-edit-field"><label>Terms</label><textarea data-cm-wiz-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(f.terms) + '</textarea></div>'
            + '<div class="bo-edit-field"><label>Level</label>' + cmSelectMarkup('cm-wiz:level', TOURNAMENT_LEVELS, f.level) + '</div>'
            + '<div class="bo-edit-field"><label>Formula</label>' + cmSelectMarkup('cm-wiz:formula', TOURNAMENT_FORMULAS, f.formula) + '</div>';
    }

    function cmRenderWizardStep2() {
        var f = cmWizardForm;
        return ''
            + '<div class="hint">Offsets in minutes relative to "now" at submit.</div>'
            + '<div class="bo-edit-field"><label>Promo start</label><input type="number" step="1" data-cm-wiz-field="promoStartOffsetMin" value="' + escapeHtml(String(f.promoStartOffsetMin)) + '" /></div>'
            + '<div class="bo-edit-field"><label>Start</label><input type="number" step="1" data-cm-wiz-field="startOffsetMin" value="' + escapeHtml(String(f.startOffsetMin)) + '" /></div>'
            + '<div class="bo-edit-field"><label>End</label><input type="number" step="1" data-cm-wiz-field="endOffsetMin" value="' + escapeHtml(String(f.endOffsetMin)) + '" /></div>';
    }

    function cmRenderWizardStep3() {
        var f = cmWizardForm;
        var fund = f.prizeFundOverride;
        var fundValue = fund === null || fund === undefined ? '' : String(fund);
        return ''
            + '<h2>Prizes</h2>'
            + '<div data-cm-wiz-prizes>' + cmRenderPrizeRows(f.prizes, 'cm-wiz-prize') + '</div>'
            + '<div class="row" style="margin-top:8px">'
            +     '<button type="button" data-cm-action="wiz-add-prize">Add prize</button>'
            + '</div>'
            + '<div class="bo-edit-field" style="margin-top:14px">'
            +     '<label>Prize fund <span class="bo-field-hint">leave empty to auto-sum from MONEY prizes</span></label>'
            +     '<div class="bo-edit-flags">'
            +         '<input type="number" min="0" step="0.01" data-cm-wiz-field="prizeFundOverride" value="' + escapeHtml(fundValue) + '" style="max-width:160px" />'
            +         '<button type="button" data-cm-action="wiz-autosum">Auto-sum</button>'
            +     '</div>'
            + '</div>';
    }

    function cmRenderWizardStep4() {
        var f = cmWizardForm;
        return ''
            + '<div class="bo-edit-field"><label>Min spin number</label><input type="number" min="0" step="1" data-cm-wiz-field="minSpinNumber" value="' + escapeHtml(String(f.minSpinNumber)) + '" /></div>'
            + '<div class="bo-edit-field"><label>Min stake</label><input type="number" min="0" step="0.01" data-cm-wiz-field="minStake" value="' + escapeHtml(String(f.minStake)) + '" /></div>'
            + '<div class="bo-edit-field"><label>Prize places <span class="bo-field-hint">Rewards tab list length, max 500</span></label><input type="number" step="1" data-cm-wiz-field="prizePlaces" value="' + escapeHtml(String(f.prizePlaces)) + '" /></div>'
            + '<div class="bo-edit-field"><label>Active players <span class="bo-field-hint">Ranking tab counter + list (up to 500 rows), max 9 digits</span></label>' + cmActivePlayersErrorMarkup() + '<input type="text" inputmode="numeric" data-cm-wiz-field="activePlayers" value="' + escapeHtml(String(f.activePlayers)) + '" /></div>'
            + '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-cm-wiz-field="baseCurrency" value="' + escapeHtml(f.baseCurrency) + '" /></div>'
            + '<div class="bo-edit-field">'
            +     '<label>Restrictions</label>'
            +     '<div class="bo-edit-flags"><label><input type="checkbox" data-cm-wiz-field="buyBonusOnly"' + (f.buyBonusOnly ? ' checked' : '') + ' /> BUY_BONUS only</label></div>'
            + '</div>';
    }

    function cmRenderWizard() {
        if (!cmWizardOpen || !cmWizardForm) return '';
        var step = cmWizardStep;
        var body = step === 1 ? cmRenderWizardStep1()
            : step === 2 ? cmRenderWizardStep2()
            : step === 3 ? cmRenderWizardStep3()
            : cmRenderWizardStep4();
        var stepLabel = ['Basics', 'Dates', 'Prizes', 'Settings'][step - 1];
        var navHtml = ''
            + '<div class="row" style="margin-top:14px;gap:8px;justify-content:space-between">'
            +     '<div>'
            +         (step > 1 ? '<button type="button" data-cm-action="wiz-back">Back</button>' : '')
            +         ' <button type="button" data-cm-action="wiz-cancel">Cancel</button>'
            +     '</div>'
            +     '<div>'
            +         (step < 4
                        ? '<button type="button" class="primary" data-cm-action="wiz-next">Next</button>'
                        : '<button type="button" class="primary" data-cm-action="wiz-submit">Create</button>')
            +     '</div>'
            + '</div>';
        return ''
            + '<div class="section" style="border:1px solid var(--accent-dim);background:var(--accent-bg)">'
            +     '<h2 style="margin-bottom:6px">New mock tournament — Step ' + step + ' of 4: ' + stepLabel + '</h2>'
            +     body
            +     navHtml
            + '</div>';
    }

    function cmHasTournaments() {
        return campaignsState.tournaments && campaignsState.tournaments.length > 0;
    }

    function cmRenderState() {
        if (!cmRootEl) return;
        var html = '';
        if (!campaignsState.mockMode) {
            html = cmRenderEmptyState();
        } else {
            html += cmRenderBanner();
            if (cmShowFetchPanel && !cmHasTournaments()) {
                html += cmRenderFetchPanel();
            } else if (cmShowFetchPanel && cmHasTournaments()) {
                // The user re-clicked "Re-fetch with different creds" — show
                // the paste field on top of the list so they can re-seed
                // without losing context.
                html += cmRenderFetchPanel();
                html += cmRenderListPanel();
            } else {
                html += cmRenderListPanel();
            }
            if (cmWizardOpen) {
                html += cmRenderWizard();
            }
        }
        cmRootEl.innerHTML = html;
    }

    /* ── Wizard helpers ───────────────────────────────────────────── */

    function cmOpenWizard() {
        cmWizardOpen = true;
        cmWizardStep = 1;
        cmWizardForm = cmDefaultWizardForm();
        cmRenderState();
    }

    function cmCloseWizard() {
        cmWizardOpen = false;
        cmWizardStep = 1;
        cmWizardForm = null;
        cmRenderState();
    }

    function cmSubmitWizard() {
        if (!cmWizardForm) return;
        var f = cmWizardForm;
        var fund = f.prizeFundOverride;
        var payload = {
            name: f.name || 'QA Test Tournament',
            terms: f.terms || '',
            level: f.level,
            formula: f.formula,
            minSpinNumber: Number(f.minSpinNumber) || 0,
            minStake: Number(f.minStake) || 0,
            baseCurrency: f.baseCurrency || (campaignsState.defaultBaseCurrency || 'EUR'),
            promoStartOffsetMin: Number(f.promoStartOffsetMin) || 0,
            startOffsetMin: Number(f.startOffsetMin) || 0,
            endOffsetMin: Number(f.endOffsetMin) || 0,
            prizes: f.prizes.map(function (p) {
                return {
                    place: Number(p.place) || 1,
                    type: p.type,
                    name: p.name,
                    currencyCode: p.currencyCode,
                };
            }),
            prizeFundOverride: (fund === null || fund === undefined || fund === '') ? null : Number(fund),
            buyBonusOnly: !!f.buyBonusOnly,
            prizePlaces: Math.max(0, Math.min(500, Math.floor(Number(f.prizePlaces) || 0))),
            activePlayers: Math.max(0, Math.min(999999999, Math.floor(Number(f.activePlayers) || 0))),
        };
        send({ type: 'campaigns.createMockTournament', payload: { form: payload } });
        cmCloseWizard();
    }

    /* ── Edit helpers ─────────────────────────────────────────────── */

    function cmCollectEditForm(rowEl) {
        var fields = rowEl.querySelectorAll('[data-cm-edit-field]');
        var patch = {};
        var i;
        for (i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-cm-edit-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                patch[f] = !!el.checked;
            } else if (el.type === 'number') {
                var n = Number(el.value);
                patch[f] = isFinite(n) ? n : 0;
            } else if (el.type === 'datetime-local') {
                if (el.value) {
                    var d = new Date(el.value);
                    patch[f] = isNaN(d.getTime()) ? '' : d.toISOString();
                } else {
                    patch[f] = '';
                }
            } else {
                patch[f] = el.value;
            }
        }
        // Collect prizes from the data-cm-edit-prize-row entries.
        var prizeRows = rowEl.querySelectorAll('[data-cm-edit-prize-row]');
        var prizes = [];
        for (i = 0; i < prizeRows.length; i++) {
            var pr = prizeRows[i];
            var place = pr.querySelector('[data-cm-edit-prize-field="place"]');
            var type = pr.querySelector('[data-cm-edit-prize-field="type"]');
            var name = pr.querySelector('[data-cm-edit-prize-field="name"]');
            var ccy = pr.querySelector('[data-cm-edit-prize-field="currencyCode"]');
            prizes.push({
                place: place ? Number(place.value) || 1 : 1,
                type: type ? type.value : 'MONEY',
                name: name ? name.value : '',
                currencyCode: ccy ? ccy.value : '',
            });
        }
        return { patch: patch, prizes: prizes };
    }

    function cmBuildPatchPayload(rowEl, summary) {
        var collected = cmCollectEditForm(rowEl);
        var src = collected.patch;
        var patch = {};
        if (src.name !== undefined) patch.name = src.name;
        if (src.terms !== undefined) patch.terms = src.terms;
        if (src.level !== undefined) patch.level = src.level;
        if (src.formula !== undefined) patch.formula = src.formula;
        if (src.minSpinNumber !== undefined) patch.minSpinNumber = src.minSpinNumber;
        if (src.minStake !== undefined) patch.minStake = src.minStake;
        if (src.baseCurrency !== undefined) patch.baseCurrency = src.baseCurrency;
        if (src.promoStartDate !== undefined) patch.promoStartDate = src.promoStartDate;
        if (src.startDate !== undefined) patch.startDate = src.startDate;
        if (src.endDate !== undefined) patch.endDate = src.endDate;
        if (src.prizeFund !== undefined) patch.prizeFund = src.prizeFund;
        // Active players is a text input capped at 9 digits by
        // cmEnforceActivePlayersLimit; coerce to a non-negative integer here.
        if (src.joinedPlayersCount !== undefined) {
            var joined = Math.floor(Number(src.joinedPlayersCount));
            patch.joinedPlayersCount = isFinite(joined) && joined > 0 ? joined : 0;
        }
        // BUY_BONUS toggle → restrictions.eligibleGameModes.
        if (src.buyBonusOnly !== undefined) {
            patch.restrictions = {
                eligibleGameModes: src.buyBonusOnly ? ['BUY_BONUS'] : null,
            };
        }
        // Prizes always included (the form renders them all).
        patch.prizes = collected.prizes;
        // Mark this id as edited so the EDITED badge sticks across rerenders
        // (only relevant for non-mock entries — mocks already get a MOCK badge).
        if (summary && !summary.isMock) {
            cmEditedIds[summary.id] = true;
        }
        return patch;
    }

    /* ── Wire root container — single delegated handler set ──────── */

    if (cmRootEl) {
        cmRootEl.addEventListener('click', function (e) {
            var t = e.target;
            if (!t || !t.getAttribute) return;
            var action = t.getAttribute('data-cm-action');
            // Walk up if click landed on a child element of a button.
            if (!action && t.closest) {
                var btn = t.closest('[data-cm-action]');
                if (btn) {
                    action = btn.getAttribute('data-cm-action');
                    t = btn;
                }
            }
            if (!action) {
                return;
            }

            if (action === 'enable-mocking') {
                send({ type: 'campaigns.enableMocking' });
                cmShowFetchPanel = true;
                cmLastFetchError = null;
                return;
            }
            if (action === 'disable-mocking') {
                if (window.confirm('Disable mocking? This clears every mock tournament and resumes real polling.')) {
                    send({ type: 'campaigns.disableMocking' });
                    cmShowFetchPanel = true;
                    cmLastFetchError = null;
                    cmExpandedRowId = null;
                    cmEditedIds = Object.create(null);
                    cmCloseWizard();
                }
                return;
            }
            if (action === 'fetch') {
                var inputEl = document.getElementById('cm-fetch-input');
                var raw = inputEl ? String(inputEl.value || '').trim() : '';
                if (!raw) {
                    cmLastFetchError = 'Paste a session string first.';
                    cmRenderState();
                    return;
                }
                var params;
                try {
                    params = new URLSearchParams(raw);
                } catch (err) {
                    cmLastFetchError = 'Could not parse — expected sessionKey=...&playerId=...';
                    cmRenderState();
                    return;
                }
                var sk = params.get('sessionKey');
                var pid = params.get('playerId');
                if (!sk || !pid) {
                    cmLastFetchError = 'Missing sessionKey or playerId in the pasted string.';
                    cmRenderState();
                    return;
                }
                cmLastFetchError = null;
                send({ type: 'campaigns.fetchExternal', payload: { sessionKey: sk, playerId: pid } });
                cmShowFeedback('Fetching tournaments…', 'ok');
                return;
            }
            if (action === 'skip-fetch') {
                cmShowFetchPanel = false;
                cmLastFetchError = null;
                cmRenderState();
                return;
            }
            if (action === 'show-fetch') {
                cmShowFetchPanel = true;
                cmRenderState();
                return;
            }
            if (action === 'open-wizard') {
                cmOpenWizard();
                return;
            }
            if (action === 'wiz-back') {
                if (cmWizardStep > 1) {
                    cmCollectWizardForm();
                    cmWizardStep -= 1;
                    cmRenderState();
                }
                return;
            }
            if (action === 'wiz-next') {
                if (cmWizardStep < 4) {
                    cmCollectWizardForm();
                    cmWizardStep += 1;
                    cmRenderState();
                }
                return;
            }
            if (action === 'wiz-cancel') {
                cmCloseWizard();
                return;
            }
            if (action === 'wiz-submit') {
                cmCollectWizardForm();
                cmSubmitWizard();
                return;
            }
            if (action === 'wiz-add-prize') {
                cmCollectWizardForm();
                if (cmWizardForm) {
                    var nextPlace = cmWizardForm.prizes.length + 1;
                    cmWizardForm.prizes.push({
                        place: nextPlace,
                        type: 'MONEY',
                        name: '',
                        currencyCode: cmWizardForm.baseCurrency || (campaignsState.defaultBaseCurrency || 'EUR'),
                    });
                    cmRenderState();
                }
                return;
            }
            if (action === 'wiz-autosum') {
                cmCollectWizardForm();
                if (cmWizardForm) {
                    cmWizardForm.prizeFundOverride = cmAutoSumPrizeFundFor(cmWizardForm.prizes);
                    cmRenderState();
                }
                return;
            }
            if (action === 'toggle-row') {
                var id = t.getAttribute('data-cm-id');
                cmExpandedRowId = (cmExpandedRowId === id) ? null : id;
                cmRenderState();
                return;
            }
            if (action === 'collapse-edit') {
                cmExpandedRowId = null;
                cmRenderState();
                return;
            }
            if (action === 'remove-mock') {
                var rmId = t.getAttribute('data-cm-id');
                if (rmId) {
                    send({ type: 'campaigns.removeMockTournament', payload: { id: rmId } });
                }
                return;
            }
            if (action === 'save-edit') {
                var rowEl = t.closest && t.closest('[data-cm-row]');
                if (!rowEl) return;
                var rowId = rowEl.getAttribute('data-cm-row');
                var summary = null;
                var list = campaignsState.tournaments || [];
                for (var i = 0; i < list.length; i++) {
                    if (list[i].id === rowId) { summary = list[i]; break; }
                }
                var patch = cmBuildPatchPayload(rowEl, summary);
                send({ type: 'campaigns.updateTournament', payload: { id: rowId, patch: patch } });
                cmShowFeedback('Saved', 'ok');
                return;
            }
            if (action === 'edit-add-prize') {
                // Collect current edit form, append a blank prize, then send
                // an update — the rebroadcast repaints the row with the new
                // prize row visible. Equivalent to: save current state +
                // append. Slightly heavy but keeps the data flow uniform.
                var addRowEl = t.closest && t.closest('[data-cm-row]');
                if (!addRowEl) return;
                var addRowId = addRowEl.getAttribute('data-cm-row');
                var addSummary = null;
                var addList = campaignsState.tournaments || [];
                for (var ai = 0; ai < addList.length; ai++) {
                    if (addList[ai].id === addRowId) { addSummary = addList[ai]; break; }
                }
                var addPatch = cmBuildPatchPayload(addRowEl, addSummary);
                addPatch.prizes = (addPatch.prizes || []).slice();
                addPatch.prizes.push({
                    place: addPatch.prizes.length + 1,
                    type: 'MONEY',
                    name: '',
                    currencyCode: addSummary ? addSummary.baseCurrency : (campaignsState.defaultBaseCurrency || 'EUR'),
                });
                send({ type: 'campaigns.updateTournament', payload: { id: addRowId, patch: addPatch } });
                return;
            }

            // Wizard prize remove (data-cm-wiz-prize-remove="<idx>").
            var wizPrizeRemove = t.getAttribute('data-cm-wiz-prize-remove');
            if (wizPrizeRemove !== null && wizPrizeRemove !== undefined && wizPrizeRemove !== '' && cmWizardForm) {
                var rmIdx = parseInt(wizPrizeRemove, 10);
                if (!isNaN(rmIdx) && rmIdx >= 0 && rmIdx < cmWizardForm.prizes.length) {
                    cmCollectWizardForm();
                    cmWizardForm.prizes.splice(rmIdx, 1);
                    cmRenderState();
                }
                return;
            }

            // Edit-form prize remove (data-cm-edit-prize-remove="<idx>"). We
            // build the patch from the current form, remove the chosen prize,
            // and dispatch an update — the rebroadcast repaints.
            var editPrizeRemove = t.getAttribute('data-cm-edit-prize-remove');
            if (editPrizeRemove !== null && editPrizeRemove !== undefined && editPrizeRemove !== '') {
                var pruneRow = t.closest && t.closest('[data-cm-row]');
                if (!pruneRow) return;
                var pruneId = pruneRow.getAttribute('data-cm-row');
                var pruneSummary = null;
                var pruneList = campaignsState.tournaments || [];
                for (var pi = 0; pi < pruneList.length; pi++) {
                    if (pruneList[pi].id === pruneId) { pruneSummary = pruneList[pi]; break; }
                }
                var prunePatch = cmBuildPatchPayload(pruneRow, pruneSummary);
                var prIdx = parseInt(editPrizeRemove, 10);
                if (!isNaN(prIdx) && Array.isArray(prunePatch.prizes) && prIdx >= 0 && prIdx < prunePatch.prizes.length) {
                    prunePatch.prizes.splice(prIdx, 1);
                    send({ type: 'campaigns.updateTournament', payload: { id: pruneId, patch: prunePatch } });
                }
                return;
            }
        });

        // Capture wizard input on every change so step nav doesn't lose state.
        cmRootEl.addEventListener('input', function (e) {
            var t = e.target;
            if (!t || !t.getAttribute) return;
            // Wizard prize input.
            if (t.getAttribute('data-cm-wiz-prize-field')) {
                cmCollectWizardForm();
            }
            // Active players (row editor + wizard) — cap the digit count.
            if (t.getAttribute('data-cm-edit-field') === 'joinedPlayersCount'
                || t.getAttribute('data-cm-wiz-field') === 'activePlayers') {
                cmEnforceActivePlayersLimit(t);
            }
        });

        // Active players applies on blur instead of waiting for Save. The
        // sub-tab is rebuilt from the broadcast state, so any rebuild between
        // typing and Save would otherwise discard the typed value.
        cmRootEl.addEventListener('change', function (e) {
            var t = e.target;
            if (!t || !t.getAttribute) return;
            if (t.getAttribute('data-cm-edit-field') !== 'joinedPlayersCount') return;
            var rowEl = t.closest && t.closest('[data-cm-row]');
            if (!rowEl) return;
            var n = Math.floor(Number(t.value));
            send({
                type: 'campaigns.updateTournament',
                payload: {
                    id: rowEl.getAttribute('data-cm-row'),
                    patch: { joinedPlayersCount: isFinite(n) && n > 0 ? n : 0 },
                },
            });
            cmShowFeedback('Active players updated', 'ok');
        });

    }

    function cmCollectWizardForm() {
        if (!cmWizardOpen || !cmWizardForm || !cmRootEl) return;
        var fields = cmRootEl.querySelectorAll('[data-cm-wiz-field]');
        for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-cm-wiz-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                cmWizardForm[f] = !!el.checked;
            } else if (el.type === 'number') {
                if (f === 'prizeFundOverride') {
                    cmWizardForm[f] = el.value === '' ? null : Number(el.value);
                } else {
                    cmWizardForm[f] = Number(el.value);
                }
            } else {
                cmWizardForm[f] = el.value;
            }
        }
        // Wizard prizes.
        var prizeRows = cmRootEl.querySelectorAll('[data-cm-wiz-prize-row]');
        var prizes = [];
        for (var j = 0; j < prizeRows.length; j++) {
            var pr = prizeRows[j];
            var place = pr.querySelector('[data-cm-wiz-prize-field="place"]');
            var type = pr.querySelector('[data-cm-wiz-prize-field="type"]');
            var name = pr.querySelector('[data-cm-wiz-prize-field="name"]');
            var ccy = pr.querySelector('[data-cm-wiz-prize-field="currencyCode"]');
            prizes.push({
                place: place ? Number(place.value) || 1 : 1,
                type: type ? type.value : 'MONEY',
                name: name ? name.value : '',
                currencyCode: ccy ? ccy.value : (cmWizardForm.baseCurrency || 'EUR'),
            });
        }
        if (prizes.length > 0) {
            cmWizardForm.prizes = prizes;
        }
    }

    /* Sub-tab switching for the Campaigns tab. */
    var cmSubtabButtons = document.querySelectorAll('[data-panel="campaigns"] .subtab-nav button[data-subtab]');
    var cmSubtabPanels = document.querySelectorAll('[data-panel="campaigns"] .subtab-panel[data-subpanel]');
    function showCmSubtab(name) {
        cmSubtabButtons.forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-subtab') === name);
        });
        cmSubtabPanels.forEach(function (p) {
            p.classList.toggle('active', p.getAttribute('data-subpanel') === name);
        });
    }
    cmSubtabButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            if (btn.disabled) return;
            var name = btn.getAttribute('data-subtab');
            showCmSubtab(name);
            // Ask the lobby for fresh sub-tab state when the user activates
            // PrizePop (its first render relies on the broadcast roundtrip).
            if (name === 'cm-prizepop') {
                send({ type: 'prizepop.requestState' });
            }
            if (name === 'cm-rebate') {
                send({ type: 'rebate.requestState' });
            }
        });
    });

    cmRenderState();

    /* ════════════ Campaigns: PrizePop sub-tab ════════════ */
    /* Mirrors the Tournaments sub-tab. The state lives in prizePopState,
       fed by prizepop.state broadcasts; UI state (which row is expanded,
       fetch-panel visibility, form open/closed) is local to the popup. */

    var PP_LEVELS = ['GOLD', 'PURPLE', 'RED', 'BLUE', 'GREEN', 'MADNESS'];
    var PP_STATUSES = ['PROMO', 'ACTIVE', 'COMPLETED_VISIBLE'];
    var PP_FORMULAS = ['RANDOM'];
    /* Default [start, end] offsets (minutes) per status. The lobby derives a
       PrizePop's state from its interval vs. now, not from the status field, so the
       offsets must match the chosen status or the mock renders as ACTIVE. */
    var PP_STATUS_OFFSETS = { PROMO: [60, 300], ACTIVE: [0, 240], COMPLETED_VISIBLE: [-240, -5] };

    var ppRootEl = document.getElementById('pp-prizepop-root');
    var ppFeedbackEl = document.getElementById('pp-feedback');

    var prizePopState = { mockMode: false, mocks: [], campaigns: [], defaultBaseCurrency: 'EUR' };
    var ppFeedbackTimer = null;

    var ppShowFetchPanel = true;
    var ppLastFetchError = null;
    var ppExpandedRowId = null;
    var ppEditedIds = Object.create(null);
    var ppFormOpen = false;
    var ppForm = null;

    function ppShowFeedback(text, kind) {
        if (!ppFeedbackEl) return;
        ppFeedbackEl.textContent = text;
        ppFeedbackEl.className = 'jp-feedback show ' + (kind || 'ok');
        if (ppFeedbackTimer) window.clearTimeout(ppFeedbackTimer);
        ppFeedbackTimer = window.setTimeout(function () {
            if (ppFeedbackEl) {
                ppFeedbackEl.textContent = '';
                ppFeedbackEl.className = 'jp-feedback';
            }
        }, 3500);
    }

    function ppDefaultForm() {
        var cur = prizePopState.defaultBaseCurrency || 'EUR';
        return {
            name: 'QA Test PrizePop',
            terms: '',
            status: 'ACTIVE',
            level: 'PURPLE',
            formula: 'RANDOM',
            minSpinNumber: 25,
            minStake: 25,
            baseCurrency: cur,
            startOffsetMin: 0,
            endOffsetMin: 240,
            prizeFund: 500,
            totalPrizesCount: 100,
            /* games default-checked from the controller's MOCK_PRIZEPOP_GAMES;
               we mirror the same list here so the popup form can show its
               own checkbox grid without round-tripping. The controller will
               re-apply the canonical list on submit if this is empty. */
            games: [
                'ARSlot', 'BCSlot', 'BCBSlot', 'FBHRSlot', 'FVBPSlot',
                'FBRSlot', 'FVCLHSlot', 'FVCHTSlot', 'FVCHSlot', 'FDCSlot',
                'FDHSlot', 'FVDRSlot', 'FVJHSlot', 'FVLRSlot', 'FVSHRSlot',
                'FVSHSlot', 'FVSFSlot', 'FVSPSlot', 'FVSRSlot', 'TADSlot',
                'TBHRSlot', 'TNBPSlot', 'TNBRSlot', 'TNCLHSlot', 'TCHSlot',
                'TCHBSlot', 'TCWSlot', 'TNDCSlot', 'TNDHSlot', 'TNDRSlot',
                'TNJHSlot', 'TNJRSlot', 'TNLHSlot', 'TNLRSlot', 'TSDSlot',
                'TNSHRSlot', 'TNSHSlot', 'TNSFSlot', 'TNSPSlot', 'TNSRSlot',
                'OBCSlot', 'OBHSlot', 'OHBPSlot', 'OHBRSlot', 'OHCLHSlot',
                'OHCHTSlot', 'OHCHSlot', 'OSFSlot', 'OSHSlot', 'OSHSSlot',
            ],
        };
    }

    function ppSelectMarkup(name, options, current, editField) {
        var editAttr = editField ? ' data-pp-edit-field="' + editField + '"' : '';
        var html = '<select name="' + name + '"' + editAttr + '>';
        for (var i = 0; i < options.length; i++) {
            var v = options[i];
            html += '<option value="' + v + '"' + (v === current ? ' selected' : '') + '>' + v + '</option>';
        }
        return html + '</select>';
    }

    function ppDateInputMarkup(name, value) {
        var local = '';
        if (value) {
            var d = new Date(value);
            if (!isNaN(d.getTime())) {
                var pad = function (n) { return n < 10 ? '0' + n : String(n); };
                local = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
                    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
            }
        }
        return '<input type="datetime-local" data-pp-edit-field="' + name + '" value="' + escapeHtml(local) + '" />';
    }

    function ppRenderEmptyState() {
        return ''
            + '<div class="section" style="text-align:center;padding:24px 16px">'
            +     '<h2 style="margin-bottom:8px">Mocking is off</h2>'
            +     '<button type="button" class="primary" data-pp-action="enable-mocking">Enable mocking</button>'
            +     '<div class="hint" style="margin-top:10px">Pauses polling and lets you fetch + edit + add PrizePop campaigns.</div>'
            + '</div>';
    }

    function ppRenderBanner() {
        return ''
            + '<div class="hint" style="background:var(--accent-bg);border:1px solid var(--accent-dim);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:10px;display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +     '<span>Mocking enabled — polling paused. Edits and mocks survive until you disable mocking.</span>'
            +     '<button type="button" class="danger" data-pp-action="disable-mocking">Disable mocking</button>'
            + '</div>';
    }

    function ppRenderFetchPanel() {
        var errorHtml = ppLastFetchError
            ? '<div class="hint" style="background:var(--surface-2);border-left:3px solid var(--status-error);color:var(--status-error);border-radius:var(--radius-sm);padding:10px 14px;margin-bottom:10px">' + escapeHtml(ppLastFetchError) + '</div>'
            : '';
        return ''
            + '<div class="section">'
            +     '<h2>Fetch PrizePop campaigns</h2>'
            +     errorHtml
            +     '<div class="bo-edit-field">'
            +         '<label>Paste session string <span class="bo-field-hint">format: sessionKey=...&playerId=...</span></label>'
            +         '<input type="text" id="pp-fetch-input" autocomplete="off" placeholder="sessionKey=abc&playerId=123" />'
            +     '</div>'
            +     '<div class="row" style="margin-top:8px;gap:8px">'
            +         '<button type="button" class="primary" data-pp-action="fetch">Fetch campaigns</button>'
            +         '<button type="button" data-pp-action="skip-fetch">Skip — start adding mocks</button>'
            +     '</div>'
            + '</div>';
    }

    function ppRenderEditForm(c) {
        return ''
            + '<div class="pp-edit-form" style="padding:8px 0">'
            +     '<div class="bo-edit-field"><label>Name</label><input type="text" data-pp-edit-field="name" value="' + escapeHtml(c.name) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Terms</label><textarea data-pp-edit-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(c.terms || '') + '</textarea></div>'
            +     '<div class="bo-edit-field"><label>Status</label>' + ppSelectMarkup('pp-edit:status', PP_STATUSES, c.status, 'status') + '</div>'
            +     '<div class="bo-edit-field"><label>Level</label>' + ppSelectMarkup('pp-edit:level', PP_LEVELS, c.level, 'level') + '</div>'
            +     '<div class="bo-edit-field"><label>Formula</label>' + ppSelectMarkup('pp-edit:formula', PP_FORMULAS, c.formula, 'formula') + '</div>'
            +     '<div class="bo-edit-field"><label>Min spin</label><input type="number" min="0" step="1" data-pp-edit-field="minSpinNumber" value="' + escapeHtml(String(c.minSpinNumber)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Min stake</label><input type="number" min="0" step="0.01" data-pp-edit-field="minStake" value="' + escapeHtml(String(c.minStake)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-pp-edit-field="baseCurrency" value="' + escapeHtml(c.baseCurrency) + '" /></div>'
            +     '<h2 style="margin-top:12px">Dates</h2>'
            +     '<div class="bo-edit-field"><label>Start</label>' + ppDateInputMarkup('startDate', c.startDate) + '</div>'
            +     '<div class="bo-edit-field"><label>End</label>' + ppDateInputMarkup('endDate', c.endDate) + '</div>'
            +     '<div class="bo-edit-field"><label>Prize fund</label><input type="number" min="0" step="0.01" data-pp-edit-field="prizeFund" value="' + escapeHtml(String(c.prizeFund)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Total prizes count</label><input type="number" min="0" step="1" data-pp-edit-field="totalPrizesCount" value="' + escapeHtml(String(c.totalPrizesCount)) + '" /></div>'
            +     '<div class="row" style="margin-top:10px;gap:8px">'
            +         '<button type="button" class="primary" data-pp-action="save-edit">Save</button>'
            +         '<button type="button" data-pp-action="collapse-edit">Collapse</button>'
            +     '</div>'
            + '</div>';
    }

    function ppRenderCampaignRow(c) {
        var isExpanded = ppExpandedRowId === c.id;
        var badge = '';
        if (c.isMock) {
            badge = '<span class="badge" style="background:var(--accent);color:#ffffff">MOCK</span>';
        } else if (ppEditedIds[c.id]) {
            badge = '<span class="badge" style="background:var(--status-warn);color:#ffffff">EDITED</span>';
        }
        var removeBtn = c.isMock
            ? '<button type="button" class="danger" data-pp-action="remove-mock" data-pp-id="' + escapeHtml(c.id) + '">Remove</button>'
            : '';
        return ''
            + '<div class="bo-edit-field" data-pp-row="' + escapeHtml(c.id) + '" style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:6px">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +         '<button type="button" data-pp-action="toggle-row" data-pp-id="' + escapeHtml(c.id) + '" style="flex:1;min-width:0;text-align:left;background:none;border:0;color:var(--text);padding:0;font:inherit;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'
            +             '<strong>' + escapeHtml(c.name) + '</strong> '
            +             badge + ' '
            +             '<span class="bo-game-item-key">' + escapeHtml(c.status) + ' / ' + escapeHtml(c.level) + ' · fund ' + escapeHtml(String(c.prizeFund)) + '</span>'
            +         '</button>'
            +         removeBtn
            +     '</div>'
            +     (isExpanded ? ppRenderEditForm(c) : '')
            + '</div>';
    }

    function ppRenderListPanel() {
        var campaigns = prizePopState.campaigns || [];
        var listHtml = '';
        if (campaigns.length === 0) {
            listHtml = '<div class="hint-faint">No PrizePop campaigns. Use the wizard or re-fetch with valid creds.</div>';
        } else {
            for (var i = 0; i < campaigns.length; i++) {
                listHtml += ppRenderCampaignRow(campaigns[i]);
            }
        }
        return ''
            + '<div class="section">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between;margin-bottom:8px">'
            +         '<h2 style="margin:0">PrizePop campaigns <span class="badge">' + campaigns.length + '</span></h2>'
            +         '<button type="button" data-pp-action="show-fetch">Re-fetch with different creds</button>'
            +     '</div>'
            +     listHtml
            +     '<div class="row" style="margin-top:10px">'
            +         '<button type="button" class="primary" data-pp-action="open-form">+ Add mock PrizePop campaign</button>'
            +     '</div>'
            + '</div>';
    }

    function ppRenderGamesCheckboxes(games) {
        /* Render checkboxes for the controller default 50 EGT slots. A
           checkbox is checked when the gameKey appears in the form games
           array. */
        var defaults = ppDefaultForm().games;
        var selected = Object.create(null);
        for (var k = 0; k < games.length; k++) {
            selected[games[k]] = true;
        }
        var html = '<div class="bo-edit-flags" style="flex-wrap:wrap;gap:6px 12px">';
        for (var i = 0; i < defaults.length; i++) {
            var g = defaults[i];
            html += '<label><input type="checkbox" data-pp-form-game="' + escapeHtml(g) + '"' + (selected[g] ? ' checked' : '') + ' /> ' + escapeHtml(g) + '</label>';
        }
        return html + '</div>';
    }

    function ppRenderForm() {
        if (!ppFormOpen || !ppForm) return '';
        var f = ppForm;
        return ''
            + '<div class="section" style="border:1px solid var(--accent-dim);background:var(--accent-bg)">'
            +     '<h2 style="margin-bottom:6px">New mock PrizePop campaign</h2>'
            +     '<div class="bo-edit-field"><label>Name</label><input type="text" data-pp-form-field="name" value="' + escapeHtml(f.name) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Terms</label><textarea data-pp-form-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(f.terms) + '</textarea></div>'
            +     '<div class="bo-edit-field"><label>Status</label>' + ppSelectMarkup('pp-form:status', PP_STATUSES, f.status) + '</div>'
            +     '<div class="bo-edit-field"><label>Level</label>' + ppSelectMarkup('pp-form:level', PP_LEVELS, f.level) + '</div>'
            +     '<div class="bo-edit-field"><label>Formula</label>' + ppSelectMarkup('pp-form:formula', PP_FORMULAS, f.formula) + '</div>'
            +     '<div class="bo-edit-field"><label>Min spin number</label><input type="number" min="0" step="1" data-pp-form-field="minSpinNumber" value="' + escapeHtml(String(f.minSpinNumber)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Min stake</label><input type="number" min="0" step="0.01" data-pp-form-field="minStake" value="' + escapeHtml(String(f.minStake)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-pp-form-field="baseCurrency" value="' + escapeHtml(f.baseCurrency) + '" /></div>'
            +     '<div class="hint">Offsets in minutes relative to "now" at submit. Changing Status resets them — the lobby derives PROMO / ACTIVE / finished from the interval dates, not from the status field.</div>'
            +     '<div class="bo-edit-field"><label>Start offset (min)</label><input type="number" step="1" data-pp-form-field="startOffsetMin" value="' + escapeHtml(String(f.startOffsetMin)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>End offset (min)</label><input type="number" step="1" data-pp-form-field="endOffsetMin" value="' + escapeHtml(String(f.endOffsetMin)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Prize fund</label><input type="number" min="0" step="0.01" data-pp-form-field="prizeFund" value="' + escapeHtml(String(f.prizeFund)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Total prizes count</label><input type="number" min="0" step="1" data-pp-form-field="totalPrizesCount" value="' + escapeHtml(String(f.totalPrizesCount)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Games</label>' + ppRenderGamesCheckboxes(f.games) + '</div>'
            +     '<div class="row" style="margin-top:14px;gap:8px;justify-content:flex-end">'
            +         '<button type="button" data-pp-action="form-cancel">Cancel</button>'
            +         '<button type="button" class="primary" data-pp-action="form-submit">Create</button>'
            +     '</div>'
            + '</div>';
    }

    function ppHasCampaigns() {
        return prizePopState.campaigns && prizePopState.campaigns.length > 0;
    }

    function ppRenderState() {
        if (!ppRootEl) return;
        var html = '';
        if (!prizePopState.mockMode) {
            html = ppRenderEmptyState();
        } else {
            html += ppRenderBanner();
            if (ppShowFetchPanel && !ppHasCampaigns()) {
                html += ppRenderFetchPanel();
            } else if (ppShowFetchPanel && ppHasCampaigns()) {
                html += ppRenderFetchPanel();
                html += ppRenderListPanel();
            } else {
                html += ppRenderListPanel();
            }
            if (ppFormOpen) {
                html += ppRenderForm();
            }
        }
        ppRootEl.innerHTML = html;
    }

    function ppOpenForm() {
        ppFormOpen = true;
        ppForm = ppDefaultForm();
        ppRenderState();
    }

    function ppCloseForm() {
        ppFormOpen = false;
        ppForm = null;
        ppRenderState();
    }

    function ppCollectForm() {
        if (!ppFormOpen || !ppForm || !ppRootEl) return;
        var fields = ppRootEl.querySelectorAll('[data-pp-form-field]');
        for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-pp-form-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                ppForm[f] = !!el.checked;
            } else if (el.type === 'number') {
                ppForm[f] = el.value === '' ? 0 : Number(el.value);
            } else {
                ppForm[f] = el.value;
            }
        }
        /* Form-level select elements — these are not data-pp-form-field-
           tagged in the markup (the select carries a "name" attribute like
           "pp-form:status" instead). Read them via their name attribute
           so the form payload stays complete. */
        var statusSel = ppRootEl.querySelector('select[name="pp-form:status"]');
        var levelSel = ppRootEl.querySelector('select[name="pp-form:level"]');
        var formulaSel = ppRootEl.querySelector('select[name="pp-form:formula"]');
        if (statusSel) ppForm.status = statusSel.value;
        if (levelSel) ppForm.level = levelSel.value;
        if (formulaSel) ppForm.formula = formulaSel.value;
        /* Games checkboxes. */
        var gameBoxes = ppRootEl.querySelectorAll('[data-pp-form-game]');
        var games = [];
        for (var j = 0; j < gameBoxes.length; j++) {
            var gb = gameBoxes[j];
            if (gb.checked) games.push(gb.getAttribute('data-pp-form-game'));
        }
        ppForm.games = games;
    }

    function ppSubmitForm() {
        if (!ppForm) return;
        var f = ppForm;
        var payload = {
            name: f.name || 'QA Test PrizePop',
            terms: f.terms || '',
            status: f.status,
            level: f.level,
            formula: f.formula,
            minSpinNumber: Number(f.minSpinNumber) || 0,
            minStake: Number(f.minStake) || 0,
            baseCurrency: f.baseCurrency || (prizePopState.defaultBaseCurrency || 'EUR'),
            startOffsetMin: Number(f.startOffsetMin) || 0,
            endOffsetMin: Number(f.endOffsetMin) || 0,
            prizeFund: Number(f.prizeFund) || 0,
            totalPrizesCount: Number(f.totalPrizesCount) || 0,
            games: Array.isArray(f.games) ? f.games.slice() : [],
        };
        send({ type: 'prizepop.createMockCampaign', payload: { form: payload } });
        ppCloseForm();
    }

    function ppCollectEditForm(rowEl) {
        var fields = rowEl.querySelectorAll('[data-pp-edit-field]');
        var patch = {};
        for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-pp-edit-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                patch[f] = !!el.checked;
            } else if (el.type === 'number') {
                var n = Number(el.value);
                patch[f] = isFinite(n) ? n : 0;
            } else if (el.type === 'datetime-local') {
                if (el.value) {
                    var d = new Date(el.value);
                    patch[f] = isNaN(d.getTime()) ? '' : d.toISOString();
                } else {
                    patch[f] = '';
                }
            } else {
                patch[f] = el.value;
            }
        }
        return patch;
    }

    if (ppRootEl) {
        ppRootEl.addEventListener('click', function (e) {
            var t = e.target;
            if (!t || !t.getAttribute) return;
            var action = t.getAttribute('data-pp-action');
            if (!action && t.closest) {
                var btn = t.closest('[data-pp-action]');
                if (btn) {
                    action = btn.getAttribute('data-pp-action');
                    t = btn;
                }
            }
            if (!action) return;

            if (action === 'enable-mocking') {
                send({ type: 'prizepop.enableMocking' });
                ppShowFetchPanel = true;
                ppLastFetchError = null;
                return;
            }
            if (action === 'disable-mocking') {
                if (window.confirm('Disable mocking? This clears every mock PrizePop campaign and resumes real polling.')) {
                    send({ type: 'prizepop.disableMocking' });
                    ppShowFetchPanel = true;
                    ppLastFetchError = null;
                    ppExpandedRowId = null;
                    ppEditedIds = Object.create(null);
                    ppCloseForm();
                }
                return;
            }
            if (action === 'fetch') {
                var inputEl = document.getElementById('pp-fetch-input');
                var raw = inputEl ? String(inputEl.value || '').trim() : '';
                if (!raw) {
                    ppLastFetchError = 'Paste a session string first.';
                    ppRenderState();
                    return;
                }
                var params;
                try {
                    params = new URLSearchParams(raw);
                } catch (err) {
                    ppLastFetchError = 'Could not parse — expected sessionKey=...&playerId=...';
                    ppRenderState();
                    return;
                }
                var sk = params.get('sessionKey');
                var pid = params.get('playerId');
                if (!sk || !pid) {
                    ppLastFetchError = 'Missing sessionKey or playerId in the pasted string.';
                    ppRenderState();
                    return;
                }
                ppLastFetchError = null;
                send({ type: 'prizepop.fetchExternal', payload: { sessionKey: sk, playerId: pid } });
                ppShowFeedback('Fetching campaigns…', 'ok');
                return;
            }
            if (action === 'skip-fetch') {
                ppShowFetchPanel = false;
                ppLastFetchError = null;
                ppRenderState();
                return;
            }
            if (action === 'show-fetch') {
                ppShowFetchPanel = true;
                ppRenderState();
                return;
            }
            if (action === 'open-form') {
                ppOpenForm();
                return;
            }
            if (action === 'form-cancel') {
                ppCloseForm();
                return;
            }
            if (action === 'form-submit') {
                ppCollectForm();
                ppSubmitForm();
                return;
            }
            if (action === 'toggle-row') {
                var id = t.getAttribute('data-pp-id');
                ppExpandedRowId = (ppExpandedRowId === id) ? null : id;
                ppRenderState();
                return;
            }
            if (action === 'collapse-edit') {
                ppExpandedRowId = null;
                ppRenderState();
                return;
            }
            if (action === 'remove-mock') {
                var rmId = t.getAttribute('data-pp-id');
                if (rmId) {
                    send({ type: 'prizepop.removeMockCampaign', payload: { id: rmId } });
                }
                return;
            }
            if (action === 'save-edit') {
                var rowEl = t.closest && t.closest('[data-pp-row]');
                if (!rowEl) return;
                var rowId = rowEl.getAttribute('data-pp-row');
                var patch = ppCollectEditForm(rowEl);
                var summary = null;
                var list = prizePopState.campaigns || [];
                for (var i = 0; i < list.length; i++) {
                    if (list[i].id === rowId) { summary = list[i]; break; }
                }
                if (summary && !summary.isMock) {
                    ppEditedIds[summary.id] = true;
                }
                send({ type: 'prizepop.updateCampaign', payload: { id: rowId, patch: patch } });
                ppShowFeedback('Saved', 'ok');
                return;
            }
        });

        /* Keep the form payload current as the user types — without this,
           clicking outside the form (e.g. into the games checkbox grid)
           would lose unsaved input. */
        ppRootEl.addEventListener('input', function () {
            if (ppFormOpen) ppCollectForm();
        });

        /* Status drives the interval dates in the lobby, so when the QA picks
           a status, reset the offset inputs to matching defaults. */
        ppRootEl.addEventListener('change', function (e) {
            if (!ppFormOpen || !ppForm) return;
            var sel = e.target;
            if (!sel || sel.getAttribute('name') !== 'pp-form:status') return;
            var offsets = PP_STATUS_OFFSETS[sel.value];
            if (!offsets) return;
            var startEl = ppRootEl.querySelector('[data-pp-form-field="startOffsetMin"]');
            var endEl = ppRootEl.querySelector('[data-pp-form-field="endOffsetMin"]');
            if (startEl) startEl.value = String(offsets[0]);
            if (endEl) endEl.value = String(offsets[1]);
            ppCollectForm();
        });
    }

    ppRenderState();

    /* ════════════ Campaigns: Rebate sub-tab ════════════ */
    /* Mirrors the PrizePop sub-tab. The state lives in rebateState,
       fed by rebate.state broadcasts; UI state (which row is expanded,
       fetch-panel visibility, form open/closed) is local to the popup. */

    var RB_LEVELS = ['GOLD', 'PURPLE', 'RED', 'BLUE', 'GREEN', 'MADNESS'];
    var RB_STATUSES = ['PROMO', 'ACTIVE', 'COMPLETED_VISIBLE'];

    var rbRootEl = document.getElementById('rb-rebate-root');
    var rbFeedbackEl = document.getElementById('rb-feedback');

    var rebateState = { mockMode: false, mocks: [], campaigns: [], defaultBaseCurrency: 'EUR' };
    var rbFeedbackTimer = null;

    var rbShowFetchPanel = true;
    var rbLastFetchError = null;
    var rbExpandedRowId = null;
    /* Draft cashback levels for the expanded row; only sent on Save when touched. */
    var rbEditLevels = null;
    var rbEditLevelsDirty = false;
    /* Joined / spentBetAmount touched in the expanded row. */
    var rbEditProgressDirty = false;
    var rbEditedIds = Object.create(null);
    var rbFormOpen = false;
    var rbForm = null;

    function rbShowFeedback(text, kind) {
        if (!rbFeedbackEl) return;
        rbFeedbackEl.textContent = text;
        rbFeedbackEl.className = 'jp-feedback show ' + (kind || 'ok');
        if (rbFeedbackTimer) window.clearTimeout(rbFeedbackTimer);
        rbFeedbackTimer = window.setTimeout(function () {
            if (rbFeedbackEl) {
                rbFeedbackEl.textContent = '';
                rbFeedbackEl.className = 'jp-feedback';
            }
        }, 3500);
    }

    function rbDefaultForm() {
        var cur = rebateState.defaultBaseCurrency || 'EUR';
        return {
            name: 'QA Test Rebate',
            terms: '',
            status: 'ACTIVE',
            level: 'PURPLE',
            baseCurrency: cur,
            startOffsetMin: 0,
            endOffsetMin: 240,
            minWinBetAmount: 300,
            games: [
                'ARSlot', 'BCSlot', 'BCBSlot', 'FBHRSlot', 'FVBPSlot',
                'FBRSlot', 'FVCLHSlot', 'FVCHTSlot', 'FVCHSlot', 'FDCSlot',
                'FDHSlot', 'FVDRSlot', 'FVJHSlot', 'FVLRSlot', 'FVSHRSlot',
                'FVSHSlot', 'FVSFSlot', 'FVSPSlot', 'FVSRSlot', 'TADSlot',
                'TBHRSlot', 'TNBPSlot', 'TNBRSlot', 'TNCLHSlot', 'TCHSlot',
                'TCHBSlot', 'TCWSlot', 'TNDCSlot', 'TNDHSlot', 'TNDRSlot',
                'TNJHSlot', 'TNJRSlot', 'TNLHSlot', 'TNLRSlot', 'TSDSlot',
                'TNSHRSlot', 'TNSHSlot', 'TNSFSlot', 'TNSPSlot', 'TNSRSlot',
                'OBCSlot', 'OBHSlot', 'OHBPSlot', 'OHBRSlot', 'OHCLHSlot',
                'OHCHTSlot', 'OHCHSlot', 'OSFSlot', 'OSHSlot', 'OSHSSlot',
            ],
            cashbackLevels: rbComputeMax([
                { levelNumber: 1, betRangeFrom: 0, betRangeTo: 1000, rebatePercent: 1, maxPayoutAmount: 0 },
                { levelNumber: 2, betRangeFrom: 1000.01, betRangeTo: 7000, rebatePercent: 1.5, maxPayoutAmount: 0 },
                { levelNumber: 3, betRangeFrom: 7000.01, betRangeTo: 13000, rebatePercent: 2, maxPayoutAmount: 0 },
            ]),
            joined: false,
            spentBetAmount: 0,
        };
    }

    /* Cashback levels: 1..3 rows. Level N+1 betRangeFrom is derived
       (read-only) as level N betRangeTo + 0.01; level 1 betRangeFrom is free. */
    var RB_MAX_LEVELS = 3;

    function rbRoundAmount(n) {
        return Math.round(n * 100) / 100;
    }

    function rbChainLevels(levels) {
        for (var i = 0; i < levels.length; i++) {
            levels[i].levelNumber = i + 1;
            if (i > 0) {
                levels[i].betRangeFrom = rbRoundAmount((Number(levels[i - 1].betRangeTo) || 0) + 0.01);
            }
        }
        return levels;
    }

    /* maxPayoutAmount = (To - From) x rebatePercent / 100, 2 decimals, clamped
       at 0. Not applied on edit-form prefill, so an untouched campaign keeps
       its stored maxes; the controller recomputes whatever is sent. */
    function rbComputeMax(levels) {
        for (var i = 0; i < levels.length; i++) {
            var l = levels[i];
            var raw = ((Number(l.betRangeTo) || 0) - (Number(l.betRangeFrom) || 0)) * (Number(l.rebatePercent) || 0) / 100;
            l.maxPayoutAmount = raw > 0 ? rbRoundAmount(raw) : 0;
        }
        return levels;
    }

    /* Fresh, chained copy of a levels array (defaults when missing/empty). */
    function rbCloneLevels(levels) {
        var src = Array.isArray(levels) && levels.length > 0 ? levels : rbDefaultForm().cashbackLevels;
        return rbChainLevels(src.slice(0, RB_MAX_LEVELS).map(function (l) {
            return {
                levelNumber: l.levelNumber,
                betRangeFrom: Number(l.betRangeFrom) || 0,
                betRangeTo: Number(l.betRangeTo) || 0,
                rebatePercent: Number(l.rebatePercent) || 0,
                maxPayoutAmount: Number(l.maxPayoutAmount) || 0,
            };
        }));
    }

    /* Level editors live in a container tagged data-rb-levels="form" (new
       mock form) or "edit" (expanded campaign row). */
    function rbLevelsFor(scope) {
        if (scope === 'edit') return rbEditLevels;
        return rbForm ? rbForm.cashbackLevels : null;
    }

    function rbLevelsContainer(scope) {
        return rbRootEl ? rbRootEl.querySelector('[data-rb-levels="' + scope + '"]') : null;
    }

    function rbReadLevelInputs(scope) {
        var levels = rbLevelsFor(scope);
        var container = rbLevelsContainer(scope);
        if (!levels || !container) return;
        var inputs = container.querySelectorAll('[data-rb-level-field]');
        for (var i = 0; i < inputs.length; i++) {
            var el = inputs[i];
            var lvl = levels[Number(el.getAttribute('data-rb-level-idx'))];
            var f = el.getAttribute('data-rb-level-field');
            if (!lvl || !f) continue;
            var v = Number(el.value);
            lvl[f] = isFinite(v) ? v : 0;
        }
        rbComputeMax(rbChainLevels(levels));
        /* Push derived From / Max values back into the read-only inputs
           without a full re-render so the focused input keeps its caret. */
        for (var d = 0; d < levels.length; d++) {
            var fromEl = d > 0 ? container.querySelector('[data-rb-level-idx="' + d + '"][data-rb-level-field="betRangeFrom"]') : null;
            if (fromEl) fromEl.value = String(levels[d].betRangeFrom);
            var maxEl = container.querySelector('[data-rb-level-idx="' + d + '"][data-rb-level-field="maxPayoutAmount"]');
            if (maxEl) maxEl.value = String(levels[d].maxPayoutAmount);
        }
        var totalEl = container.querySelector('[data-rb-levels-total]');
        if (totalEl) totalEl.value = String(rbSumMax(levels));
    }

    function rbRerenderLevels(scope) {
        var levels = rbLevelsFor(scope);
        var container = rbLevelsContainer(scope);
        if (levels && container) container.innerHTML = rbRenderLevels(levels);
    }

    function rbLevelInputMarkup(idx, field, value, derivedTitle) {
        return '<input type="number" step="0.01" data-rb-level-idx="' + idx + '" data-rb-level-field="' + field + '"'
            + ' value="' + escapeHtml(String(value)) + '"'
            + (derivedTitle ? ' readonly title="' + escapeHtml(derivedTitle) + '" style="opacity:0.6"' : '')
            + ' />';
    }

    /* Campaign maxPayoutAmount = sum of the levels' max payout, 2 decimals. */
    function rbSumMax(levels) {
        var sum = 0;
        for (var i = 0; i < levels.length; i++) sum += Number(levels[i].maxPayoutAmount) || 0;
        return rbRoundAmount(sum);
    }

    /* totalOverride: stored campaign value shown on edit-form prefill until
       the levels are touched; otherwise the derived sum is shown. */
    function rbRenderLevels(levels, totalOverride) {
        var canRemove = levels.length > 1;
        var canAdd = levels.length < RB_MAX_LEVELS;
        var grid = 'display:grid;grid-template-columns:24px repeat(4,minmax(0,1fr)) 32px;gap:6px;align-items:center;margin-bottom:6px';
        var html = ''
            + '<div class="bo-edit-field">'
            +     '<label>Cashback levels <span class="bo-field-hint">1–3 levels; next From = previous To + 0.01; Max = (To − From) × % / 100</span></label>'
            +     '<div style="' + grid + '">'
            +         '<span></span>'
            +         '<span class="bo-field-hint" style="margin:0">From</span>'
            +         '<span class="bo-field-hint" style="margin:0">To</span>'
            +         '<span class="bo-field-hint" style="margin:0">Rebate %</span>'
            +         '<span class="bo-field-hint" style="margin:0">Max payout</span>'
            +         '<span></span>'
            +     '</div>';
        for (var i = 0; i < levels.length; i++) {
            var l = levels[i];
            html += ''
                + '<div style="' + grid + '">'
                +     '<strong>L' + (i + 1) + '</strong>'
                +     rbLevelInputMarkup(i, 'betRangeFrom', l.betRangeFrom, i > 0 ? 'Derived: previous level To + 0.01' : '')
                +     rbLevelInputMarkup(i, 'betRangeTo', l.betRangeTo, '')
                +     rbLevelInputMarkup(i, 'rebatePercent', l.rebatePercent, '')
                +     rbLevelInputMarkup(i, 'maxPayoutAmount', l.maxPayoutAmount, 'Derived: (To - From) x Rebate % / 100')
                +     (canRemove
                        ? '<button type="button" class="danger" data-rb-action="level-remove" data-rb-level-idx="' + i + '" title="Remove level" style="padding:6px 0">×</button>'
                        : '<span></span>')
                + '</div>';
        }
        html += ''
            +     '<button type="button" data-rb-action="level-add"' + (canAdd ? '' : ' disabled style="opacity:0.5;cursor:not-allowed"') + '>+ Add level</button>'
            + '</div>'
            + '<div class="bo-edit-field"><label>Max payout amount</label>'
            +     '<input type="number" step="0.01" data-rb-levels-total readonly title="Sum of all levels&#39; max payout" style="opacity:0.6"'
            +     ' value="' + escapeHtml(String(typeof totalOverride === 'number' ? totalOverride : rbSumMax(levels))) + '" />'
            + '</div>';
        return html;
    }

    /* Joined checkbox + spentBetAmount (shown only when joined). The form
       scope reuses data-rb-form-field so rbCollectForm picks them up; the
       edit scope uses data-rb-progress-field so they are only patched when
       touched (see save-edit). userProgress itself is computed lobby-side. */
    function rbRenderProgressFields(scope, joined, spent) {
        var attr = scope === 'form' ? 'data-rb-form-field' : 'data-rb-progress-field';
        return ''
            + '<div data-rb-progress="' + scope + '">'
            +     '<div class="bo-edit-field">'
            +         '<label>User progress</label>'
            +         '<div class="bo-edit-flags"><label><input type="checkbox" ' + attr + '="joined"' + (joined ? ' checked' : '') + ' /> Joined</label></div>'
            +     '</div>'
            +     '<div class="bo-edit-field" data-rb-spent-wrap' + (joined ? '' : ' style="display:none"') + '>'
            +         '<label>spentBetAmount <span class="bo-field-hint">drives levelProgress / claimedWin</span></label>'
            +         '<input type="number" min="0" step="0.01" ' + attr + '="spentBetAmount" value="' + escapeHtml(String(Number(spent) || 0)) + '" />'
            +     '</div>'
            + '</div>';
    }

    function rbSyncSpentVisibility(target) {
        var box = target && target.closest ? target.closest('[data-rb-progress]') : null;
        if (!box || target.type !== 'checkbox') return;
        var wrap = box.querySelector('[data-rb-spent-wrap]');
        if (wrap) wrap.style.display = target.checked ? '' : 'none';
    }

    function rbSelectMarkup(name, options, current, editField) {
        var editAttr = editField ? ' data-rb-edit-field="' + editField + '"' : '';
        var html = '<select name="' + name + '"' + editAttr + '>';
        for (var i = 0; i < options.length; i++) {
            var v = options[i];
            html += '<option value="' + v + '"' + (v === current ? ' selected' : '') + '>' + v + '</option>';
        }
        return html + '</select>';
    }

    function rbDateInputMarkup(name, value) {
        var local = '';
        if (value) {
            var d = new Date(value);
            if (!isNaN(d.getTime())) {
                var pad = function (n) { return n < 10 ? '0' + n : String(n); };
                local = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
                    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
            }
        }
        return '<input type="datetime-local" data-rb-edit-field="' + name + '" value="' + escapeHtml(local) + '" />';
    }

    function rbRenderEmptyState() {
        return ''
            + '<div class="section" style="text-align:center;padding:24px 16px">'
            +     '<h2 style="margin-bottom:8px">Mocking is off</h2>'
            +     '<button type="button" class="primary" data-rb-action="enable-mocking">Enable mocking</button>'
            +     '<div class="hint" style="margin-top:10px">Pauses polling and lets you fetch + edit + add Rebate campaigns.</div>'
            + '</div>';
    }

    function rbRenderBanner() {
        return ''
            + '<div class="hint" style="background:var(--accent-bg);border:1px solid var(--accent-dim);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:10px;display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +     '<span>Mocking enabled — polling paused. Edits and mocks survive until you disable mocking.</span>'
            +     '<button type="button" class="danger" data-rb-action="disable-mocking">Disable mocking</button>'
            + '</div>';
    }

    function rbRenderFetchPanel() {
        var errorHtml = rbLastFetchError
            ? '<div class="hint" style="background:var(--surface-2);border-left:3px solid var(--status-error);color:var(--status-error);border-radius:var(--radius-sm);padding:10px 14px;margin-bottom:10px">' + escapeHtml(rbLastFetchError) + '</div>'
            : '';
        return ''
            + '<div class="section">'
            +     '<h2>Fetch Rebate campaigns</h2>'
            +     errorHtml
            +     '<div class="bo-edit-field">'
            +         '<label>Paste session string <span class="bo-field-hint">format: sessionKey=...&playerId=...</span></label>'
            +         '<input type="text" id="rb-fetch-input" autocomplete="off" placeholder="sessionKey=abc&playerId=123" />'
            +     '</div>'
            +     '<div class="row" style="margin-top:8px;gap:8px">'
            +         '<button type="button" class="primary" data-rb-action="fetch">Fetch campaigns</button>'
            +         '<button type="button" data-rb-action="skip-fetch">Skip — start adding mocks</button>'
            +     '</div>'
            + '</div>';
    }

    function rbRenderEditForm(c) {
        /* Draft is created once per expand from the campaign's current levels. */
        if (!rbEditLevels) rbEditLevels = rbCloneLevels(c.cashbackLevels);
        return ''
            + '<div class="rb-edit-form" style="padding:8px 0">'
            +     '<div class="bo-edit-field"><label>Name</label><input type="text" data-rb-edit-field="name" value="' + escapeHtml(c.name) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Terms</label><textarea data-rb-edit-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(c.terms || '') + '</textarea></div>'
            +     '<div class="bo-edit-field"><label>Status</label>' + rbSelectMarkup('rb-edit:status', RB_STATUSES, c.status, 'status') + '</div>'
            +     '<div class="bo-edit-field"><label>Level</label>' + rbSelectMarkup('rb-edit:level', RB_LEVELS, c.level, 'level') + '</div>'
            +     '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-rb-edit-field="baseCurrency" value="' + escapeHtml(c.baseCurrency) + '" /></div>'
            +     '<h2 style="margin-top:12px">Dates</h2>'
            +     '<div class="bo-edit-field"><label>Start</label>' + rbDateInputMarkup('startDate', c.startDate) + '</div>'
            +     '<div class="bo-edit-field"><label>End</label>' + rbDateInputMarkup('endDate', c.endDate) + '</div>'
            +     '<div class="bo-edit-field"><label>Min win bet amount</label><input type="number" min="0" step="0.01" data-rb-edit-field="minWinBetAmount" value="' + escapeHtml(String(c.minWinBetAmount)) + '" /></div>'
            +     '<div data-rb-levels="edit">' + rbRenderLevels(rbEditLevels, rbEditLevelsDirty ? null : Number(c.maxPayoutAmount) || 0) + '</div>'
            +     rbRenderProgressFields('edit', !!c.joined, c.spentBetAmount)
            +     '<div class="row" style="margin-top:10px;gap:8px">'
            +         '<button type="button" class="primary" data-rb-action="save-edit">Save</button>'
            +         '<button type="button" data-rb-action="collapse-edit">Collapse</button>'
            +     '</div>'
            + '</div>';
    }

    function rbRenderCampaignRow(c) {
        var isExpanded = rbExpandedRowId === c.id;
        var badge = '';
        if (c.isMock) {
            badge = '<span class="badge" style="background:var(--accent);color:#ffffff">MOCK</span>';
        } else if (rbEditedIds[c.id]) {
            badge = '<span class="badge" style="background:var(--status-warn);color:#ffffff">EDITED</span>';
        }
        var removeBtn = c.isMock
            ? '<button type="button" class="danger" data-rb-action="remove-mock" data-rb-id="' + escapeHtml(c.id) + '">Remove</button>'
            : '';
        return ''
            + '<div class="bo-edit-field" data-rb-row="' + escapeHtml(c.id) + '" style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 11px;margin-bottom:6px">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between">'
            +         '<button type="button" data-rb-action="toggle-row" data-rb-id="' + escapeHtml(c.id) + '" style="flex:1;min-width:0;text-align:left;background:none;border:0;color:var(--text);padding:0;font:inherit;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'
            +             '<strong>' + escapeHtml(c.name) + '</strong> '
            +             badge + ' '
            +             '<span class="bo-game-item-key">' + escapeHtml(c.status) + ' / ' + escapeHtml(c.level) + ' · fund ' + escapeHtml(String(c.maxPayoutAmount)) + '</span>'
            +         '</button>'
            +         removeBtn
            +     '</div>'
            +     (isExpanded ? rbRenderEditForm(c) : '')
            + '</div>';
    }

    function rbRenderListPanel() {
        var campaigns = rebateState.campaigns || [];
        var listHtml = '';
        if (campaigns.length === 0) {
            listHtml = '<div class="hint-faint">No Rebate campaigns. Use the form or re-fetch with valid creds.</div>';
        } else {
            for (var i = 0; i < campaigns.length; i++) {
                listHtml += rbRenderCampaignRow(campaigns[i]);
            }
        }
        return ''
            + '<div class="section">'
            +     '<div style="display:flex;align-items:center;gap:8px;justify-content:space-between;margin-bottom:8px">'
            +         '<h2 style="margin:0">Rebate campaigns <span class="badge">' + campaigns.length + '</span></h2>'
            +         '<button type="button" data-rb-action="show-fetch">Re-fetch with different creds</button>'
            +     '</div>'
            +     listHtml
            +     '<div class="row" style="margin-top:10px">'
            +         '<button type="button" class="primary" data-rb-action="open-form">+ Add mock Rebate campaign</button>'
            +     '</div>'
            + '</div>';
    }

    function rbRenderGamesCheckboxes(games) {
        var defaults = rbDefaultForm().games;
        var selected = Object.create(null);
        for (var k = 0; k < games.length; k++) {
            selected[games[k]] = true;
        }
        var html = '<div class="bo-edit-flags" style="flex-wrap:wrap;gap:6px 12px">';
        for (var i = 0; i < defaults.length; i++) {
            var g = defaults[i];
            html += '<label><input type="checkbox" data-rb-form-game="' + escapeHtml(g) + '"' + (selected[g] ? ' checked' : '') + ' /> ' + escapeHtml(g) + '</label>';
        }
        return html + '</div>';
    }

    function rbRenderForm() {
        if (!rbFormOpen || !rbForm) return '';
        var f = rbForm;
        return ''
            + '<div class="section" style="border:1px solid var(--accent-dim);background:var(--accent-bg)">'
            +     '<h2 style="margin-bottom:6px">New mock Rebate campaign</h2>'
            +     '<div class="bo-edit-field"><label>Name</label><input type="text" data-rb-form-field="name" value="' + escapeHtml(f.name) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Terms</label><textarea data-rb-form-field="terms" rows="2" style="width:100%;background:var(--surface-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius-sm);padding:7px 9px;font-family:var(--font-sans);font-size:11px">' + escapeHtml(f.terms) + '</textarea></div>'
            +     '<div class="bo-edit-field"><label>Status</label>' + rbSelectMarkup('rb-form:status', RB_STATUSES, f.status) + '</div>'
            +     '<div class="bo-edit-field"><label>Level</label>' + rbSelectMarkup('rb-form:level', RB_LEVELS, f.level) + '</div>'
            +     '<div class="bo-edit-field"><label>Base currency</label><input type="text" data-rb-form-field="baseCurrency" value="' + escapeHtml(f.baseCurrency) + '" /></div>'
            +     '<div class="hint">Offsets in minutes relative to "now" at submit.</div>'
            +     '<div class="bo-edit-field"><label>Start offset (min)</label><input type="number" step="1" data-rb-form-field="startOffsetMin" value="' + escapeHtml(String(f.startOffsetMin)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>End offset (min)</label><input type="number" step="1" data-rb-form-field="endOffsetMin" value="' + escapeHtml(String(f.endOffsetMin)) + '" /></div>'
            +     '<div class="bo-edit-field"><label>Min win bet amount</label><input type="number" min="0" step="0.01" data-rb-form-field="minWinBetAmount" value="' + escapeHtml(String(f.minWinBetAmount)) + '" /></div>'
            +     '<div data-rb-levels="form">' + rbRenderLevels(f.cashbackLevels) + '</div>'
            +     rbRenderProgressFields('form', !!f.joined, f.spentBetAmount)
            +     '<div class="bo-edit-field"><label>Games</label>' + rbRenderGamesCheckboxes(f.games) + '</div>'
            +     '<div class="row" style="margin-top:14px;gap:8px;justify-content:flex-end">'
            +         '<button type="button" data-rb-action="form-cancel">Cancel</button>'
            +         '<button type="button" class="primary" data-rb-action="form-submit">Create</button>'
            +     '</div>'
            + '</div>';
    }

    function rbHasCampaigns() {
        return rebateState.campaigns && rebateState.campaigns.length > 0;
    }

    function rbRenderState() {
        if (!rbRootEl) return;
        var html = '';
        if (!rebateState.mockMode) {
            html = rbRenderEmptyState();
        } else {
            html += rbRenderBanner();
            if (rbShowFetchPanel && !rbHasCampaigns()) {
                html += rbRenderFetchPanel();
            } else if (rbShowFetchPanel && rbHasCampaigns()) {
                html += rbRenderFetchPanel();
                html += rbRenderListPanel();
            } else {
                html += rbRenderListPanel();
            }
            if (rbFormOpen) {
                html += rbRenderForm();
            }
        }
        rbRootEl.innerHTML = html;
    }

    function rbOpenForm() {
        rbFormOpen = true;
        rbForm = rbDefaultForm();
        rbRenderState();
    }

    function rbCloseForm() {
        rbFormOpen = false;
        rbForm = null;
        rbRenderState();
    }

    function rbCollectForm() {
        if (!rbFormOpen || !rbForm || !rbRootEl) return;
        var fields = rbRootEl.querySelectorAll('[data-rb-form-field]');
        for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-rb-form-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                rbForm[f] = !!el.checked;
            } else if (el.type === 'number') {
                rbForm[f] = el.value === '' ? 0 : Number(el.value);
            } else {
                rbForm[f] = el.value;
            }
        }
        var statusSel = rbRootEl.querySelector('select[name="rb-form:status"]');
        var levelSel = rbRootEl.querySelector('select[name="rb-form:level"]');
        if (statusSel) rbForm.status = statusSel.value;
        if (levelSel) rbForm.level = levelSel.value;
        var gameBoxes = rbRootEl.querySelectorAll('[data-rb-form-game]');
        var games = [];
        for (var j = 0; j < gameBoxes.length; j++) {
            var gb = gameBoxes[j];
            if (gb.checked) games.push(gb.getAttribute('data-rb-form-game'));
        }
        rbForm.games = games;
        rbReadLevelInputs('form');
    }

    function rbSubmitForm() {
        if (!rbForm) return;
        var f = rbForm;
        var payload = {
            name: f.name || 'QA Test Rebate',
            terms: f.terms || '',
            status: f.status,
            level: f.level,
            baseCurrency: f.baseCurrency || (rebateState.defaultBaseCurrency || 'EUR'),
            startOffsetMin: Number(f.startOffsetMin) || 0,
            endOffsetMin: Number(f.endOffsetMin) || 0,
            minWinBetAmount: Number(f.minWinBetAmount) || 0,
            games: Array.isArray(f.games) ? f.games.slice() : [],
            cashbackLevels: rbCloneLevels(f.cashbackLevels),
            joined: !!f.joined,
            spentBetAmount: Number(f.spentBetAmount) || 0,
        };
        send({ type: 'rebate.createMockCampaign', payload: { form: payload } });
        rbCloseForm();
    }

    function rbCollectEditForm(rowEl) {
        var fields = rowEl.querySelectorAll('[data-rb-edit-field]');
        var patch = {};
        for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var f = el.getAttribute('data-rb-edit-field');
            if (!f) continue;
            if (el.type === 'checkbox') {
                patch[f] = !!el.checked;
            } else if (el.type === 'number') {
                var n = Number(el.value);
                patch[f] = isFinite(n) ? n : 0;
            } else if (el.type === 'datetime-local') {
                if (el.value) {
                    var d = new Date(el.value);
                    patch[f] = isNaN(d.getTime()) ? '' : d.toISOString();
                } else {
                    patch[f] = '';
                }
            } else {
                patch[f] = el.value;
            }
        }
        return patch;
    }

    if (rbRootEl) {
        rbRootEl.addEventListener('click', function (e) {
            var t = e.target;
            if (!t || !t.getAttribute) return;
            var action = t.getAttribute('data-rb-action');
            if (!action && t.closest) {
                var btn = t.closest('[data-rb-action]');
                if (btn) {
                    action = btn.getAttribute('data-rb-action');
                    t = btn;
                }
            }
            if (!action) return;

            if (action === 'enable-mocking') {
                send({ type: 'rebate.enableMocking' });
                rbShowFetchPanel = true;
                rbLastFetchError = null;
                return;
            }
            if (action === 'disable-mocking') {
                if (window.confirm('Disable mocking? This clears every mock Rebate campaign and resumes real polling.')) {
                    send({ type: 'rebate.disableMocking' });
                    rbShowFetchPanel = true;
                    rbLastFetchError = null;
                    rbExpandedRowId = null;
                    rbEditLevels = null;
                    rbEditLevelsDirty = false;
                    rbEditProgressDirty = false;
                    rbEditedIds = Object.create(null);
                    rbCloseForm();
                }
                return;
            }
            if (action === 'fetch') {
                var inputEl = document.getElementById('rb-fetch-input');
                var raw = inputEl ? String(inputEl.value || '').trim() : '';
                if (!raw) {
                    rbLastFetchError = 'Paste a session string first.';
                    rbRenderState();
                    return;
                }
                var params;
                try {
                    params = new URLSearchParams(raw);
                } catch (err) {
                    rbLastFetchError = 'Could not parse — expected sessionKey=...&playerId=...';
                    rbRenderState();
                    return;
                }
                var sk = params.get('sessionKey');
                var pid = params.get('playerId');
                if (!sk || !pid) {
                    rbLastFetchError = 'Missing sessionKey or playerId in the pasted string.';
                    rbRenderState();
                    return;
                }
                rbLastFetchError = null;
                send({ type: 'rebate.fetchExternal', payload: { sessionKey: sk, playerId: pid } });
                rbShowFeedback('Fetching campaigns…', 'ok');
                return;
            }
            if (action === 'skip-fetch') {
                rbShowFetchPanel = false;
                rbLastFetchError = null;
                rbRenderState();
                return;
            }
            if (action === 'show-fetch') {
                rbShowFetchPanel = true;
                rbRenderState();
                return;
            }
            if (action === 'open-form') {
                rbOpenForm();
                return;
            }
            if (action === 'form-cancel') {
                rbCloseForm();
                return;
            }
            if (action === 'form-submit') {
                rbCollectForm();
                rbSubmitForm();
                return;
            }
            if (action === 'level-add' || action === 'level-remove') {
                var lvlBox = t.closest && t.closest('[data-rb-levels]');
                var scope = lvlBox ? lvlBox.getAttribute('data-rb-levels') : null;
                var lvls = scope ? rbLevelsFor(scope) : null;
                if (!lvls) return;
                rbReadLevelInputs(scope);
                if (action === 'level-add') {
                    if (lvls.length >= RB_MAX_LEVELS) return;
                    var last = lvls[lvls.length - 1];
                    var lastTo = Number(last.betRangeTo) || 0;
                    lvls.push({
                        levelNumber: lvls.length + 1,
                        betRangeFrom: 0,
                        betRangeTo: rbRoundAmount(lastTo + 1000),
                        rebatePercent: Number(last.rebatePercent) || 0,
                        maxPayoutAmount: 0,
                    });
                } else {
                    var rmIdx = Number(t.getAttribute('data-rb-level-idx'));
                    if (lvls.length <= 1 || !(rmIdx >= 0 && rmIdx < lvls.length)) return;
                    lvls.splice(rmIdx, 1);
                }
                rbComputeMax(rbChainLevels(lvls));
                rbRerenderLevels(scope);
                if (scope === 'edit') rbEditLevelsDirty = true;
                return;
            }
            if (action === 'toggle-row') {
                var id = t.getAttribute('data-rb-id');
                rbExpandedRowId = (rbExpandedRowId === id) ? null : id;
                rbEditLevels = null;
                rbEditLevelsDirty = false;
                rbEditProgressDirty = false;
                rbRenderState();
                return;
            }
            if (action === 'collapse-edit') {
                rbExpandedRowId = null;
                rbEditLevels = null;
                rbEditLevelsDirty = false;
                rbEditProgressDirty = false;
                rbRenderState();
                return;
            }
            if (action === 'remove-mock') {
                var rmId = t.getAttribute('data-rb-id');
                if (rmId) {
                    send({ type: 'rebate.removeMockCampaign', payload: { id: rmId } });
                }
                return;
            }
            if (action === 'save-edit') {
                var rowEl = t.closest && t.closest('[data-rb-row]');
                if (!rowEl) return;
                var rowId = rowEl.getAttribute('data-rb-row');
                var patch = rbCollectEditForm(rowEl);
                var levelsChanged = !!(rbEditLevels && rbEditLevelsDirty);
                if (levelsChanged) {
                    rbReadLevelInputs('edit');
                    patch.cashbackLevels = rbCloneLevels(rbEditLevels);
                    rbEditLevelsDirty = false;
                }
                var joinedEl = rowEl.querySelector('[data-rb-progress-field="joined"]');
                var spentEl = rowEl.querySelector('[data-rb-progress-field="spentBetAmount"]');
                var joinedVal = !!(joinedEl && joinedEl.checked);
                if (rbEditProgressDirty || (levelsChanged && joinedVal)) {
                    patch.joined = joinedVal;
                    /* Only spentBetAmount matters; the lobby-side controller
                       recomputes levelProgress / claimedWin / totalWinAmount. */
                    patch.userProgress = joinedVal
                        ? { spentBetAmount: spentEl ? (Number(spentEl.value) || 0) : 0, levelProgress: 0, claimedWin: 0 }
                        : null;
                    rbEditProgressDirty = false;
                }
                var summary = null;
                var list = rebateState.campaigns || [];
                for (var i = 0; i < list.length; i++) {
                    if (list[i].id === rowId) { summary = list[i]; break; }
                }
                if (summary && !summary.isMock) {
                    rbEditedIds[summary.id] = true;
                }
                send({ type: 'rebate.updateCampaign', payload: { id: rowId, patch: patch } });
                rbShowFeedback('Saved', 'ok');
                return;
            }
        });

        rbRootEl.addEventListener('input', function (e) {
            if (rbFormOpen) rbCollectForm();
            rbSyncSpentVisibility(e.target);
            if (e.target && e.target.closest && e.target.closest('[data-rb-progress="edit"]')) {
                rbEditProgressDirty = true;
            }
            var inEditLevels = e.target && e.target.closest && e.target.closest('[data-rb-levels="edit"]');
            if (rbEditLevels && inEditLevels) {
                rbReadLevelInputs('edit');
                rbEditLevelsDirty = true;
            }
        });
    }

    rbRenderState();

    /* ── Wire up buttons ────────────────────────────────────────── */

    modeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            var mode = btn.getAttribute('data-mode');
            send({ type: 'setMode', payload: { mode: mode } });
        });
    });

    document.getElementById('preload-all').addEventListener('click', function () {
        send({ type: 'preloadAllLanguages' });
    });

    document.getElementById('export-csv').addEventListener('click', function () {
        send({ type: 'exportMissingKeys' });
    });

    document.getElementById('clear-missing').addEventListener('click', function () {
        send({ type: 'clearMissingKeys' });
    });

    inspectorFilterButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            inspectorFilter = btn.getAttribute('data-filter');
            inspectorFilterButtons.forEach(function (b) {
                b.classList.toggle('active', b === btn);
            });
            renderInspector();
        });
    });

    if (inspectorSearchEl) {
        inspectorSearchEl.addEventListener('input', function () {
            inspectorQuery = inspectorSearchEl.value;
            renderInspector();
        });
    }

    if (inspectorCloseEl) {
        inspectorCloseEl.addEventListener('click', closeInspector);
    }

    /* ── Tab navigation ─────────────────────────────────────────── */

    var tabButtons = document.querySelectorAll('.tab-nav button[data-tab]');
    var tabPanels = document.querySelectorAll('.tab-panel[data-panel]');

    function showTab(name) {
        tabButtons.forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-tab') === name);
        });
        tabPanels.forEach(function (p) {
            p.classList.toggle('active', p.getAttribute('data-panel') === name);
        });
    }

    tabButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            showTab(btn.getAttribute('data-tab'));
        });
    });

    /* ── Theme toggle ────────────────────────────────────────── */

    var themeBtn = document.getElementById('theme-btn');

    function syncThemeIcon() {
        if (!themeBtn) return;
        var current = document.documentElement.getAttribute('data-theme') || 'dark';
        // Show the icon for the theme you'd switch TO when you click —
        // sun in dark mode (click → light), moon in light mode (click → dark).
        themeBtn.textContent = current === 'dark' ? '☀' : '☾';
        themeBtn.title = current === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
    }

    if (themeBtn) {
        // Sync the icon with whatever the head script set so the button
        // matches the current state on first paint.
        syncThemeIcon();
        themeBtn.addEventListener('click', function () {
            var current = document.documentElement.getAttribute('data-theme') || 'dark';
            var next = current === 'dark' ? 'light' : 'dark';
            document.documentElement.setAttribute('data-theme', next);
            syncThemeIcon();
            // Persist explicit choice so it survives reloads. Until the user
            // clicks once, the popup follows OS prefers-color-scheme.
            try {
                localStorage.setItem('qa-panel-theme', next);
            } catch (e) {
                // localStorage may be unavailable in some sandboxed contexts
            }
        });
    }

    /* ── Reload button ──────────────────────────────────────────── */

    var reloadBtn = document.getElementById('reload-btn');
    if (reloadBtn) {
        reloadBtn.addEventListener('click', function () {
            // Soft refresh: clear popup-local state, ask the lobby for a fresh
            // snapshot. Doesn't physically reload the popup window — that
            // would trip the popup blocker since the click here propagates as
            // a postMessage, not a native gesture in the lobby's context.
            inspection = null;
            inspectorFilter = 'all';
            inspectorQuery = '';
            if (inspectorSearchEl) inspectorSearchEl.value = '';
            inspectorFilterButtons.forEach(function (b) {
                b.classList.toggle('active', b.getAttribute('data-filter') === 'all');
            });
            renderInspector();
            send({ type: 'requestState' });

            // Visual feedback: brief 360deg spin.
            reloadBtn.classList.add('spinning');
            setTimeout(function () {
                reloadBtn.classList.remove('spinning');
            }, 400);
        });
    }

    /* ── Listen to lobby messages ───────────────────────────────── */

    window.addEventListener('message', function (event) {
        var data = event.data;
        if (!data || data.namespace !== NAMESPACE) return;

        if (data.type === 'state') {
            applyState(data.payload);
        } else if (data.type === 'missingKey') {
            // Optimistic incremental update — full state will follow soon.
            var existing = (state.missingKeys || []).find(function (r) {
                return r.lang === data.payload.lang && r.key === data.payload.key;
            });
            if (existing) {
                existing.count += 1;
            } else {
                state.missingKeys = (state.missingKeys || []).concat([{
                    lang: data.payload.lang,
                    key: data.payload.key,
                    count: 1,
                    firstSeen: Date.now(),
                }]);
            }
            renderMissing();
        } else if (data.type === 'exportReady') {
            downloadCsv(data.payload.csv, data.payload.filename);
        } else if (data.type === 'inspection') {
            inspection = data.payload;
            renderInspector();
            renderCoverage(); // refresh "selected" row highlight
            // Auto-scroll the inspector into view so the click-to-inspect feels responsive.
            if (inspectorSectionEl) {
                inspectorSectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else if (data.type === 'jackpot.state') {
            jackpotState = data.payload;
            renderJackpotMock();
        } else if (data.type === 'jackpot.winStatsGenerated') {
            if (data.payload.rows >= 0) {
                showJpWinStatsFeedback(
                    'generated ' + data.payload.rows + ' wins for ' + data.payload.jackpotName + ' — mock mode is now ON',
                    'ok'
                );
            } else {
                showJpWinStatsFeedback(
                    'nothing generated for ' + data.payload.jackpotName + ' — games are not loaded yet',
                    'err'
                );
            }
        } else if (data.type === 'backoffice.state') {
            backofficeState = data.payload;
            renderBackoffice();
            renderBackofficeGames();
            renderCasinoTab();
            // The Jackpot / Bonus toggle lives in the Jackpot Mock tab but
            // reads backofficeState.noJackpot — keep it synced on every
            // backoffice broadcast.
            renderJackpotMock();
        } else if (data.type === 'backoffice.thumbnailResults') {
            thumbCheckRunning = false;
            if (boThumbCheckBtnEl) {
                boThumbCheckBtnEl.disabled = false;
                boThumbCheckBtnEl.textContent = 'Check missing thumbnails';
            }
            renderThumbResults(data.payload);
        } else if (data.type === 'preset.state') {
            presetState = data.payload;
            renderPresetState();
        } else if (data.type === 'preset.exportReady') {
            downloadJson(data.payload.json, data.payload.filename);
            showPresetFeedback('Preset exported.', 'ok');
        } else if (data.type === 'sentry.state') {
            sentryState = data.payload;
            renderSentryTab();
        } else if (data.type === 'jira.state') {
            jiraState = data.payload;
            renderJiraTab();
        } else if (data.type === 'console.state') {
            consoleState = data.payload;
            renderConsoleTab();
        } else if (data.type === 'environment.state') {
            environmentState = data.payload;
            renderEnvironmentTab();
        } else if (data.type === 'jackpotWidget.state') {
            jpwState = data.payload;
            renderJackpotWidgetTab();
        } else if (data.type === 'measure.state') {
            msState = data.payload;
            renderMeasureTab();
        } else if (data.type === 'campaigns.state') {
            campaignsState = data.payload;
            // Forget any "edited" flags for ids that no longer exist (e.g.
            // after a re-fetch wiped the previous list).
            var keepIds = Object.create(null);
            for (var ci = 0; ci < (campaignsState.tournaments || []).length; ci++) {
                keepIds[campaignsState.tournaments[ci].id] = true;
            }
            for (var k in cmEditedIds) {
                if (!keepIds[k]) delete cmEditedIds[k];
            }
            cmRenderState();
        } else if (data.type === 'campaigns.created') {
            cmShowFeedback('Mock tournament created — id ' + data.payload.id, 'ok');
        } else if (data.type === 'campaigns.fetchResult') {
            var fr = data.payload;
            if (fr.ok) {
                cmLastFetchError = null;
                cmShowFetchPanel = false;
                cmShowFeedback('Fetched ' + (fr.count || 0) + ' tournaments.', 'ok');
                cmRenderState();
            } else {
                if (fr.status === 0) {
                    cmLastFetchError = 'Fetch failed: ' + (fr.message || 'Network error');
                } else if (fr.status && fr.status >= 400 && fr.status < 500) {
                    cmLastFetchError = 'Invalid or expired session — try fresh creds (' + fr.status + ').';
                } else {
                    cmLastFetchError = 'Fetch failed (' + (fr.status || '?') + '): ' + (fr.message || 'Unknown');
                }
                cmRenderState();
            }
        } else if (data.type === 'prizepop.state') {
            prizePopState = data.payload;
            var ppKeepIds = Object.create(null);
            for (var pci = 0; pci < (prizePopState.campaigns || []).length; pci++) {
                ppKeepIds[prizePopState.campaigns[pci].id] = true;
            }
            for (var ppk in ppEditedIds) {
                if (!ppKeepIds[ppk]) delete ppEditedIds[ppk];
            }
            ppRenderState();
        } else if (data.type === 'prizepop.created') {
            ppShowFeedback('Mock PrizePop campaign created — id ' + data.payload.id, 'ok');
        } else if (data.type === 'prizepop.fetchResult') {
            var ppFr = data.payload;
            if (ppFr.ok) {
                ppLastFetchError = null;
                ppShowFetchPanel = false;
                ppShowFeedback('Fetched ' + (ppFr.count || 0) + ' PrizePop campaigns.', 'ok');
                ppRenderState();
            } else {
                if (ppFr.status === 0) {
                    ppLastFetchError = 'Fetch failed: ' + (ppFr.message || 'Network error');
                } else if (ppFr.status && ppFr.status >= 400 && ppFr.status < 500) {
                    ppLastFetchError = 'Invalid or expired session — try fresh creds (' + ppFr.status + ').';
                } else {
                    ppLastFetchError = 'Fetch failed (' + (ppFr.status || '?') + '): ' + (ppFr.message || 'Unknown');
                }
                ppRenderState();
            }
        } else if (data.type === 'rebate.state') {
            rebateState = data.payload;
            var rbKeepIds = Object.create(null);
            for (var rci = 0; rci < (rebateState.campaigns || []).length; rci++) {
                rbKeepIds[rebateState.campaigns[rci].id] = true;
            }
            for (var rbk in rbEditedIds) {
                if (!rbKeepIds[rbk]) delete rbEditedIds[rbk];
            }
            rbRenderState();
        } else if (data.type === 'rebate.created') {
            rbShowFeedback('Mock Rebate campaign created — id ' + data.payload.id, 'ok');
        } else if (data.type === 'rebate.fetchResult') {
            var rbFr = data.payload;
            if (rbFr.ok) {
                rbLastFetchError = null;
                rbShowFetchPanel = false;
                rbShowFeedback('Fetched ' + (rbFr.count || 0) + ' Rebate campaigns.', 'ok');
                rbRenderState();
            } else {
                if (rbFr.status === 0) {
                    rbLastFetchError = 'Fetch failed: ' + (rbFr.message || 'Network error');
                } else if (rbFr.status && rbFr.status >= 400 && rbFr.status < 500) {
                    rbLastFetchError = 'Invalid or expired session — try fresh creds (' + rbFr.status + ').';
                } else {
                    rbLastFetchError = 'Fetch failed (' + (rbFr.status || '?') + '): ' + (rbFr.message || 'Unknown');
                }
                rbRenderState();
            }
        } else if (data.type === 'figma.state') {
            figmaState = data.payload;
            renderFigmaTab();
        } else if (data.type === 'figma.image') {
            figmaImage = data.payload && data.payload.image ? data.payload.image : null;
            figmaDims = null;
            renderFigmaTab();
            if (figmaImage) {
                fgDecodeDims(figmaImage, function (dims) {
                    figmaDims = dims;
                    renderFigmaTab();
                });
            }
        } else if (data.type === 'preset.importResult') {
            var r = data.payload;
            if (r.success) {
                showPresetFeedback('Imported: ' + r.applied + ' games applied, ' + r.skipped + ' skipped.', 'ok');
                // Apply imported theme immediately (the bridge wrote it to
                // localStorage; re-read and sync the popup's DOM + icon).
                try {
                    var importedTheme = localStorage.getItem('qa-panel-theme');
                    if (importedTheme && importedTheme !== (document.documentElement.getAttribute('data-theme') || 'dark')) {
                        document.documentElement.setAttribute('data-theme', importedTheme);
                        syncThemeIcon();
                    }
                } catch (e) { /* ignore */ }
            } else {
                showPresetFeedback('Import failed: ' + (r.error || 'unknown error'), 'err');
            }
        }
    });

    /* ── Heartbeat: ask for fresh state and notice if lobby goes away ── */

    send({ type: 'requestState' });
    send({ type: 'campaigns.requestState' });
    send({ type: 'prizepop.requestState' });
    send({ type: 'jira.requestState' });
    send({ type: 'console.requestState' });
    send({ type: 'environment.requestState' });
    send({ type: 'jackpotWidget.requestState' });
    send({ type: 'measure.requestState' });
    // Figma tab disabled — re-enable by uncommenting
    // send({ type: 'figma.requestState' });
    // send({ type: 'figma.requestImage' });
    setInterval(function () {
        var target = lobbyWindow();
        if (!target || target === window || target.closed) {
            setConnected(false);
        }
    }, 1000);

    // Initial paint with the inlined state.
    applyState(state);
    // Render the Sentry tab once with the zero-state so it's not blank
    // before the first poll broadcast lands (~2s after open).
    renderSentryTab();
    // Same for the Jira tab — paint the placeholder hint immediately;
    // the postMessage roundtrip replaces it with the real list.
    renderJiraTab();
    // Initial paint for the Console tab — empty state until the lobby's
    // first console.state broadcast arrives.
    renderConsoleTab();
    // Figma tab disabled — re-enable by uncommenting
    // renderFigmaTab();

    // Close us if user closes the lobby.
    window.addEventListener('beforeunload', function () { /* noop */ });
})();
</script>
</body>
</html>`;
}
